// #SPLIT_REVENUE_V1 — moved out of App.js unchanged (Bill 10/21: get the big files under 10,000 lines).
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { CUSTOMER_ALIAS_KEY, resolveCustomer, customerOptionsFrom } from "./customerAlias";
import { stripCP, checkDuc, jobNum } from "./ducValid";
import CustomerPicker from "./CustomerPicker";
import { WeekLinesModal, exportRevenueYearXlsx } from "./RevWeekDrill";   // #REV_WK_DRILL_V1
import { Plus } from "lucide-react";
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, LineChart, Line, Cell, LabelList } from "recharts";
import { sg, useLiveKeys, ss, PM_NAMES, _revNum, todayStr, C, _isPhoneNow, f$, Btn, useIsDesktop, isoWeekNum, pmCanon, PENDING_REV, PMS, SVC, fAbbr, ProgressBar, StatusBadge, fmtD } from "./appShared";

// #WEEK_RPT_V1 — supervisor → rig group + rig type (baked in; names match the invoice SUP column).
// Rig types read from the SUM sheet — correct any that are off and I'll update. Off-roster sups → OTHER.
const RIG_ROSTER = [
  { rig:'R1', sups:[{n:'ANGEL G',t:'23x30DR'},{n:'SERGIO',t:'23x30DR'},{n:'JUAN R',t:'23x30DR'},{n:'EDWARD G',t:'24x40'},{n:'CONRAD',t:'10x15'}] },
  { rig:'R2', sups:[{n:'IRVING',t:'32AT'},{n:'BRYAN S',t:'32AT'},{n:'ISMAEL',t:'32AT'}] },
  { rig:'R3', sups:[{n:'DALTON',t:'40x55'},{n:'M BONE',t:'40x55'},{n:'TYLER',t:'40AT'},{n:'ED',t:'40AT'},{n:'MANUEL',t:'40AT'},{n:'MATT B',t:'40AT'},{n:'M DIXON',t:'40AT'},{n:'PAUL D',t:'40AT'},{n:'RICHARD H',t:'40AT'}] },
  { rig:'R4', sups:[{n:'NOEL S',t:'100x140'},{n:'KADE',t:'AT120'},{n:'JUAN P',t:'100x140'},{n:'CODY',t:'AT120'}] },
  { rig:'R5', sups:[{n:'RAMIRO',t:'220x500'},{n:'NORBERTO',t:'330x500'},{n:'FELIX',t:'220x300'}] },
  { rig:'AU', sups:[{n:'RONALD',t:'AUGER'},{n:'TITAN',t:'AUGER'}] },
];

// #RU_R4_JUANP_V1 (2026-10-27) — Bill: "Move Juan P into TY's place and move Ty to Other for now." Juan P runs the 100x140 TY had;
// TY is off the roster so his lines fall to Other. Invoices spell Juan Padilla "J PADILA" — fold the spellings onto JUAN P.
const REV_SUP_ALIAS = { 'J PADILA':'JUAN P', 'J PADILLA':'JUAN P', 'J PADILLO':'JUAN P', 'JUAN PADILLA':'JUAN P' };

// #Q_COLORS_V1 — one palette per quarter, shared by the Week headers and the SUM quarter block so the quarter transition is visible.
const Q_COLORS = {
  1: { hdr:'#FCD34D', sub:'#FEF3C7' }, // Q1 amber/gold
  2: { hdr:'#93C5FD', sub:'#DBEAFE' }, // Q2 blue
  3: { hdr:'#86EFAC', sub:'#DCFCE7' }, // Q3 green
  4: { hdr:'#C4B5FD', sub:'#EDE9FE' }, // Q4 violet
};

// ══════════════════════════════════════════════════════════
//  TAB 3 — REVENUE TRACKER
// ══════════════════════════════════════════════════════════
const REV_COLS = [
  ['pm','PM'],['sup','SUP'],['jobType','Type'],['duc','DUC'],['customer','Customer'],
  ['pullDate','Pull Date'],['month','Mo'],['invDate','INV Date'],['invNo','INV #'],
  ['bore','# of Bore'],['lf','LF'],['size','Size'],['invAmnt','INV Amount'],
];

const REV_FORM_COLS = REV_COLS.filter(([k]) => k !== 'jobType' && k !== 'month');

// #BUDGET_RPT_V1 — monthly reference figures from 2026_Job_Revenue.xlsx (Budget tab). Hardcoded for
// now (read off the workbook); can be moved to an editable Supabase store later. Index 0=Jan … 11=Dec.
const BUDGET_2026 = [2966128,3014604,3956698,3711136,3392118,3532289,3316190,4696908,4010836,4084912,3530536,3164523];

const ACTUAL_2025 = [2999661,3056103,2770021,3403415,2844709,2493682,3577677,3689821,3363065,3606718,3604407,2694210];

const ACTUAL_2024 = [1606045,2718677,2888191,2983852,3132436,3057768,2606130,4366634,3347963,4711861,3684363,2201411];

// #REF_YR_2024 — BUDGET tab, MAR/DEC nudged -$1/-$102 to tie each quarter (and the $37,305,331 year) to the quarterly tab

// #BUDGET_RPT_V1 — prior-year quarterly history from 2026_Job_Revenue.xlsx (Budget tab). Baked in.
// pct is the stored workbook ratio (vs prior year). 2026 is computed live elsewhere.
const PRIOR_QTR = [
  { label:'2025', // #PRIOR_QTR_FIX_V1 — corrected to workbook quarterly tab (ties to monthly 2025 total)
    rows:[
      { q:'Q1', rev:8825785,  lf:109482, jobs:128, inv:416, bores:515, pct:1.015 },
      { q:'Q2', rev:8741806,  lf:167315, jobs:133, inv:526, bores:744, pct:0.925 },
      { q:'Q3', rev:10630563, lf:156785, jobs:111, inv:521, bores:656, pct:1.009 },
      { q:'Q4', rev:9905335,  lf:122272, jobs:94,  inv:422, bores:508, pct:1.059 },
    ],
    tot:{ rev:38103489, lf:555854, jobs:466, inv:1885, bores:2423 }, revLF:68.5495, avgLen:229.41 },
  { label:'2024', // #PRIOR_QTR_FIX_V1 — corrected to workbook quarterly tab
    rows:[
      { q:'Q1', rev:7212913,  lf:120127, jobs:99,  inv:386, bores:496, pct:0.798 },
      { q:'Q2', rev:9174056,  lf:121241, jobs:123, inv:390, bores:510, pct:0.968 },
      { q:'Q3', rev:10320727, lf:135540, jobs:110, inv:446, bores:570, pct:0.978 },
      { q:'Q4', rev:10597635, lf:112550, jobs:113, inv:415, bores:442, pct:1.171 },
    ],
    tot:{ rev:37305331, lf:489458, jobs:445, inv:1637, bores:2018 }, revLF:76.2186, avgLen:242.55 },
  { label:'2023',
    rows:[
      { q:'Q1', rev:8642494.56,  lf:122028, jobs:123, inv:358, bores:388, pct:1.0847498 },
      { q:'Q2', rev:8669333.41,  lf:127482, jobs:109, inv:396, bores:425, pct:1.0102027 },
      { q:'Q3', rev:10282551.38, lf:130357, jobs:97,  inv:426, bores:496, pct:1.1281443 },
      { q:'Q4', rev:10147651.55, lf:108955, jobs:106, inv:376, bores:413, pct:1.2682119 },
    ],
    tot:{ rev:37742030.90, lf:488822, jobs:435, inv:1556, bores:1722 }, revLF:77.2101724, avgLen:283.8687573 },
];

// ── Pull Checklist (rail on Invoice Input) — Bores reported PULLED, awaiting revenue capture ──
// Truth LF = boreLF (bore profile). Confirm = drops from queue (tracked in revenue-recon).
// NOTE: writing billed:true back onto the Daily is the paired "go-live" step — deferred to play first.
function PullChecklist({ entries, onPlaceholder, jobs, canEdit }) {
  const [logs, setLogs]           = useState({});
  const [confirmed, setConfirmed] = useState({});
  const [loaded, setLoaded]       = useState(false);
  const [q, setQ]                 = useState('');
  const [showDone, setShowDone]   = useState(false);

  useEffect(() => { (async () => {
    const dl = await sg('daily-logs');    if (dl && typeof dl === 'object') setLogs(dl);
    const rc = await sg('revenue-recon'); if (rc && typeof rc === 'object') setConfirmed(rc);
    setLoaded(true);
  })(); }, []);

  const norm = s => String(s||'').trim().toUpperCase().replace(/^CP-/,'').replace(/-CP$/,''); // strip CP whether front or rear

  // PM initials per DUC, pulled from the Job List (the truth). Keyed by normalized duc + displayDuc.
  const pmByDuc = {};
  (jobs || []).forEach(j => { [j.duc, j.displayDuc].forEach(d => { const n = norm(d); if (n && j.pm) pmByDuc[n] = j.pm; }); });

  // #PULL_BORE_V1 — resolve a log's REAL job number so fork jobs stop showing their custom-<ts> storage
  // key on the rail. Same resolve order as #DAILY_NUM_V1: number stamped on the log → Job List → raw key.
  const dispByDuc = {};
  (jobs || []).forEach(j => { const n = norm(j.duc); if (n) dispByDuc[n] = j.displayDuc || j.duc; });

  // Every entry tagged stage:"Pull" across all daily logs
  const pulls = [];
  Object.values(logs).forEach(job => {
    (job.entries || []).forEach(e => {
      if (String(e.stage||'').toLowerCase() === 'pull') {
        pulls.push({
          key:`${job.duc}__${e.id}`, duc:job.duc||'', customer:job.customer||'',
          disp: job.displayDuc || dispByDuc[norm(job.duc)] || job.duc || '',   // #PULL_BORE_V1
          date:e.date||'', sup:e.sup||'', bore:e.bore||'',
          pm: pmByDuc[norm(job.duc)] || pmByDuc[norm(job.displayDuc)] || '',
          size:String(e.pipeSize||job.pipeSize||'').replace(/"/g,''),
          lf:Number(e.boreLF)||0, typedLF:Number(e.drilledLF)||0,
        });
      }
    });
  });
  pulls.sort((a,b) => (a.date||'').localeCompare(b.date||'')); // oldest on top

  // Invoiced LF per normalized DUC (from the lines she's already entered)
  const invByDuc = {};
  (entries || []).forEach(l => { const d = norm(l.duc); if (!d) return; if (!invByDuc[d]) invByDuc[d] = { lf:0, lines:0 }; invByDuc[d].lf += Number(l.lf)||0; invByDuc[d].lines++; });

  const ql = norm(q);
  const doneCount = pulls.filter(p => confirmed[p.key]).length;
  const openCount = pulls.length - doneCount;
  const visible = pulls.filter(p => {
    if (!showDone && confirmed[p.key]) return false;
    if (ql && !norm(p.duc).includes(ql)) return false;
    return true;
  });

  const toggle = async (key) => { const n = {...confirmed}; if (n[key]) delete n[key]; else n[key] = true; setConfirmed(n); await ss('revenue-recon', n); };

  const statusOf = (p) => {
    const invd = invByDuc[norm(p.duc)];
    if (!invd) return { dot:'○', color:'#FEF3C7', txt:'no invoice line yet' };
    if (invd.lf === p.lf) return { dot:'✓', color:'#DCFCE7', txt:`matches (${p.lf.toLocaleString()} LF)` };
    const d = invd.lf - p.lf;
    return { dot:'⚠', color:'#FECACA', txt:`prof ${p.lf.toLocaleString()} / inv ${invd.lf.toLocaleString()} (${d>0?'+':''}${d.toLocaleString()})` };
  };

  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:12 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:6 }}>
        <div style={{ fontWeight:800, fontSize:13, color:C.text }}>🔗 Jobs to Inv</div>
        <div style={{ fontSize:11, color:C.muted }}>{openCount} open · {doneCount} done</div>
      </div>
      <div style={{ fontSize:10, color:C.muted, marginBottom:8, lineHeight:1.35 }}>
        Bores reported <b>PULLED</b> from the daily logs. <b>Fill form</b> drops this pull into the entry form above so you can start a line now and verify later. Check the box once billed and it drops off.
      </div>
      <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Filter by DUC…"
        style={{ width:'100%', fontSize:12, padding:'6px 8px', borderRadius:6, marginBottom:6 }} />
      <label style={{ fontSize:11, color:C.muted, display:'flex', alignItems:'center', gap:5, cursor:'pointer', marginBottom:8 }}>
        <input type="checkbox" checked={showDone} onChange={e=>setShowDone(e.target.checked)} style={{ width:'auto' }} /> Show confirmed
      </label>

      {!loaded ? <div style={{ color:C.muted, fontSize:12, padding:'10px 0' }}>Loading…</div> : (
      <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:520, overflowY:'auto' }}>
        {visible.map(p => {
          const s = statusOf(p);
          const isDone = !!confirmed[p.key];
          return (
            <div key={p.key} style={{ background: isDone ? '#14532D' : '#16A34A', borderRadius:7, padding:'8px 10px', color:'#FFFFFF' }}>
              <div style={{ display:'flex', justifyContent:'space-between', gap:8, alignItems:'flex-start' }}>
                <div style={{ fontSize:12, fontWeight:700, lineHeight:1.35 }}>
                  {p.disp || p.duc} – {p.customer || '—'}<br/>
                  {fmtD(p.date)} {p.sup} · {p.bore ? <>BORE {p.bore} · </> : null}PULLED {p.lf.toLocaleString()} LF {p.size}"
                </div>
                {canEdit && <input type="checkbox" checked={isDone} onChange={()=>toggle(p.key)} title="Mark billed — drops off the list" style={{ width:16, height:16, flexShrink:0, cursor:'pointer' }} />}
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:6, marginTop:6 }}>
                <span style={{ fontSize:10.5, fontWeight:800, color:'#111111', background:s.color, padding:'2px 6px', borderRadius:4 }}>{s.dot} {s.txt}</span>
                <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                  {p.pm && <span style={{ fontSize:10.5, fontWeight:800, color:'#111111', background:'#FFFFFF', padding:'2px 7px', borderRadius:4, whiteSpace:'nowrap' }} title="Project Manager (from the Job List)">{p.pm}</span>}
                  {canEdit && <span onClick={()=>onPlaceholder && onPlaceholder({ duc:p.duc, customer:p.customer, pullDate:fmtD(p.date), bore:String(p.bore||''), lf:String(p.lf||''), size:p.size })}
                    style={{ fontSize:10.5, fontWeight:800, color:'#C2410C', background:'#FFFFFF', padding:'2px 8px', borderRadius:4, cursor:'pointer', whiteSpace:'nowrap' }} title="Prefill the entry form above with this pull's DUC, date, LF & size">Fill form</span>}
                </div>
              </div>
            </div>
          );
        })}
        {!visible.length && <div style={{ color:C.muted, fontSize:11, textAlign:'center', padding:'14px 4px' }}>{pulls.length ? 'Nothing open — all caught up. 🎉' : 'No Pulls in the daily logs yet.'}</div>}
      </div>
      )}
    </div>
  );
}

// Small add-a-name input used in the PM/SUP manage panel
function CrewAdder({ onAdd, placeholder }) {
  const [v, setV] = useState('');
  const go = () => { if (v.trim()) { onAdd(v.trim()); setV(''); } };
  return (
    <div style={{ display:'flex', gap:6 }}>
      <input value={v} onChange={e=>setV(e.target.value)} placeholder={placeholder}
        onKeyDown={e=>{ if (e.key==='Enter') go(); }}
        style={{ flex:1, fontSize:12, padding:'5px 7px', border:'1px solid #E2E8F0', borderRadius:5, boxSizing:'border-box' }} />
      <button onClick={go} style={{ fontSize:12, padding:'5px 11px', borderRadius:5, border:'none', background:'#E85D24', color:'#fff', fontWeight:700, cursor:'pointer' }}>Add</button>
    </div>
  );
}

// Revenue Invoice Input — paste from Excel (INPUT tab cols A-M) or add a row; stored in Supabase
// #PERIOD_LOCK_V1 — module-scope month names. BudgetReport has its own local MONTHS (all-caps, line
// ~6029) that is NOT in scope here; referencing it from RevenueInput would throw at runtime.
const _LOCK_MOS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function RevenueInput({ jobs, view, setView, onSyncActuals, canEdit, userRole, upn }) {
  const [entries, setEntries] = useState([]);
  const [closedThru, setClosedThru] = useState('');   // #PERIOD_LOCK_V1 — "YYYY-MM", Owner-settable
  const [hist, setHist] = useState([]); // #REV_HIST_V1 — prior years, read-only, one Supabase key per year
  const [syncPreview, setSyncPreview] = useState(null); // #REV_SYNC_V1
  const [ackMiss, setAckMiss] = useState([]);   // #REV_SKIPMISS_V1 — DUC keys acknowledged as "no job, skip & stop warning"
  const [skipSel, setSkipSel] = useState({});   // transient modal checkbox selection (key -> bool)
  const [syncMsg, setSyncMsg] = useState('');
  const [dlogs, setDlogs]     = useState({});
  const [fAlias, setFAlias]   = useState({});   // #FORK_FIX_REV_V1 — custom-id -> real-DUC links
  const [loaded, setLoaded]   = useState(false);
  const [pasteText, setPaste] = useState('');
  const [filter, setFilter]   = useState('');
  const [msg, setMsg]         = useState('');
  const [crew, setCrew]       = useState({ pms:[], sups:[] }); // editable PM / SUP rosters
  const [showCrew, setShowCrew] = useState(false);
  // #REV_CUST_LOCK_V1 (2026-10-20) — Bill: "same happening in AR... trim this down before it gets out of
  // hand." Customer on an invoice line was free text, so every typo became a new customer. Now: DUC
  // auto-fill puts in the Job List customer (merged spelling), the Customer box is the same locked
  // dropdown the Job List uses (+ Add new for a true first-timer), and every save/paste runs the name
  // through the customer-alias map so a known variant can't be stored.
  const [custAlias, setCustAlias] = useState({});
  const reloadCustAlias = useCallback(async () => { const a = await sg(CUSTOMER_ALIAS_KEY); if (a && typeof a==='object' && !Array.isArray(a)) setCustAlias(a); }, []);
  useEffect(() => { reloadCustAlias(); }, [reloadCustAlias]);
  useLiveKeys([CUSTOMER_ALIAS_KEY], reloadCustAlias);
  const _canonCust = (n) => resolveCustomer(custAlias, String(n||'').trim());
  const custOptions = useMemo(() => customerOptionsFrom(jobs, custAlias), [jobs, custAlias]);
  const _todayMMDDYY = () => { const d = new Date(); return `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${String(d.getFullYear()).slice(2)}`; }; // local date — no UTC shift
  const blank = { pm:'', sup:'', jobType:'', duc:'', customer:'', pullDate:'', month:'', invDate:'', invNo:'', bore:'', lf:'', size:'', invAmnt:'' };
  const freshForm = () => ({ ...blank, invDate: _todayMMDDYY() }); // INV Date defaults to today (editable)
  const [form, setForm]       = useState(freshForm);

  useEffect(() => { (async () => {
    let saved = await sg('revenue-input');
    if (Array.isArray(saved)) {
      // Guarantee every row has a unique id — older pasted batches collided. Self-heals once.
      const seen = new Set(); let changed = false;
      saved = saved.map(e => {
        let id = e && e.id;
        if (id==null || seen.has(id)) { id = `r${Date.now().toString(36)}${seen.size.toString(36)}${Math.random().toString(36).slice(2,8)}`; changed = true; }
        seen.add(id);
        return id===(e&&e.id) ? e : { ...e, id };
      });
      setEntries(saved);
      if (changed) ss('revenue-input', saved);
    }
    // #REV_HIST_V1 — merge each prior year's key (each small; avoids the ~1MB single-blob cap)
    { const cy=new Date().getFullYear(); const acc=[];
      for (let y=cy-1; y>=cy-12; y--) { const h=await sg(`revenue-${y}`); if (Array.isArray(h)) h.forEach(r=>acc.push({ ...r, _hist:true, _hy:y })); }
      if (acc.length) setHist(acc);
    }
    const dl = await sg('daily-logs');
    if (dl && typeof dl === 'object') setDlogs(dl);
    const am = await sg('revenue-sync-skipmiss'); // #REV_SKIPMISS_V1
    if (Array.isArray(am)) setAckMiss(am);
    const fa = await sg('fork-alias'); // #FORK_FIX_REV_V1
    if (fa && typeof fa === 'object') setFAlias(fa);
    const cl = await sg('crew-lists');
    if (cl && (Array.isArray(cl.pms) || Array.isArray(cl.sups))) {
      setCrew({ pms: cl.pms||[], sups: cl.sups||[] });
    } else {
      // Seed once: PMs from the known five, SUPs from supervisors already in the data (to curate)
      const seedSups = [...new Set((Array.isArray(saved)?saved:[]).map(e=>String(e.sup||'').trim()).filter(Boolean))].sort((a,b)=>a.localeCompare(b));
      const seed = { pms: Object.values(PM_NAMES).filter(n => n !== 'Auger'), sups: seedSups };   // #REV_PM_LOCK_V1 — AU is a job code, not a PM
      setCrew(seed); ss('crew-lists', seed);
    }
    setLoaded(true);
  })(); }, []);

  const saveCrew   = (next) => { setCrew(next); ss('crew-lists', next); };
  const addCrew    = (which, name) => { if (which==='pms' && userRole!=='Owner') return; /* #REV_PM_LOCK_V1 */ const v=String(name||'').trim(); if(!v) return; const list=crew[which]||[]; if(list.some(x=>x.toLowerCase()===v.toLowerCase())) return; saveCrew({ ...crew, [which]: [...list, v].sort((a,b)=>a.localeCompare(b)) }); };
  const removeCrew = (which, name) => { if (which==='pms' && userRole!=='Owner') return; /* #REV_PM_LOCK_V1 */ saveCrew({ ...crew, [which]: (crew[which]||[]).filter(x=>x!==name) }); };

  // #REV_PM_LOCK_V1 (2026-10-05) — Bill: "Auger" kept landing in the PM column and Marilyn swore she
  // wasn't adding it. She wasn't. Typing a DUC auto-fills PM from the Job List, and an Auger job's PM
  // code there is AU -> "Auger" — a name that is NOT on the PM master list. The dropdown has no such
  // option so it showed blank, while "Auger" rode along underneath and saved with the line (and it
  // overwrote a PM she had already picked, since PM sits left of DUC). The master list (Manage PM /
  // SUP names) is now the only source of PM values: auto-fill applies a name only if it's on the
  // list, "+ Add new" is gone from the PM dropdown, Add / Edit / Paste refuse an off-list PM, and
  // only the Owner changes the PM list.
  const _pmOnList = (name) => { const t = String(name||'').trim().toLowerCase(); return t ? ((crew.pms||[]).find(x => String(x).trim().toLowerCase() === t) || '') : ''; };
  const pmAutoRef = useRef(false);   // true while the PM showing came from the Job List auto-fill, not a hand pick
  const _jobPmFor = (duc) => { const j = String(duc||'').trim() ? jobByDuc(duc) : null; if (!j) return null; const raw = PM_NAMES[j.pm] || j.pm || ''; return { job:j, raw, pm:_pmOnList(raw) }; };
  const _ducFill = (v) => {
    const hit = _jobPmFor(v); const pu = pullByDuc(v);
    let pmPatch = {};
    if (hit) {
      if (hit.pm) { pmPatch = { pm: hit.pm }; pmAutoRef.current = true; }
      else if (pmAutoRef.current) { pmPatch = { pm:'' }; pmAutoRef.current = false; }   // don't carry an auto-filled PM over from a different job
    }
    setForm(p => ({ ...p, duc:v,
      ...(hit ? { customer: _canonCust(hit.job.cust || p.customer) } : {}), ...pmPatch,   /* #REV_CUST_LOCK_V1 */
      ...(pu ? { lf: pu.lf ? String(pu.lf) : p.lf, size: pu.size || p.size } : {}) }));
  };

  const [revYear, setRevYear] = useState(new Date().getFullYear()); // #REV_YEAR_V1 — default current year
  const persist = async (next) => { setEntries(next); await ss('revenue-input', next); };

  // #LIVE_KEYS_V1 — pull in another device's revenue edits. Every edit here persists immediately, so
  // our local `entries` always equals our last write; if the server differs it's someone else's change.
  // The add-form (`form`) is separate local state, so a refresh never disturbs a line you're typing.
  const reloadRev = async () => {
    const saved = await sg('revenue-input');
    if (!Array.isArray(saved)) return;
    if (JSON.stringify(saved) === JSON.stringify(entries)) return;   // our own write / no real change
    setEntries(saved);
    setMsg('Updated from another device ✓'); setTimeout(()=>setMsg(''), 1600);
  };
  useLiveKeys(['revenue-input'], reloadRev);

  // #PERIOD_LOCK_V1 — load the closed-through setting; live-refresh it so reopening a period
  // takes effect on everyone's screen without a reload.
  const reloadClosed = useCallback(async () => {
    const v = await sg('rev-closed-through');
    if (typeof v === 'string') setClosedThru(v);
  }, []);
  useEffect(() => { reloadClosed(); }, [reloadClosed]);
  useLiveKeys(['rev-closed-through'], reloadClosed);

  const importPaste = async () => {
    const lines = pasteText.split(/\r?\n/).filter(l => l.trim() !== '');
    if (!lines.length) { setMsg('Nothing to import — paste rows first.'); setTimeout(()=>setMsg(''),5000); return; }
    const out = [];
    for (const line of lines) {
      const c = line.split('\t');
      if ((c[0]||'').trim().toLowerCase() === 'project manager') continue; // skip header row
      out.push({
        id: Date.now() + Math.random(),
        pm:(c[0]||'').trim(), sup:(c[1]||'').trim(), jobType:(c[2]||'').trim(),
        duc:(c[3]||'').trim(), customer:_canonCust(c[4]), pullDate:(c[5]||'').trim(),   /* #REV_CUST_LOCK_V1 */
        month:(c[6]||'').trim(), invDate:(c[7]||'').trim(), invNo:(c[8]||'').trim(),
        bore:_revNum(c[9]), lf:_revNum(c[10]), size:(c[11]||'').trim(), invAmnt:_revNum(c[12]),
      });
    }
    if (!out.length) { setMsg('No data rows found (header only?).'); setTimeout(()=>setMsg(''),5000); return; }
    // #REV_HIST_V1 — a paste is one year's file. Route the WHOLE paste to a single key = its dominant
    // invoice year, so one paste only ever touches one key (a stray boundary date can't clobber another
    // year). Current year appends to the live 'revenue-input'; a prior year REPLACES 'revenue-YYYY'.
    const _yy = r => { const m=String(r.invDate||r.pullDate||'').match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/); return m ? (m[3].length===2?2000+Number(m[3]):Number(m[3])) : null; };
    const cy = new Date().getFullYear();
    const cnt = {}; out.forEach(r => { const y=_yy(r)||cy; cnt[y]=(cnt[y]||0)+1; });
    const domYr = Number(Object.keys(cnt).sort((a,b)=>cnt[b]-cnt[a])[0]) || cy;
    if (domYr === cy) {
      // #REV_PM_LOCK_V1 — same rule for a bulk paste into the live year
      out.forEach(r => { const on = _pmOnList(r.pm); if (on) r.pm = on; });
      const _off = [...new Set(out.filter(r => !_pmOnList(r.pm)).map(r => r.pm || '(blank)'))];
      if (_off.length) {
        if (userRole !== 'Owner') { window.alert(`⚠ Paste blocked — PM not on the list: ${_off.join(', ')}.\n\nFix the PM column to one of: ${(crew.pms||[]).join(', ')}.`); return; }
        if (!window.confirm(`⚠ ${_off.length} PM name${_off.length!==1?'s':''} in this paste ${_off.length!==1?'are':'is'} not on the PM list: ${_off.join(', ')}.\n\nImport anyway?`)) return;
      }
      const _g3p = _dupGate(out, entries, 'Paste'); if (!_g3p.ok) return;   // #REV_DUP_V3 — vs existing lines AND inside the paste
      const next=[...entries, ...out]; await persist(next);
      if (_g3p.stamp) await _approveDups(_g3p.stamp.keys, _g3p.stamp.counts);
      setMsg(`Imported ${out.length} rows into ${cy} (live). Total now ${next.length}.`);
    } else {
      await ss(`revenue-${domYr}`, out);
      const acc=[]; for (let y=cy-1; y>=cy-12; y--) { const h=await sg(`revenue-${y}`); if (Array.isArray(h)) h.forEach(r=>acc.push({ ...r, _hist:true, _hy:y })); } setHist(acc);
      setMsg(`Saved ${out.length} rows to ${domYr} (history) — safe write.`);
    }
    setPaste(''); setTimeout(()=>setMsg(''),9000);
  };

  const addManual = async () => {
    if (!form.duc.trim() && !form.invAmnt) { setMsg('Enter at least a DUC and an amount.'); setTimeout(()=>setMsg(''),4000); return; }
    // #REV_PM_LOCK_V1 — the PM has to be a name on the master list. Hard stop, not a confirm.
    const _pmSel = _pmOnList(form.pm);
    if (!_pmSel) {
      const _typedPm = String(form.pm||'').trim();
      window.alert((_typedPm ? `⚠ "${_typedPm}" is not on the PM list.` : `⚠ No PM selected.`) +
        `\n\nPick the PM for this line: ${(crew.pms||[]).join(', ')}.` +
        `\n\nA new PM has to be added to the master list (Manage PM / SUP names) by the Owner first.`);
      if (_typedPm) setForm(p => ({ ...p, pm:'' }));
      return;
    }
    // #DUC_GATE_V1 — verify the job # against the Job List before it can post. A mistyped DUC that matches
    // nothing auto-fills nothing and used to slip through clean; now it must be acknowledged.
    {
      const _typed = String(form.duc||'').trim();
      const _hit = _typed ? jobByDuc(_typed) : null;
      if (!_hit) {
        const _q = _typed
          ? `⚠ Job number "${_typed}" is NOT on the Job List.\n\nNothing matched, so the Customer couldn't be verified — this is exactly how a mistyped job number slips through. Check it against the Job List first.\n\nPost this line anyway?`
          : `⚠ No job number entered.\n\nThis revenue line won't be tied to any job on the Job List. Post without a DUC?`;
        if (!window.confirm(_q)) return;
      }
    }
    // #REV_DATE_GATE_V1 — dates must be complete & valid; partials like 07/10 or 07/27/6 can't post. Non-current year needs a confirm.
    {
      const _cy = new Date().getFullYear();
      const iv = String(form.invDate||'').trim();
      if (!iv) { window.alert('⚠ Invoice date is required.\n\nRevenue tracks the invoice date — enter it as MM/DD/YYYY.'); return; }
      const ivd = _validDate(iv);
      if (!ivd) { window.alert(`⚠ Invoice date "${iv}" isn't a complete date.\n\nUse MM/DD/YYYY (e.g. 07/27/2026). Short or partial dates like 07/10 or 07/27/6 can't post — that's how a bad line slips in.`); return; }
      const pd = String(form.pullDate||'').trim();
      if (pd && !_validDate(pd)) { window.alert(`⚠ Pull date "${pd}" isn't a complete date.\n\nUse MM/DD/YYYY, or clear it. Partial dates like 07/10 can't post.`); return; }
      if (ivd.yr !== _cy && !window.confirm(`⚠ Invoice date ${iv} falls in ${ivd.yr}, not ${_cy}.\n\nThis line will post to the ${ivd.yr} revenue year. Add it to ${ivd.yr}?`)) return;
    }
    // #REV_DUP_V1 retired 2026-10-29 by #REV_DUP_V3 (SUP + Date + INV # + Amount, job # deliberately out).
    // #REV_DUP_V2 — catch a re-entry even when a date was mistyped: same DUC + amount + LF, dates ignored (the gap that slipped through).
    {
      const _nd2 = (x) => String(x||'').trim().toUpperCase().replace(/^CP-/,'').replace(/-CP$/,'');
      const _duc=_nd2(form.duc), _amt=_revNum(form.invAmnt), _lf=_revNum(form.lf);
      if (_duc && _amt) {
        const _k3 = _dupKey(form); /* #REV_DUP_V3 — if the V3 rule already matches, let V3 ask (one prompt, approver-gated) */
        const _twin = (_k3 && (entries||[]).some(e => _dupKey(e)===_k3)) ? null : (entries||[]).find(e => _nd2(e.duc)===_duc && _revNum(e.invAmnt)===_amt && _revNum(e.lf)===_lf);
        if (_twin && !window.confirm(`⚠ Possible duplicate — same DUC, amount, and LF as an existing line (dates aside):\n\nDUC ${form.duc} · $${_amt.toLocaleString()} · ${_lf} LF · Inv ${_twin.invNo||'—'} · ${_twin.invDate||_twin.pullDate||'—'}\n\nThis is the exact pattern that slipped through before. Add anyway?`)) return;
      }
    }
    // #REV_GATE_V1 — the standing rule says the DUC validator gates every module's front door; Revenue
    // was the one door with no lock (that's how L4506215 / jobType "L" got saved). Job-List membership
    // is checked FIRST and format SECOND, per ducValid.js — legacy Auger numbers that don't fit the
    // 8-char shape must still pass when they're on the list. Warn + confirm, never a hard block.
    {
      const _jobKeys = new Set((jobs||[]).flatMap(j => [j.duc, j.displayDuc]).filter(Boolean).map(stripCP));
      const _chk = checkDuc(form.duc, _jobKeys);
      if (_chk.status === 'invalid' && !window.confirm(
        `⚠ "${String(form.duc||'').trim()}" is not a valid job number — ${_chk.reason}.\n\n` +
        `A DUC is 8 characters: E/F/G/P/S/W + 2-digit year + 3-digit day-of-year + 2-digit sequence (or Auger A0-A9 / AU).\n\n` +
        `Add this line anyway?`)) return;
      if (_chk.status === 'unlisted' && !window.confirm(
        `⚠ "${_chk.norm}" is a valid format but is NOT on the Job List.\n\n` +
        `Either the job hasn't been created yet, or this is a typo.\n\nAdd this line anyway?`)) return;
    }
    // #REV_DATE_V1 — a dropped slash silently poisoned the month field: invDate "0731/26" made the
    // month regex below match 31 instead of 07. Normalize to MM/DD/YY first, and refuse a date that
    // can't be read at all rather than saving a number that only looks like a date.
    const _iv = _normDate(form.invDate), _pd = _normDate(form.pullDate);
    if (!_iv.ok) { window.alert(`INV Date "${form.invDate}" can't be read.\n\nUse MM/DD/YY — e.g. 07/31/26.`); return; }
    if (!_pd.ok) { window.alert(`Pull Date "${form.pullDate}" can't be read.\n\nUse MM/DD/YY — e.g. 07/31/26.`); return; }

    const cleanDuc = String(form.duc||'').trim().toUpperCase().replace(/^CP-/,'').replace(/-CP$/,'');
    const autoType = (cleanDuc.match(/[A-Z]/) || [''])[0];        // utility = 1st letter of DUC
    const autoMonth = _deriveMonth(_iv.val, _pd.val);              // #REV_DATE_V1 — normalized, not raw
    const rec = { id: Date.now()+Math.random(), ...form, customer:_canonCust(form.customer), pm:_pmSel, invDate:_iv.val, pullDate:_pd.val, jobType:autoType, month:autoMonth, bore:_revNum(form.bore), lf:_revNum(form.lf), invAmnt:_revNum(form.invAmnt) };
    if (_blockedByLock(rec, 'Add')) return;   // #PERIOD_LOCK_V1 — no back-dating into a closed period
    const _g3 = _dupGate([rec], entries, 'Add'); if (!_g3.ok) return;   // #REV_DUP_V3
    await persist([...entries, rec]); setForm(freshForm()); pmAutoRef.current = false;
    if (_g3.stamp) await _approveDups(_g3.stamp.keys, _g3.stamp.counts);   // #REV_DUP_V3
    setMsg('Row added.'); setTimeout(()=>setMsg(''),3000);
  };

  const removeEntry = (entry) => {
    if (_blockedByLock(entry, 'Delete')) return;   // #PERIOD_LOCK_V1
    const ok = window.confirm(`Delete this invoice line?\n\n${entry.duc||'—'} · ${entry.customer||''}\nINV ${entry.invNo||'—'} · $${_revNum(entry.invAmnt).toLocaleString()}\n\nThis can't be undone.`);
    if (!ok) return;
    setEntries(prev => { const next = prev.filter(e => e !== entry); ss('revenue-input', next); return next; });
  };
  const [noteRow, setNoteRow] = useState(null);   // the entry whose note is open
  const [noteText, setNoteText] = useState('');
  const openNote = (entry) => { setNoteRow(entry); setNoteText(entry.note || ''); };
  const saveNote = () => {
    setEntries(prev => { const next = prev.map(e => e===noteRow ? { ...e, note: noteText.trim() } : e); ss('revenue-input', next); return next; });
    setNoteRow(null);
  };
  const [editRow, setEditRow] = useState(null);   // the entry being edited
  const [editForm, setEditForm] = useState({});
  const _fmtMoney = v => { const n=_revNum(v); return n ? n.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}) : ''; };
  const _fmtNum   = v => { const n=_revNum(v); return n ? n.toLocaleString() : ''; };
  const _rawNum   = v => { const n=_revNum(v); return n ? String(n) : ''; };

  // #REV_DATE_V1 — ONE date normalizer + month deriver shared by add AND edit. These used to live
  // inside addEntry only, so saveEdit wrote whatever was typed and never re-derived month — that's how
  // E4604208 ended up with a corrected invDate (07/29/26) still carrying month 29.
  const _normDate = (s) => {
    const t = String(s||'').trim();
    if (!t) return { ok:true, val:'' };
    let mm, dd, yy;
    const m = t.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
    if (m) { mm=+m[1]; dd=+m[2]; yy=m[3]; }
    else {
      const g = t.replace(/\D/g,'');
      if (g.length===6)      { mm=+g.slice(0,2); dd=+g.slice(2,4); yy=g.slice(4,6); }
      else if (g.length===8) { mm=+g.slice(0,2); dd=+g.slice(2,4); yy=g.slice(4,8); }
      else return { ok:false, val:t };
    }
    if (!(mm>=1 && mm<=12) || !(dd>=1 && dd<=31)) return { ok:false, val:t };
    return { ok:true, val:`${String(mm).padStart(2,'0')}/${String(dd).padStart(2,'0')}/${String(yy).slice(-2)}` };
  };

  // #REV_DUP_V3 (2026-10-29) — Bill: a duplicate is SAME SUP + DATE + INV # + AMOUNT. Job # left out on
  // purpose — one SUP can't be on two jobs the same day, and a DUC can itself be keyed wrong. DATE = pull
  // date (INV date if no pull date). A legit duplicate CAN happen, so Marilyn (AR) or an Owner approves
  // it; everyone else is blocked at save. Approvals live in 'rev-dup-cleared' keyed by the 4-field key
  // with the line count approved — a further copy of the same line re-alerts.
  const _canApproveDup = userRole === 'AR' || userRole === 'Owner';
  const _dupWho = String(upn||'').split('@')[0] || userRole || 'unknown';
  const _dupKey = (e) => {
    if (!e) return '';
    const sv = String(e.sup||'').trim().toUpperCase();
    const dt = _normDate(e.pullDate || e.invDate || '').val;
    const iv = String(e.invNo||'').trim().toUpperCase();
    const am = _revNum(e.invAmnt);
    if (!sv || !iv || !am) return '';
    return `${sv}|${dt}|${iv}|${am}`;
  };
  const [dupCleared, setDupCleared] = useState({});
  const [dupShowOk, setDupShowOk] = useState(false);
  const reloadDupCleared = useCallback(async () => { const v = await sg('rev-dup-cleared'); if (v && typeof v==='object' && !Array.isArray(v)) setDupCleared(v); }, []);
  useEffect(() => { reloadDupCleared(); }, [reloadDupCleared]);
  useLiveKeys(['rev-dup-cleared'], reloadDupCleared);
  const _dupLatest = async () => { try { const v = await sg('rev-dup-cleared'); if (v && typeof v==='object' && !Array.isArray(v)) return { ...v }; } catch {} return { ...dupCleared }; };
  const _approveDups = async (keys, counts) => {
    const base = await _dupLatest(); const at = new Date().toISOString();
    keys.forEach(k => { base[k] = { by:_dupWho, at, n: counts[k] || 2 }; });
    setDupCleared(base); await ss('rev-dup-cleared', base);
  };
  const _unapproveDup = async (k) => {
    if (!_canApproveDup) return;
    const base = await _dupLatest(); delete base[k];
    setDupCleared(base); await ss('rev-dup-cleared', base);
  };
  // recs = lines about to be saved; others = lines already there. Returns { ok, stamp }.
  const _dupGate = (recs, others, what) => {
    const cnt = {}; (others||[]).forEach(e => { const k = _dupKey(e); if (k) cnt[k] = (cnt[k]||0) + 1; });
    const hits = [];
    recs.forEach(r => { const k = _dupKey(r); if (!k) return; if (cnt[k]) hits.push({ r, k }); cnt[k] = (cnt[k]||0) + 1; });
    if (!hits.length) return { ok:true, stamp:null };
    const list = hits.slice(0,8).map(h => `• ${h.r.sup} · ${h.r.pullDate || h.r.invDate} · INV ${h.r.invNo} · $${_revNum(h.r.invAmnt).toLocaleString()}`).join('\n')
               + (hits.length > 8 ? `\n…and ${hits.length-8} more` : '');
    if (!_canApproveDup) {
      window.alert(`⛔ ${what} blocked — duplicate revenue line.\n\nSame SUP + Date + INV # + Amount as a line already entered:\n${list}\n\nIf this really is a second, legitimate line, Marilyn (AR) has to enter or approve it.`);
      return { ok:false, stamp:null };
    }
    if (!window.confirm(`⚠ DUPLICATE — same SUP + Date + INV # + Amount as a line already entered:\n${list}\n\nOK = this is a LEGITIMATE duplicate. Save it and record the approval (${_dupWho}).\nCancel = don't save.`)) return { ok:false, stamp:null };
    const keys = [...new Set(hits.map(h => h.k))]; const counts = {}; keys.forEach(k => { counts[k] = cnt[k]; });
    return { ok:true, stamp:{ keys, counts } };
  };
  // month ALWAYS re-derived from the normalized date — never trusted from the stored field.
  const _deriveMonth = (invDate, pullDate) => {
    const m = String(invDate || pullDate || '').match(/^(\d{1,2})[\/\-]/);
    const n = m ? parseInt(m[1],10) : 0;
    return (n>=1 && n<=12) ? String(n) : '';
  };

  // #PERIOD_LOCK_V1 — closed-period gate. Two rules, both Owner-overridable:
  //   1. any prior calendar year is always closed
  //   2. the current year is closed through the Owner-set 'rev-closed-through' month ("YYYY-MM")
  // Gates add, edit AND delete, and also blocks MOVING a line into a closed period.
  const _isOwner = userRole === 'Owner';
  const _periodOf = (e) => {
    const m = String((e && (e.invDate || e.pullDate)) || '').match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
    if (!m) return null;
    const yy = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    return { y: yy, m: Number(m[1]) };
  };
  const _lockReason = (e) => {
    if (_isOwner) return '';
    const p = _periodOf(e);
    if (!p) return '';
    if (p.y < new Date().getFullYear()) return `${p.y} is a closed year`;
    if (closedThru) {
      const [cy, cm] = String(closedThru).split('-').map(Number);
      if (cy && cm && (p.y < cy || (p.y === cy && p.m <= cm))) return `${_LOCK_MOS[p.m-1]} ${p.y} is closed (through ${_LOCK_MOS[cm-1]} ${cy})`;
    }
    return '';
  };
  const _blockedByLock = (e, what) => {
    const r = _lockReason(e);
    if (!r) return false;
    window.alert(`🔒 ${what} blocked — ${r}.\n\nClosed periods are read-only. Ask Bill to reopen the period if this needs to change.`);
    return true;
  };
  const _saveClosedThru = async (v) => { setClosedThru(v); try { await ss('rev-closed-through', v); } catch {} };
  const openEdit = (entry) => {
    setEditRow(entry);
    setEditForm({
      ...entry,
      invAmnt: _fmtMoney(entry.invAmnt),
      lf:      _fmtNum(entry.lf),
      size:    entry.size ? (String(entry.size).includes('"') ? entry.size : entry.size+'"') : '',
    });
  };
  const saveEdit = () => {
    const f = editForm;
    // #REV_EDIT_GATE_V1 — the edit path used to write the form straight through: no date normalize,
    // no month recompute, no DUC check. Correcting a bad date here left the derived month stale
    // (E4604208: invDate fixed to 07/29/26, month still 29 → dropped from every month-based rollup).
    const _iv = _normDate(f.invDate), _pd = _normDate(f.pullDate);
    if (!_iv.ok) { window.alert(`INV Date "${f.invDate}" can't be read.\n\nUse MM/DD/YY — e.g. 07/29/26.`); return; }
    if (!_pd.ok) { window.alert(`Pull Date "${f.pullDate}" can't be read.\n\nUse MM/DD/YY — e.g. 07/29/26.`); return; }
    {
      const _jobKeys = new Set((jobs||[]).flatMap(j => [j.duc, j.displayDuc]).filter(Boolean).map(stripCP));
      const _chk = checkDuc(f.duc, _jobKeys);
      if (_chk.status === 'invalid' && !window.confirm(
        `⚠ "${String(f.duc||'').trim()}" is not a valid job number — ${_chk.reason}.\n\nSave this edit anyway?`)) return;
      if (_chk.status === 'unlisted' && !window.confirm(
        `⚠ "${_chk.norm}" is a valid format but is NOT on the Job List.\n\nSave this edit anyway?`)) return;
    }
    // #REV_PM_LOCK_V1 — an old line may keep the PM it already has, but can't be CHANGED to an off-list name
    if (String(f.pm||'').trim() !== String(editRow.pm||'').trim() && !_pmOnList(f.pm)) {
      window.alert(`⚠ "${String(f.pm||'').trim() || '(blank)'}" is not on the PM list.\n\nPick one of: ${(crew.pms||[]).join(', ')}.`);
      return;
    }
    const rec = { ...f, customer:_canonCust(f.customer), invDate:_iv.val, pullDate:_pd.val, month:_deriveMonth(_iv.val, _pd.val),
                  bore:_revNum(f.bore), lf:_revNum(f.lf), invAmnt:_revNum(f.invAmnt) };
    // #PERIOD_LOCK_V1 — check where it IS and where it's being MOVED to
    if (_blockedByLock(editRow, 'Edit') || _blockedByLock(rec, 'Edit')) return;
    // #REV_DUP_V3 — only when the edit changes the SUP / Date / INV # / Amount
    let _g3e = { ok:true, stamp:null };
    { const _ke = _dupKey({ ...editRow, ...rec }); if (_ke && _ke !== _dupKey(editRow)) _g3e = _dupGate([{ ...editRow, ...rec }], (entries||[]).filter(e => e !== editRow), 'Edit'); }
    if (!_g3e.ok) return;
    if (_g3e.stamp) _approveDups(_g3e.stamp.keys, _g3e.stamp.counts);
    setEntries(prev => { const next = prev.map(e => e===editRow ? { ...e, ...rec } : e); ss('revenue-input', next); return next; });
    setEditRow(null);
  };
  const clearAll = () => { if (window.confirm(`Clear ALL ${entries.length} revenue rows? No take backs. #WeAreDakota`)) persist([]); };
  // #REV_DATE_GATE_V1 — a complete, real calendar date (MM/DD/YY or MM/DD/YYYY). Rejects partials like 07/10 or 07/27/6.
  const _validDate = (s) => {
    const m = String(s||'').trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
    if (!m) return null;
    const mo=+m[1], da=+m[2]; let yr=+m[3]; if (m[3].length===2) yr+=2000;
    if (mo<1||mo>12||da<1||da>31||yr<2000||yr>2100) return null;
    const dt=new Date(yr, mo-1, da); if (dt.getMonth()!==mo-1||dt.getDate()!==da) return null; // reject impossible days (e.g. 02/30)
    return { mo, da, yr };
  };

  // #REV_YEAR_V1 — scope KPIs + table to the selected invoice year (default current year)
  const _revYrOf = e => { const m=String(e.invDate||e.pullDate||'').match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/); return m ? (m[3].length===2?2000+Number(m[3]):Number(m[3])) : null; };
  const allE = [...entries, ...hist]; // #REV_HIST_V1 — live current year + read-only prior years
  const _revYears = Array.from(new Set(allE.map(_revYrOf).filter(Boolean))).sort((a,b)=>b-a);
  const _curYr = new Date().getFullYear();
  const yearEntries = revYear==='ALL' ? allE : allE.filter(e => _revYrOf(e) === revYear);
  const total = yearEntries.reduce((s,e) => s + _revNum(e.invAmnt), 0);
  const ducs  = new Set(yearEntries.map(e => String(e.duc||'').trim().toUpperCase().replace(/^CP-/,'').replace(/-CP$/,'')).filter(Boolean)).size;
  const _normDuc = s => String(s||'').trim().toUpperCase().replace(/^CP-/,'').replace(/-CP$/,''); // CP-agnostic
  // #FORK_FIX_REV_V1 — resolve typed DUCs against the REAL job number: displayDuc first, then a
  // manual fork-alias link, then raw duc. Fork jobs (custom-NNNN ids) never matched before this.
  const _realOf = (o) => (fAlias[o.duc] && fAlias[o.duc].duc) || o.displayDuc || o.duc;
  const jobByDuc = (duc) => { const n = _normDuc(duc); return n ? (jobs||[]).find(j => _normDuc(_realOf(j)) === n || _normDuc(j.duc) === n) : null; };
  const pullByDuc = (duc) => {
    const n = _normDuc(duc); if (!n) return null;
    const job = Object.values(dlogs).find(j => _normDuc(_realOf(j)) === n || _normDuc(j.duc) === n);
    if (!job) return null;
    const pulls = (job.entries||[]).filter(e => String(e.stage||'').toLowerCase()==='pull');
    if (!pulls.length) return null;
    pulls.sort((a,b)=>(b.date||'').localeCompare(a.date||'')); // most recent first
    const p = pulls[0];
    return { lf: Number(p.boreLF)||0, size: String(p.pipeSize||job.pipeSize||'').replace(/"/g,'') };
  };
  const totalLF = yearEntries.reduce((s,e) => s + _revNum(e.lf), 0);
  const totalBores = yearEntries.filter(e => _revNum(e.lf) > 0).length;
  const _doy = (() => { const n=new Date(); const s=new Date(n.getFullYear(),0,0); return Math.max(1, Math.floor((n - s)/86400000)); })();
  const projection = (revYear===_curYr) ? total * (365 / _doy) : total; // project only the live year; prior years show actuals
  const _jobs = jobs || [];
  const _completedJobs = _jobs.filter(j=>j.stat==='Complete');
  const _backlogJobs = _jobs.filter(j=>['Approved','Called In','In Progress'].includes(j.stat));
  const _ytdJobs = _completedJobs.reduce((s,j)=>s+(j.arev||j.prev||0),0);
  const _backlogRev = _backlogJobs.reduce((s,j)=>s+Math.max(0,(j.prev||0)-(j.arev||0)),0); // #BACKLOG_FLOOR_V1
  const filtered = filter.trim()
    ? yearEntries.filter(e => [e.pm,e.sup,e.duc,e.customer,e.invNo].some(v => String(v||'').toLowerCase().includes(filter.toLowerCase())))
    : yearEntries;
  const [sort, setSort] = useState({ key:null, dir:'desc' });
  const _sortVal = (e, k) => {
    if (k==='invAmnt'||k==='lf'||k==='bore'||k==='month') return _revNum(e[k]);
    if (k==='pullDate'||k==='invDate') { const m=String(e[k]||'').match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/); return m ? Number(`${m[3].slice(-2)}${String(m[1]).padStart(2,'0')}${String(m[2]).padStart(2,'0')}`) : 0; }
    return String(e[k]||'').toUpperCase();
  };
  const sorted = sort.key
    ? [...filtered].sort((a,b)=>{ const av=_sortVal(a,sort.key), bv=_sortVal(b,sort.key); const c = av<bv?-1:av>bv?1:0; return sort.dir==='asc'?c:-c; })
    : filtered.slice().reverse(); // default: newest first
  const toggleSort = (k) => setSort(s => s.key===k ? { key:k, dir:s.dir==='asc'?'desc':'asc' } : { key:k, dir:'asc' });
  const exportCSV = () => {
    const esc = v => { v = v==null?'':String(v); return /[",\n]/.test(v) ? '"'+v.replace(/"/g,'""')+'"' : v; };
    const head = [...REV_COLS.map(([,lbl])=>lbl), 'Note'].join(',');
    const rows = sorted.map(e => [...REV_COLS.map(([k]) => (k==='invAmnt'||k==='lf'||k==='bore') ? _revNum(e[k]) : esc(e[k])), esc(e.note)].join(','));
    const csv = [head, ...rows].join('\n');
    const blob = new Blob([csv], { type:'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download=`dakota-revenue-${todayStr()}.csv`; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const inp = { fontSize:12, padding:'5px 7px', border:`1px solid ${C.border}`, borderRadius:5, boxSizing:'border-box' };
  const th  = { border:`1px solid #3A5BA8`, padding:'6px 7px', fontSize:11, fontWeight:700, color:'#FFFFFF', background:'#4472C4', textAlign:'left', whiteSpace:'nowrap', position:'sticky', top:0, zIndex:2 };
  const td  = { border:`1px solid ${C.border}`, padding:'4px 7px', fontSize:12.5, color:C.text, whiteSpace:'nowrap' };

  // #REV_SYNC_V1 — push Revenue actuals onto the Job List (ACT REV + ACT LF). Aggregates the ENTIRE
  // line store (all years) by normalized DUC (CP stripped), credits subtract. Only jobs that HAVE
  // revenue lines are touched; jobs with none keep their manual values. Preview before any write.
  const computeSync = () => { // #REV_AUTOSYNC_V1 — pure aggregation, reused by manual review + auto-sync
    const agg = {}; // normKey -> { rev, lf, sampleDuc }
    (entries||[]).forEach(e => {
      const key = _normDuc(e.duc);
      if (!key) return;
      const a = agg[key] || (agg[key] = { rev:0, lf:0, sampleDuc:e.duc });
      a.rev += _revNum(e.invAmnt);
      a.lf  += _revNum(e.lf);
    });
    const jobByKey = {};
    (jobs||[]).forEach(j => { [j.duc, j.displayDuc].forEach(d => { const k=_normDuc(d); if (k && !jobByKey[k]) jobByKey[k]=j; }); });
    const changes = {}; const matched = []; const orphans = [];
    let totalRev = 0, totalLF = 0, lockedCount = 0;
    Object.keys(agg).forEach(key => {
      const a = agg[key]; const j = jobByKey[key];
      if (j) {
        if (j.revLock) { lockedCount++; return; } // #REV_LOCK_V1 — frozen actuals, sync skips it
        const arev = Math.round(a.rev); const alf = Math.round(a.lf);
        changes[j.duc] = { arev, alf };
        matched.push({ duc: j.displayDuc||j.duc, arev, alf });
        totalRev += arev; totalLF += alf;
      } else {
        orphans.push({ key, duc: a.sampleDuc, rev: Math.round(a.rev), lf: Math.round(a.lf) });
      }
    });
    matched.sort((x,y)=>y.arev-x.arev);
    orphans.sort((x,y)=>y.rev-x.rev);
    return { changes, matched, orphans, totalRev, totalLF, lockedCount };
  };
  const buildSyncPreview = () => setSyncPreview(computeSync());

  // #REV_AUTOSYNC_V1 — Bill: stop relying on remembering to click Sync; run it automatically, once
  // per day, the first time anyone with edit rights opens Revenue. Locked jobs are still skipped;
  // unmatched DUCs are still surfaced (as a standing badge, not a blocking popup) so a real DUC
  // mismatch still gets caught instead of silently disappearing into an auto-write.
  const autoSyncRan = useRef(false);
  useEffect(() => {
    if (!loaded || autoSyncRan.current || !onSyncActuals || !canEdit) return;
    autoSyncRan.current = true;
    (async () => {
      const key = todayStr();
      const already = await sg('revenue-sync-lastauto');
      if (already === key) return;
      const preview = computeSync();
      if (Object.keys(preview.changes).length) {
        try { await onSyncActuals(preview.changes); }
        catch (e) {
          setSyncMsg('Auto-sync failed: ' + (e && e.message ? e.message : 'error'));
          setTimeout(()=>setSyncMsg(''), 8000);
          return;
        }
      }
      await ss('revenue-sync-lastauto', key);
      if (preview.matched.length) {
        setSyncMsg(`✓ Auto-synced ${preview.matched.length} job${preview.matched.length!==1?'s':''} to Job List`);
        setTimeout(()=>setSyncMsg(''), 8000);
      }
    })();
  }, [loaded]);

  if (!loaded) return <div style={{ padding:20, color:C.muted }}>Loading revenue input…</div>;
  const confirmSync = async () => {
    if (!syncPreview || !onSyncActuals) { setSyncPreview(null); return; }
    setSyncMsg('Syncing\u2026');
    try {
      await onSyncActuals(syncPreview.changes);
      setSyncMsg(`\u2713 Synced ${syncPreview.matched.length} job${syncPreview.matched.length!==1?'s':''} to Job List`);
    } catch(err){ setSyncMsg('Sync failed: '+(err&&err.message?err.message:'error')); }
    setSyncPreview(null);
    setTimeout(()=>setSyncMsg(''), 8000);
  };

  // #REV_SKIPMISS_V1 — orphans split into "still shown" (warn) vs "acknowledged" (suppressed)
  const _orphansAll   = syncPreview ? syncPreview.orphans : [];
  const _shownOrphans = _orphansAll.filter(o => !ackMiss.includes(o.key));
  const _ackedOrphans = _orphansAll.filter(o =>  ackMiss.includes(o.key));
  const _skipCount    = Object.keys(skipSel).filter(k => skipSel[k]).length;
  const skipChecked = async () => {
    const keys = Object.keys(skipSel).filter(k => skipSel[k]);
    if (!keys.length) return;
    const next = [...new Set([...ackMiss, ...keys])];
    setAckMiss(next); setSkipSel({});
    await ss('revenue-sync-skipmiss', next);
  };
  const resetSkips = async () => { setAckMiss([]); setSkipSel({}); await ss('revenue-sync-skipmiss', []); };

  // #REV_DUP_V3 — standing sweep: every live-year SUP + Date + INV # + Amount group with 2+ lines that
  // Marilyn / an Owner hasn't approved at its current count. Fix = delete the extra line (✕ in the table)
  // or Approve if it's legit.
  const _dupBox = (() => {
    const g = {};
    (entries||[]).forEach(e => { const k = _dupKey(e); if (!k) return; (g[k] = g[k] || []).push(e); });
    const all = Object.entries(g).filter(([,rows]) => rows.length > 1);
    const open = all.filter(([k,rows]) => !(dupCleared[k] && Number(dupCleared[k].n||0) >= rows.length))
                    .sort((a,b) => _revNum(b[1][0].invAmnt)*b[1].length - _revNum(a[1][0].invAmnt)*a[1].length);
    const okKeys = Object.keys(dupCleared||{});
    const cell = { padding:'3px 7px', fontSize:11.5, borderBottom:'1px solid #FECACA', whiteSpace:'nowrap' };
    if (!open.length) {
      return (
        <div style={{ fontSize:11, color:C.muted, margin:'0 0 6px 2px' }}>
          ✓ Duplicate check clear (SUP + Date + INV # + Amount){okKeys.length ? <> · {okKeys.length} approved <span onClick={()=>setDupShowOk(v=>!v)} style={{ cursor:'pointer', textDecoration:'underline' }}>{dupShowOk?'hide':'show'}</span></> : null}
          {dupShowOk && okKeys.map(k => (
            <div key={k} style={{ marginLeft:12 }}>{k.split('|').join(' · ')} — approved by {dupCleared[k].by} {String(dupCleared[k].at||'').slice(0,10)} ({dupCleared[k].n} lines)
              {_canApproveDup && <span onClick={()=>_unapproveDup(k)} style={{ cursor:'pointer', color:C.red, marginLeft:6, fontWeight:700 }}>undo</span>}</div>
          ))}
        </div>
      );
    }
    const atStake = open.reduce((s,[,rows]) => s + _revNum(rows[0].invAmnt)*(rows.length-1), 0);
    return (
      <div style={{ border:'2px solid #DC2626', background:'#FEF2F2', borderRadius:8, padding:'8px 11px', marginBottom:6 }}>
        <div style={{ display:'flex', alignItems:'baseline', gap:10, flexWrap:'wrap' }}>
          <span style={{ fontWeight:900, color:'#B91C1C', fontSize:12.5 }}>⚠ POSSIBLE DUPLICATE REVENUE LINES — {open.length}</span>
          <span style={{ fontSize:11.5, color:'#7F1D1D' }}>Same SUP + Date + INV # + Amount · ${Math.round(atStake).toLocaleString()} counted extra if they're not real</span>
        </div>
        <div style={{ fontSize:11, color:'#7F1D1D', margin:'2px 0 5px' }}>Delete the extra line with ✕ in the table below, or {_canApproveDup ? 'Approve it if it\'s a legitimate duplicate.' : 'ask Marilyn (AR) to approve it if it\'s legitimate.'}</div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ borderCollapse:'collapse', width:'100%' }}>
            <thead><tr>{['SUP','Date','INV #','Amount','Lines','DUC','Customer','LF',''].map(h => <th key={h} style={{ ...cell, textAlign:'left', fontWeight:800, color:'#991B1B' }}>{h}</th>)}</tr></thead>
            <tbody>
              {open.map(([k,rows]) => { const r0 = rows[0]; return (
                <tr key={k}>
                  <td style={{ ...cell, fontWeight:800 }}>{r0.sup}</td>
                  <td style={cell}>{_normDate(r0.pullDate || r0.invDate).val}</td>
                  <td style={cell}>{r0.invNo}</td>
                  <td style={{ ...cell, textAlign:'right' }}>${_revNum(r0.invAmnt).toLocaleString()}</td>
                  <td style={{ ...cell, textAlign:'center', fontWeight:800, color:'#B91C1C' }}>×{rows.length}</td>
                  <td style={cell}>{[...new Set(rows.map(r => r.duc))].join(', ')}</td>
                  <td style={cell}>{[...new Set(rows.map(r => r.customer))].join(', ')}</td>
                  <td style={cell}>{rows.map(r => _revNum(r.lf)).join(' / ')}</td>
                  <td style={cell}>{_canApproveDup
                    ? <button onClick={()=>{ if (window.confirm(`Approve as a LEGITIMATE duplicate?\n\n${r0.sup} · ${_normDate(r0.pullDate||r0.invDate).val} · INV ${r0.invNo} · $${_revNum(r0.invAmnt).toLocaleString()} × ${rows.length} lines\n\nRecorded as approved by ${_dupWho}.`)) _approveDups([k], { [k]: rows.length }); }}
                        style={{ fontSize:11, fontWeight:800, color:'#fff', background:'#B91C1C', border:'none', borderRadius:5, padding:'3px 8px', cursor:'pointer' }}>Approve</button>
                    : <span style={{ fontSize:10.5, color:C.muted }}>AR to approve</span>}</td>
                </tr> ); })}
            </tbody>
          </table>
        </div>
      </div>
    );
  })();

  // #PERIOD_LOCK_V1 — status bar. Owner sees a month picker to roll the close forward / reopen;
  // everyone else sees a read-only note of what's closed, so a blocked save is never a surprise.
  const _lockBar = (() => {
    const cy = new Date().getFullYear();
    const _cl = String(closedThru||'').match(/^(\d{4})-(\d{1,2})$/);
    const label = _cl ? `${_LOCK_MOS[Number(_cl[2])-1]} ${_cl[1]}` : 'not set';
    return (
      <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap',
        background:_isOwner?'#FFF7ED':'#F8FAFC', border:`1px solid ${_isOwner?C.accent:C.border}`,
        borderRadius:8, padding:'7px 11px', marginBottom:6 }}>
        {/* #REV_TOPBAR_V2 (2026-08-28) — Bill: Unlinked Fields, Closed Period, and Year choice were three
            stacked rows — Year folded in here so Closed Period + Year read as one line. */}
        <span style={{ display:'flex', alignItems:'baseline', gap:5, flexShrink:0 }}>
          <span style={{ fontSize:9.5, color:C.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:0.3 }}>Year</span>
          <select value={revYear} onChange={e=>setRevYear(e.target.value==='ALL'?'ALL':Number(e.target.value))}
            style={{ fontSize:12.5, fontWeight:800, color:C.text, border:`1px solid ${C.border}`, borderRadius:6, padding:'3px 5px', background:'#fff', cursor:'pointer' }}>
            {_revYears.map(y=>(<option key={y} value={y}>{y}</option>))}
            <option value="ALL">All</option>
          </select>
        </span>
        <span style={{ fontSize:11.5, fontWeight:800, color:_isOwner?C.accent:C.muted, whiteSpace:'nowrap' }}>
          🔒 CLOSED PERIODS
        </span>
        <span style={{ fontSize:11.5, color:C.muted }}>
          All of {cy-1} and prior{_cl ? `, plus ${cy} through ${label}` : ''} — read-only.
        </span>
        {_isOwner ? (
          <span style={{ display:'flex', alignItems:'center', gap:6, marginLeft:'auto' }}>
            <span style={{ fontSize:11, color:C.muted }}>Close {cy} through:</span>
            <select value={closedThru} onChange={e=>_saveClosedThru(e.target.value)}
              style={{ fontSize:11.5, padding:'3px 6px', borderRadius:6, border:`1px solid ${C.border}` }}>
              <option value="">— open —</option>
              {_LOCK_MOS.map((mo,i)=>(<option key={mo} value={`${cy}-${String(i+1).padStart(2,'0')}`}>{mo} {cy}</option>))}
            </select>
          </span>
        ) : (
          <span style={{ fontSize:10.5, color:C.muted, marginLeft:'auto' }}>Owner can reopen</span>
        )}
      </div>
    );
  })();

  return (
    <div>
      {!_isPhoneNow() && _lockBar}{_dupBox}{/* #PERIOD_LOCK_V1 · #AR_MOBILE_V1 — Year / Closed Periods are desk work; hidden on phones */}
      {/* #REV_SYNC_V1 — preview before writing actuals to the Job List */}
      {syncPreview && (
        <div onClick={()=>setSyncPreview(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:12, padding:20, maxWidth:560, width:'100%', maxHeight:'85vh', overflow:'auto', boxShadow:'0 10px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ fontWeight:800, fontSize:16, color:C.text, marginBottom:6 }}>Sync Revenue → Job List</div>
            <div style={{ fontSize:12.5, color:C.muted, marginBottom:14 }}>
              Overwrites ACT REV &amp; ACT LF on matched jobs from all revenue lines (credits subtract). Jobs with no revenue lines are left untouched.
            </div>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:14 }}>
              <div style={{ flex:1, minWidth:120, background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, padding:'10px 12px' }}>
                <div style={{ fontSize:10.5, fontWeight:700, color:'#15803D', letterSpacing:0.3 }}>JOBS TO UPDATE</div>
                <div style={{ fontSize:22, fontWeight:800, color:'#15803D' }}>{syncPreview.matched.length}</div>
                {syncPreview.lockedCount > 0 && <div style={{ fontSize:10.5, fontWeight:700, color:C.accent, marginTop:2 }}>🔒 {syncPreview.lockedCount} locked · skipped</div>}
              </div>
              <div style={{ flex:1, minWidth:120, background:'#F8FAFC', border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 12px' }}>
                <div style={{ fontSize:10.5, fontWeight:700, color:C.muted, letterSpacing:0.3 }}>TOTAL ACT REV</div>
                <div style={{ fontSize:18, fontWeight:800, color:C.text }}>{f$(syncPreview.totalRev)}</div>
              </div>
              <div style={{ flex:1, minWidth:120, background:'#F8FAFC', border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 12px' }}>
                <div style={{ fontSize:10.5, fontWeight:700, color:C.muted, letterSpacing:0.3 }}>TOTAL ACT LF</div>
                <div style={{ fontSize:18, fontWeight:800, color:C.text }}>{syncPreview.totalLF.toLocaleString()}</div>
              </div>
            </div>
            {/* #REV_RECON_V1 — full-book reconciliation so writing + held back = total invoiced on screen */}
            {(() => {
              const heldRev = Math.max(0, Math.round(total) - syncPreview.totalRev);
              const heldLF  = Math.max(0, Math.round(totalLF) - syncPreview.totalLF);
              const row = (label, sub, rev, lf, bold) => (
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', gap:8, padding:'2px 0' }}>
                  <span style={{ color: bold?C.text:C.muted, fontWeight: bold?800:600 }}>{label}{sub && <span style={{ color:C.muted, fontWeight:600 }}> · {sub}</span>}</span>
                  <span style={{ fontWeight: bold?800:700, color:C.text, whiteSpace:'nowrap', fontVariantNumeric:'tabular-nums' }}>{f$(rev)} · {lf.toLocaleString()} LF</span>
                </div>
              );
              return (
                <div style={{ background:'#F8FAFC', border:`1px solid ${C.border}`, borderRadius:8, padding:'9px 12px', marginBottom:14, fontSize:11.5 }}>
                  {row('Writing now', `${syncPreview.matched.length} job${syncPreview.matched.length!==1?'s':''}`, syncPreview.totalRev, syncPreview.totalLF, false)}
                  {row('Held back', `${syncPreview.lockedCount} locked + ${syncPreview.orphans.length} skipped`, heldRev, heldLF, false)}
                  <div style={{ borderTop:`1px solid ${C.border}`, marginTop:4, paddingTop:4 }}>
                    {row('Total invoiced', `${ducs} DUCs`, Math.round(total), Math.round(totalLF), true)}
                  </div>
                </div>
              );
            })()}
            {_shownOrphans.length > 0 && (
              <div style={{ background:'#FEF3C7', border:'1px solid #F59E0B', borderRadius:8, padding:'10px 12px', marginBottom:14 }}>
                <div style={{ fontSize:12, fontWeight:800, color:'#92400E', marginBottom:6 }}>
                  ⚠ {_shownOrphans.length} revenue DUC{_shownOrphans.length!==1?'s':''} match NO job — skipped:
                </div>
                <div style={{ fontSize:11.5, color:'#92400E', maxHeight:140, overflow:'auto', lineHeight:1.6 }}>
                  {_shownOrphans.map((o)=>(
                    <label key={o.key} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'1px 0' }}>
                      <input type="checkbox" checked={!!skipSel[o.key]} onChange={e=>setSkipSel(p=>({...p,[o.key]:e.target.checked}))} style={{ width:'auto', flexShrink:0 }} />
                      <span>{o.duc} · {f$(o.rev)} · {o.lf.toLocaleString()} LF</span>
                    </label>
                  ))}
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, marginTop:8, borderTop:'1px solid #F59E0B', paddingTop:8 }}>
                  <span style={{ fontSize:10.5, color:'#92400E' }}>Check the cross-year ones (no 2026 job) to stop warning about them.</span>
                  <button onClick={skipChecked} disabled={!_skipCount}
                    style={{ fontSize:11, fontWeight:800, padding:'5px 10px', borderRadius:5, border:'none', whiteSpace:'nowrap',
                      background:_skipCount?'#92400E':'#E5D6A8', color:'#fff', cursor:_skipCount?'pointer':'default' }}>
                    Skip checked{_skipCount?` (${_skipCount})`:''} — don't warn again
                  </button>
                </div>
              </div>
            )}
            {_shownOrphans.length === 0 && _ackedOrphans.length > 0 && (
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, fontSize:11, color:C.muted, marginBottom:14 }}>
                <span>🔕 {_ackedOrphans.length} revenue DUC{_ackedOrphans.length!==1?'s':''} with no job — being skipped.</span>
                <span onClick={resetSkips} style={{ fontWeight:700, color:C.accent, cursor:'pointer' }}>Reset</span>
              </div>
            )}
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
              <Btn onClick={()=>setSyncPreview(null)} variant="secondary">Cancel</Btn>
              <Btn onClick={confirmSync} variant="primary">Write {syncPreview.matched.length} to Job List</Btn>
            </div>
          </div>
        </div>
      )}
      {/* Frozen metric line — Backlog/YTD (Job List) + invoice metrics, with view toggle tucked top-right */}
      <div style={{ position:'sticky', top:71, zIndex:5, background:C.bg, display:'flex', justifyContent:'flex-start', gap:7, overflowX:'auto', padding:'4px 0 10px' }}><style>{`.rev-kpi{transition:transform .15s ease,box-shadow .15s ease;box-shadow:0 1px 2px rgba(15,23,42,.06);}.rev-kpi:hover{transform:translateY(-2px);box-shadow:0 8px 18px rgba(15,23,42,.13);}`}</style>
        {/* #REV_TOPBAR_V2 — Year selector moved up into the Closed Periods bar; not duplicated here. */}
        {[
          { l:'Backlog',        v:`$${(_backlogRev/1e6).toFixed(1)}M`, c:C.accent, s:`${_backlogJobs.length} active jobs`, src:'jobs' },
          { l:'Total Invoiced', v:f$(total) },
          { l:'Total LF Inv',   v:totalLF.toLocaleString() },
          { l:'Total Bores',    v:String(totalBores) },
          { l:'Avg LF/Bore',    v: totalBores ? Math.round(totalLF/totalBores).toLocaleString() : '—' },
          { l:'DUCs Inv',       v:String(ducs) },
          { l:'Avg $/DUC',      v:f$(ducs ? total/ducs : 0) },
          { l:'Avg $/LF',       v: totalLF ? '$'+(total/totalLF).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}) : '—' },
          { l:'Total Entries',  v:String(yearEntries.length) },
          { l: revYear==='ALL' ? 'All-Yr Total' : (revYear===_curYr ? 'Yr-End Proj' : `${revYear} Total`), v:f$(projection) },
        ].map((t,i) => (
          <div key={i} className="rev-kpi" style={{ flex:'0 0 auto', minWidth:112, background: t.src==='jobs' ? 'linear-gradient(180deg,#F3F7FE 0%,#E6EEFA 100%)' : 'linear-gradient(180deg,#FFFFFF 0%,#F6F8FC 100%)', border:`1px solid ${t.src==='jobs' ? '#C7D6EE' : C.border}`, borderLeft: `3px solid ${C.blue}`, borderRadius:10, padding:'8px 11px' }}>
            {t.src==='jobs' && <div style={{ fontSize:8, color:C.blue, fontWeight:800, textTransform:'uppercase', letterSpacing:0.4, whiteSpace:'nowrap', marginBottom:1 }}>From Job List</div>}
            <div style={{ fontSize:9.5, color:C.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:0.3, whiteSpace:'nowrap' }}>{t.l}</div>
            <div style={{ fontSize:16, fontWeight:800, color:t.c||C.text, marginTop:2, whiteSpace:'nowrap' }}>{t.v}</div>
            {t.s && <div style={{ fontSize:9, color:C.muted, marginTop:1, whiteSpace:'nowrap' }}>{t.s}</div>}
          </div>
        ))}
      </div>

      <div style={{ display:'flex', gap:14, alignItems:'flex-start', flexWrap:'wrap' }}>
        <div style={{ flex:'4 1 600px', minWidth:0 }}>

      {/* Manual single-row add — frozen 2nd tier, sits just under the KPI bar */}
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:14, marginBottom:12, position:'sticky', top:138, zIndex:4 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, gap:8 }}>
          <span style={{ fontWeight:800, fontSize:13, color:C.text }}>➕ Add one line manually</span>
          <span onClick={()=>setShowCrew(s=>!s)} style={{ fontSize:11, fontWeight:700, color:C.accent, cursor:'pointer' }}>⚙ {showCrew?'Hide':'Manage'} PM / SUP names</span>
        </div>
        {/* #REV_FORM_HDR_V1 — persistent per-field labels so the column meaning stays visible while typing */}
        <div style={{ display:'flex', gap:6, flexWrap:'nowrap', overflowX:'auto', alignItems:'flex-end', paddingBottom:2 }}>
          {REV_FORM_COLS.map(([k,lbl]) => {
            const Hdr = <span style={{ fontSize:9, fontWeight:800, color:C.muted, textTransform:'uppercase', letterSpacing:0.4, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', marginBottom:3, textAlign:'center' }}>{lbl}</span>;
            if (k==='pm' || k==='sup') {
              const list = k==='pm' ? crew.pms : crew.sups;
              // #REV_PM_LOCK_V1 — job found but its Job List PM isn't a listed PM (Auger): flag it, make her pick
              const _pmHit  = (k==='pm' && !_pmOnList(form.pm)) ? _jobPmFor(form.duc) : null;
              const _pmNeed = !!_pmHit;
              return (
                <div key={k} style={{ flex:'1 1 72px', minWidth:62, display:'flex', flexDirection:'column', position:'relative' }}>
                  {Hdr}
                  <select value={(k==='pm' ? _pmOnList(form.pm) : form[k])||''}
                    onChange={e=>{
                      const v = e.target.value;
                      if (v === '__add__') {
                        const nm = window.prompt(`Add new ${k==='pm'?'PM':'Supervisor'} name:`);
                        if (nm && nm.trim()) { addCrew(k==='pm'?'pms':'sups', nm); setForm(p=>({...p,[k]:nm.trim()})); }
                        return;
                      }
                      if (k==='pm') pmAutoRef.current = false;   // hand-picked — a later DUC lookup won't clear it
                      setForm(p=>({...p,[k]:v}));
                    }}
                    style={{ ...inp, width:'100%', ...(_pmNeed ? { border:'1.5px solid #DC2626', background:'#FEF2F2' } : {}) }}>
                    <option value="">{lbl}</option>
                    {(list||[]).map(n => <option key={n} value={n}>{n}</option>)}
                    {k==='sup' && <option value="__add__">＋ Add new…</option>}
                  </select>
                  {_pmNeed && (
                    <div title={_pmHit.raw ? `The Job List shows "${_pmHit.raw}" on this job — that is not a PM on the list. Pick the PM.` : 'No PM on the Job List for this job — pick the PM.'}
                      style={{ position:'absolute', top:'100%', left:0, right:0, marginTop:2, fontSize:9, fontWeight:800, lineHeight:1.1, color:'#B91C1C', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      ⚠ Pick PM
                    </div>
                  )}
                </div>
              );
            }
            if (k==='duc') {
              // #DUC_GATE_V1 — live check the typed job # against the Job List; green = identified, red = not found
              const _typed = String(form.duc||'').trim();
              const _hit = _typed ? jobByDuc(_typed) : null;
              const _state = !_typed ? 'empty' : (_hit ? 'ok' : 'bad');
              const _bc = _state==='ok' ? '#16A34A' : _state==='bad' ? '#DC2626' : C.border;
              const _bg = _state==='ok' ? '#ECFDF5' : _state==='bad' ? '#FEF2F2' : '#FFFFFF';
              return (
                <div key={k} style={{ flex:'1 1 122px', minWidth:104, display:'flex', flexDirection:'column', position:'relative' }}>
                  {Hdr}
                  <input value={form.duc}
                    onChange={e=>_ducFill(e.target.value)}
                    placeholder={lbl}
                    style={{ ...inp, width:'100%', border:`1.5px solid ${_bc}`, background:_bg, fontWeight:700 }} />
                  {_state!=='empty' && (
                    <div title={_state==='ok' ? [_hit.cust, _hit.city, _hit.stat].filter(Boolean).join(' · ') : 'No matching job number on the Job List'}
                      style={{ position:'absolute', top:'100%', left:0, right:0, marginTop:2, fontSize:9, fontWeight:800, lineHeight:1.1,
                        color: _state==='ok' ? '#15803D' : '#B91C1C', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {_state==='ok' ? `✓ ${_hit.cust || '(job found)'}` : '⚠ Not in Job List'}
                    </div>
                  )}
                </div>
              );
            }
            if (k==='customer') {   // #REV_CUST_LOCK_V1 — locked list, same as the Job List
              return (
                <div key={k} style={{ flex:'1 1 150px', minWidth:130, display:'flex', flexDirection:'column' }}>
                  {Hdr}
                  <CustomerPicker value={form.customer} onChange={v=>setForm(p=>({ ...p, customer:v }))}
                    options={custOptions} placeholder="Customer" style={{ ...inp, width:'100%' }} />
                </div>
              );
            }
            if (k==='pullDate' || k==='invDate') {
              // #REV_DATE_GATE_V1 — live red flag when the typed date isn't a complete MM/DD/YYYY
              const _raw = String(form[k]||'').trim();
              const _bad = _raw && !_validDate(_raw);
              return (
                <div key={k} style={{ flex:'1 1 84px', minWidth:74, display:'flex', flexDirection:'column', position:'relative' }}>
                  {Hdr}
                  <input value={form[k]} onChange={e=>setForm(p=>({ ...p, [k]:e.target.value }))}
                    placeholder="MM/DD/YYYY"
                    style={{ ...inp, width:'100%', ...(_bad ? { border:'1.5px solid #DC2626', background:'#FEF2F2' } : {}) }} />
                  {_bad && <div style={{ position:'absolute', top:'100%', left:0, right:0, marginTop:2, fontSize:9, fontWeight:800, color:'#B91C1C', whiteSpace:'nowrap' }}>⚠ MM/DD/YYYY</div>}
                </div>
              );
            }
            return (
              <div key={k} style={{ flex:'1 1 72px', minWidth:62, display:'flex', flexDirection:'column' }}>
                {Hdr}
                <input value={form[k]}
                  onChange={e=>{
                    const v = e.target.value;
                    if (k==='duc') {
                      _ducFill(v);
                    } else {
                      setForm(p => ({ ...p, [k]: v }));
                    }
                  }}
                  placeholder={lbl} style={{ ...inp, width:'100%' }} />
              </div>
            );
          })}
          {canEdit && <div style={{ flex:'0 0 auto' }}><Btn onClick={addManual} variant="primary" small><Plus size={12}/>Add</Btn></div>}
        </div>

        {showCrew && (
        <div style={{ marginTop:12, borderTop:`1px solid ${C.border}`, paddingTop:12, display:'flex', gap:16, flexWrap:'wrap' }}>
          {[['pms','PMs'],['sups','Supervisors']].map(([key,label]) => (
            <div key={key} style={{ flex:'1 1 260px', minWidth:0 }}>
              <div style={{ fontWeight:700, fontSize:12, color:C.text, marginBottom:6 }}>{label} <span style={{ color:C.muted, fontWeight:500 }}>· {(crew[key]||[]).length}</span></div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginBottom:8 }}>
                {(crew[key]||[]).map(n => (
                  <span key={n} style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:99, padding:'3px 9px', fontSize:11, display:'inline-flex', alignItems:'center', gap:6 }}>
                    {n}{!(key==='pms' && !_isOwner) && <span onClick={()=>removeCrew(key,n)} title="Remove" style={{ cursor:'pointer', color:C.red, fontWeight:800 }}>×</span>}
                  </span>
                ))}
                {!(crew[key]||[]).length && <span style={{ fontSize:11, color:C.muted }}>None yet.</span>}
              </div>
              {(key==='pms' && !_isOwner)
                ? <div style={{ fontSize:10.5, color:C.muted, fontWeight:600 }}>🔒 PM list — Owner only</div>
                : <CrewAdder onAdd={(v)=>addCrew(key,v)} placeholder={`Add ${label.slice(0,-1)}…`} />}
            </div>
          ))}
        </div>
        )}
      </div>

      {/* Table */}
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:14 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, gap:8, flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <div style={{ fontWeight:800, fontSize:13, color:C.text }}>Invoice Lines <span style={{ color:C.muted, fontWeight:500 }}>· {filtered.length}{filtered.length!==yearEntries.length?` of ${yearEntries.length}`:''}</span></div>
            {/* #REV_FILTER_TOTALS_V1 — sum of the currently shown lines (Bores · LF · REV) */}
            {(() => {
              const _fBore = filtered.reduce((s,e)=>s+_revNum(e.bore),0);
              const _fLF   = filtered.reduce((s,e)=>s+_revNum(e.lf),0);
              const _fRev  = filtered.reduce((s,e)=>s+_revNum(e.invAmnt),0);
              return (
                <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, fontWeight:700, background:'#FFF4EE', border:`1px solid ${C.border}`, borderRadius:8, padding:'3px 10px' }}>
                  <span style={{ color:C.text }}>{_fBore.toLocaleString()} <span style={{ color:C.muted, fontWeight:400 }}>bores</span></span>
                  <span style={{ color:C.muted, fontWeight:400 }}>·</span>
                  <span style={{ color:C.text }}>{_fLF.toLocaleString()} <span style={{ color:C.muted, fontWeight:400 }}>LF</span></span>
                  <span style={{ color:C.muted, fontWeight:400 }}>·</span>
                  <span style={{ color:C.accent }}>${_fRev.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                </div>
              );
            })()}
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>   {/* #REV_MOBILE_WRAP_V1 — wraps instead of squeezing syncMsg to near-zero width on narrow screens */}
            {syncMsg && <span style={{ fontSize:11, fontWeight:700, whiteSpace:'nowrap', color: syncMsg.startsWith('\u2713')?C.green:(syncMsg.startsWith('Sync failed')?C.red:C.muted) }}>{syncMsg}</span>}
            {/* #REV_AUTOSYNC_V1 — sync now runs automatically; this badge just surfaces unmatched DUCs that still need a human look */}
            {canEdit && (() => {
              const _unacked = computeSync().orphans.filter(o => !ackMiss.includes(o.key));
              return _unacked.length > 0 ? (
                <span onClick={buildSyncPreview} title="Revenue lines whose DUC matches no Job List job — click to review"
                  style={{ cursor:'pointer', fontSize:11, fontWeight:800, color:'#92400E', background:'#FEF3C7',
                    border:'1px solid #F59E0B', borderRadius:6, padding:'3px 8px', whiteSpace:'nowrap' }}>
                  ⚠ {_unacked.length} unmatched DUC{_unacked.length!==1?'s':''}
                </span>
              ) : (
                <span style={{ fontSize:10.5, color:C.muted, whiteSpace:'nowrap' }}>Auto-syncs daily ✓</span>
              );
            })()}
            <input value={filter} onChange={e=>setFilter(e.target.value)} placeholder="Filter PM / DUC / customer / inv #…" style={{ ...inp, flex:'1 1 160px', minWidth:140, maxWidth:220 }} />
            {canEdit && <Btn onClick={buildSyncPreview} variant="secondary" small title="Review matches, locked jobs, and unmatched DUCs — force a re-sync now">🔄 Review Sync</Btn>}
            <Btn onClick={exportCSV} variant="secondary" small>⬇ CSV</Btn>
          </div>
        </div>
        {_isPhoneNow() && <div style={{ fontSize:10.5, fontWeight:800, color:C.accent, margin:'2px 0 4px' }}>⇆ Swipe the table sideways for more columns</div>}{/* #AR_MOBILE_V1 */}
        <div data-rev-scroll style={{ overflow:'auto', maxHeight:'65vh' }}>
          <table style={{ borderCollapse:'collapse', width:'100%', minWidth:900 }}>
            <thead><tr>{REV_COLS.map(([k,lbl])=>(
              <th key={k} onClick={()=>toggleSort(k)} style={{...th, cursor:'pointer', userSelect:'none'}} title="Click to sort">
                {lbl}{sort.key===k ? (sort.dir==='asc'?' ▲':' ▼') : ''}
              </th>
            ))}<th style={th}></th></tr></thead>
            <tbody>
              {sorted.map((e,i) => (
                <tr key={e.id} style={{ background: i%2 ? '#EDF2FB' : '#FFFFFF' }}>
                  {REV_COLS.map(([k]) => {
                    const CENTER = ['jobType','pullDate','month','invDate','invNo','bore','lf','size'];
                    if (k==='invAmnt') {
                      const n = _revNum(e[k]);
                      const neg = n < 0; // #REV_CREDIT_RED_V1 — credits show $(1,500.00) in red
                      const disp = Math.abs(n).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});
                      return (
                        <td key={k} style={{...td, padding:0, fontWeight:700}}>
                          {n ? (
                            neg ? (
                              <div style={{ textAlign:'right', padding:'4px 7px', color:C.red }}>
                                {`$(${disp})`}
                              </div>
                            ) : (
                              <div style={{ display:'flex', justifyContent:'space-between', gap:10, padding:'4px 7px' }}>
                                <span>$</span><span>{disp}</span>
                              </div>
                            )
                          ) : <div style={{ textAlign:'right', padding:'4px 7px' }}>—</div>}
                        </td>
                      );
                    }
                    const align = CENTER.includes(k) ? 'center' : 'left';
                    let val;
                    if (k==='lf')         val = _revNum(e[k]) ? _revNum(e[k]).toLocaleString() : '';
                    else if (k==='bore')  val = _revNum(e[k]) || '';
                    else if (k==='size')  val = e[k] ? (String(e[k]).includes('"') ? e[k] : e[k]+'"') : '';
                    else if (k==='pullDate'||k==='invDate'){const _s=String(e[k]||'').trim();let _m=_s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);if(_m){val=`${_m[1].padStart(2,'0')}/${_m[2].padStart(2,'0')}/${_m[3].slice(-2)}`;}else{_m=_s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);val=_m?`${_m[2].padStart(2,'0')}/${_m[3].padStart(2,'0')}/${_m[1].slice(-2)}`:_s;}}
                    else                  val = e[k] || '';
                    return <td key={k} style={{...td, textAlign:align}}>{val}</td>;
                  })}
                  <td style={{...td, textAlign:'center', whiteSpace:'nowrap'}}>
                    {e._hist ? <span title={`${e._hy} history · read-only`} style={{ fontSize:11, color:C.muted }}>🔒 {e._hy}</span> : !canEdit ? <span title="View only" style={{ fontSize:12, color:C.muted }}>🔒</span> : <>
                    <span onClick={()=>openEdit(e)} title="Edit line" style={{ cursor:'pointer', marginRight:16, fontSize:13 }}>✏️</span>
                    <span onClick={()=>openNote(e)} title={e.note ? e.note : 'Add note'} style={{ cursor:'pointer', marginRight:16, fontSize:13, opacity: e.note ? 1 : 0.3 }}>📝</span>
                    <span onClick={()=>removeEntry(e)} title="Remove" style={{ cursor:'pointer', color:C.red, fontWeight:800, fontSize:15, padding:'0 4px' }}>×</span>
                    </>}
                  </td>
                </tr>
              ))}
              {!sorted.length && <tr><td colSpan={REV_COLS.length+1} style={{...td, textAlign:'center', color:C.muted, padding:'16px'}}>No invoice lines yet — paste from the INPUT tab below.</td></tr>}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize:11, color:C.muted, marginTop:8 }}>Showing all {sorted.length} lines{sort.key?` · sorted by ${(REV_COLS.find(c=>c[0]===sort.key)||[])[1]} ${sort.dir==='asc'?'↑':'↓'}`:''}. Click a column header to sort; ⬇ CSV exports what's shown.</div>
      </div>

      {editRow && canEdit && (
        <div onClick={()=>setEditRow(null)} style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div onClick={ev=>ev.stopPropagation()} style={{ background:C.card, borderRadius:12, padding:18, width:'100%', maxWidth:560, maxHeight:'88vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.35)' }}>
            <div style={{ fontWeight:800, fontSize:14, color:C.text, marginBottom:2 }}>✏️ Edit invoice line</div>
            <div style={{ fontSize:12, color:C.muted, marginBottom:12 }}>{(editForm.duc||'—')} · {(editForm.customer||'')}</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {REV_COLS.map(([k,lbl]) => (
                <div key={k} style={{ display:'flex', flexDirection:'column', gap:3 }}>
                  <label style={{ fontSize:10, fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:0.3 }}>{lbl}</label>
                  {(k==='pm'||k==='sup') ? (
                    <select value={editForm[k]||''} onChange={ev=>setEditForm(p=>({...p,[k]:ev.target.value}))} style={{ ...inp }}>
                      <option value="">—</option>
                      {[...new Set([...((k==='pm'?crew.pms:crew.sups)||[]), editForm[k]].filter(Boolean))].map(n=><option key={n} value={n}>{n}</option>)}
                    </select>
                  ) : k==='invAmnt' ? (
                    <div style={{ display:'flex', alignItems:'center', border:`1px solid ${C.border}`, borderRadius:5, overflow:'hidden' }}>
                      <span style={{ padding:'5px 7px', color:C.muted, background:'#F1F5F9', fontSize:12, fontWeight:700 }}>$</span>
                      <input value={editForm.invAmnt==null?'':editForm.invAmnt}
                        onFocus={()=>setEditForm(p=>({...p, invAmnt:_rawNum(p.invAmnt)}))}
                        onBlur={()=>setEditForm(p=>({...p, invAmnt:_fmtMoney(p.invAmnt)}))}
                        onChange={ev=>setEditForm(p=>({...p, invAmnt:ev.target.value}))}
                        style={{ ...inp, border:'none', flex:1, textAlign:'right' }} />
                    </div>
                  ) : k==='lf' ? (
                    <input value={editForm.lf==null?'':editForm.lf}
                      onFocus={()=>setEditForm(p=>({...p, lf:_rawNum(p.lf)}))}
                      onBlur={()=>setEditForm(p=>({...p, lf:_fmtNum(p.lf)}))}
                      onChange={ev=>setEditForm(p=>({...p, lf:ev.target.value}))}
                      style={{ ...inp, textAlign:'right' }} />
                  ) : k==='customer' ? (
                    <CustomerPicker value={editForm.customer||''} onChange={v=>setEditForm(p=>({...p, customer:v}))}
                      options={custOptions} placeholder="Customer" style={{ ...inp }} />
                  ) : k==='size' ? (
                    <input value={editForm.size==null?'':editForm.size}
                      onFocus={()=>setEditForm(p=>({...p, size:String(p.size||'').replace(/"$/,'')}))}
                      onBlur={()=>setEditForm(p=>({...p, size:(p.size&&!String(p.size).includes('"'))?String(p.size)+'"':p.size}))}
                      onChange={ev=>setEditForm(p=>({...p, size:ev.target.value}))}
                      style={{ ...inp }} />
                  ) : (
                    <input value={editForm[k]==null?'':editForm[k]} onChange={ev=>setEditForm(p=>({...p,[k]:ev.target.value}))} style={{ ...inp }} />
                  )}
                </div>
              ))}
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:14 }}>
              <Btn onClick={()=>setEditRow(null)} variant="secondary" small>Cancel</Btn>
              <Btn onClick={saveEdit} small>Save changes</Btn>
            </div>
          </div>
        </div>
      )}

      {noteRow && (
        <div onClick={()=>setNoteRow(null)} style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div onClick={ev=>ev.stopPropagation()} style={{ background:C.card, borderRadius:12, padding:18, width:'100%', maxWidth:440, boxShadow:'0 20px 60px rgba(0,0,0,0.35)' }}>
            <div style={{ fontWeight:800, fontSize:14, color:C.text, marginBottom:2 }}>📝 Note</div>
            <div style={{ fontSize:12, color:C.muted, marginBottom:10 }}>{(noteRow.duc||'—')} · {(noteRow.customer||'')}{noteRow.invNo?` · INV ${noteRow.invNo}`:''}</div>
            <textarea value={noteText} onChange={ev=>setNoteText(ev.target.value)} autoFocus placeholder="Credit, adjustment, non-bore revenue, reminder for Marilyn…" style={{ width:'100%', minHeight:120, fontSize:13, padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius:7, boxSizing:'border-box', resize:'vertical', fontFamily:'inherit' }} />
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:12, gap:8 }}>
              <Btn onClick={()=>{ setNoteText(''); }} variant="secondary" small>Clear</Btn>
              <div style={{ display:'flex', gap:8 }}>
                <Btn onClick={()=>setNoteRow(null)} variant="secondary" small>Cancel</Btn>
                <Btn onClick={saveNote} small>Save note</Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:10, marginTop:12 }}>
        <div style={{ fontWeight:800, fontSize:12, color:C.accent, marginBottom:3 }}>📥 Paste from Excel (INPUT tab) <span style={{ fontWeight:500, fontSize:10, color:C.muted }}>— bulk load, cols A–M; header row skipped</span></div>
        <textarea value={pasteText} onChange={e=>setPaste(e.target.value)} rows={2}
          placeholder="Paste rows here…"
          style={{ width:'100%', boxSizing:'border-box', fontSize:11, fontFamily:'monospace', padding:7, border:`1px solid ${C.border}`, borderRadius:6, resize:'vertical' }} />
        <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:6, flexWrap:'wrap' }}>
          <Btn onClick={importPaste} variant="primary" small>Import pasted rows</Btn>
          <Btn onClick={clearAll} variant="secondary" small>Clear all</Btn>
          {msg && <span style={{ fontSize:12, color:C.green, fontWeight:600 }}>{msg}</span>}
        </div>
      </div>
        </div>

        <div style={{ flex:'1 1 250px', minWidth:230, maxWidth:300, position:'sticky', top:150 }}>
          {setView && (
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', justifyContent:'flex-end', marginBottom:8 }}>
            {[['pm','👤 PM'],['svc','🔧 SVC'],['sup','👷 SUP'],['jobs','📋 JOBS'],['budget','📊 BUDGET'],['week','📅 WK'],['sum','📈 SUM'],['yoy','🔁 YOY'],['aging','⏳ AGING']].map(([v,l])=>(
              <Btn key={v} onClick={()=>setView(v)} variant={view===v?'primary':'secondary'} small>{l}</Btn>
            ))}
          </div>
          )}
          <PullChecklist entries={entries} jobs={jobs} onPlaceholder={(seed)=>{ const h = _jobPmFor(seed.duc); if (h && h.pm) pmAutoRef.current = true; setForm(p=>({ ...p, ...seed, ...(h && h.pm ? { pm:h.pm } : {}) })); }} canEdit={canEdit} />
        </div>
      </div>
    </div>
  );
}

// #BUDGET_RPT_V1 — Budget vs. Invoiced (vs. 2025) monthly table, styled to mirror the workbook.
// Budget & 2025 are baked-in reference data; Invoiced is computed live from the invoice lines.
function BudgetReport({ inv }) {
  const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  const _mo = e => parseInt(e.month,10) || (()=>{ const x=String(e.invDate||'').match(/(\d{1,2})[\/\-]/); return x?parseInt(x[1],10):0; })();
  const invoiced = Array(12).fill(0);
  (inv||[]).forEach(e => { const m=_mo(e); if (m>=1&&m<=12) invoiced[m-1] += _revNum(e.invAmnt); });
  const chartData = MONTHS.map((mo,i)=>({ name:mo, Budget:BUDGET_2026[i], Invoiced:invoiced[i], '2025':ACTUAL_2025[i] }));
  // #BUDGET_RPT_V1 — cumulative YTD pace: Plan (budget) full year vs Actual (invoiced), which stops at the latest invoiced month
  const _lastInv = invoiced.reduce((acc,v,i)=> v>0 ? i : acc, -1);
  let _cb=0, _ci=0;
  const paceData = MONTHS.map((mo,i)=>{ _cb+=BUDGET_2026[i]; _ci+=invoiced[i]; return { name:mo, Budget:_cb, Invoiced: i<=_lastInv ? _ci : null }; });
  const sum = a => a.reduce((s,x)=>s+x,0);
  // #BUDGET_RPT_V1 — live 2026 quarterly summary from the invoice lines. % = quarter REV ÷ quarter budget.
  const q26 = [0,1,2,3].map(q => {
    const months = [q*3+1, q*3+2, q*3+3];
    const lines = (inv||[]).filter(e => months.includes(_mo(e)));
    const rev = sum(lines.map(e=>_revNum(e.invAmnt)));
    const lf  = sum(lines.map(e=>_revNum(e.lf)));
    const bores = sum(lines.map(e=>_revNum(e.bore)));
    const jobs = new Set(lines.map(e=>String(e.duc||'').trim().toUpperCase()).filter(Boolean)).size;
    const budget = months.reduce((s,m)=>s+BUDGET_2026[m-1],0);
    return { q:`Q${q+1}`, rev, lf, bores, jobs, inv:lines.length, pct: budget?rev/budget:0 };
  });
  const q26Tot = { rev:sum(q26.map(x=>x.rev)), lf:sum(q26.map(x=>x.lf)), bores:sum(q26.map(x=>x.bores)), jobs:sum(q26.map(x=>x.jobs)), inv:sum(q26.map(x=>x.inv)) };
  const revPerLF = q26Tot.lf ? q26Tot.rev/q26Tot.lf : 0;
  const avgLen   = q26Tot.bores ? q26Tot.lf/q26Tot.bores : 0;
  const num = n => Math.round(n).toLocaleString();
  // #BUDGET_RPT_V1 — one renderer for a year's quarterly table (live 2026 + baked prior years)
  const qtr = ({ label, rows, tot, revLF, avgLen, live }) => (
    <div key={label} style={{ marginBottom:14 }}>
      <div style={{ fontWeight:700, fontSize:13, color:C.text, marginBottom:6 }}>{label} Quarterly{live && <span style={{ fontWeight:500, fontSize:11, color:C.muted }}> (live)</span>}</div>
      <div style={{ overflowX:'auto', background:'#FFFFFF', borderRadius:8, border:`1px solid ${C_LINE}` }}>
        <table style={{ borderCollapse:'collapse', width:'100%', minWidth:300 }}>
          <thead><tr>
            <th style={th('left')}>{label}</th>
            <th style={th()}>REV</th>
            <th style={th()}>LF</th>
            <th style={th()}>JOBS</th>
            <th style={th()}>Inv</th>
            <th style={th()}>Bores</th>
            <th style={th()}>%</th>
          </tr></thead>
          <tbody>
            {rows.map(r=>(
              <tr key={r.q}>
                <td style={td('left', { fontWeight:700 })}>{r.q}</td>
                <td style={td('right', { fontWeight:700 })}>{money(r.rev)}</td>
                <td style={td()}>{num(r.lf)}</td>
                <td style={td()}>{r.jobs}</td>
                <td style={td()}>{r.inv}</td>
                <td style={td()}>{num(r.bores)}</td>
                <td style={td('right', { color:C.muted })}>{(r.pct*100).toFixed(1)}%</td>
              </tr>
            ))}
            <tr>
              <td style={td('left', { fontWeight:800, borderTop:`2px solid #111` })}>Tot</td>
              <td style={td('right', { fontWeight:800, borderTop:`2px solid #111` })}>{money(tot.rev)}</td>
              <td style={td('right', { fontWeight:800, borderTop:`2px solid #111` })}>{num(tot.lf)}</td>
              <td style={td('right', { fontWeight:800, borderTop:`2px solid #111` })}>{tot.jobs}</td>
              <td style={td('right', { fontWeight:800, borderTop:`2px solid #111` })}>{tot.inv}</td>
              <td style={td('right', { fontWeight:800, borderTop:`2px solid #111` })}>{num(tot.bores)}</td>
              <td style={td('right', { borderTop:`2px solid #111` })}></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div style={{ display:'flex', gap:16, flexWrap:'wrap', fontSize:11, color:C.muted, marginTop:6 }}>
        <span>REV / LF: <b style={{ color:C.text }}>${revLF.toFixed(2)}</b></span>
        <span>Avg Length: <b style={{ color:C.text }}>{num(avgLen)}</b></span>
      </div>
    </div>
  );
  const money = v => v ? `$${Math.round(v).toLocaleString()}` : '$  -';
  const moneyVar = v => { const n=Math.round(v); return n<0 ? `$(${Math.abs(n).toLocaleString()})` : `$${n.toLocaleString()}`; };
  // #ACCT_ALIGN_V1 — $ pinned left, digits pinned right, so every $ lines up down the column
  const acct = v => { const n=Math.round(v||0); return (<span style={{display:'flex',justifyContent:'space-between',gap:12}}><span>$</span><span>{n?n.toLocaleString():'-'}</span></span>); };
  const acctVar = v => { const n=Math.round(v||0); const body=n<0?`(${Math.abs(n).toLocaleString()})`:n.toLocaleString(); return (<span style={{display:'flex',justifyContent:'space-between',gap:12}}><span>$</span><span>{n?body:'-'}</span></span>); };
  const budTot = sum(BUDGET_2026), invTot = sum(invoiced), actTot = sum(ACTUAL_2025), act24Tot = sum(ACTUAL_2024);
  const C_LINE = '#D1D5DB';
  const th  = (al='right') => ({ padding:'4px 11px', fontSize:11.5, fontWeight:800, textAlign:al, background:'#E5E7EB', color:'#111', borderBottom:`1px solid ${C_LINE}`, verticalAlign:'bottom' });
  const td  = (al='right', extra={}) => ({ padding:'2px 11px', fontSize:12.5, textAlign:al, color:'#111', borderBottom:`1px solid ${C_LINE}`, whiteSpace:'nowrap', ...extra });
  // #WIND_V1 — net tail/head wind across COMPLETED months only; a month counts once it's fully behind us (rolls forward on the 1st)
  const _now = new Date();
  const _curMo = _now.getFullYear() > 2026 ? 12 : (_now.getFullYear() < 2026 ? 0 : _now.getMonth());
  const windSum = MONTHS.reduce((s,mo,i)=> i < _curMo ? s + (invoiced[i]-BUDGET_2026[i]) : s, 0);
  const windPos = windSum >= 0;
  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:12 }}>
      <div style={{ fontWeight:800, fontSize:14, color:C.text, marginBottom:8 }}>📊 Budget vs. Invoiced — 2026</div>
      <div style={{ display:'flex', gap:14, flexWrap:'wrap', alignItems:'flex-start' }}>

        {/* LEFT — monthly table + chart */}
        <div style={{ flex:'1 1 540px', minWidth:320 }}>
          <div style={{ overflowX:'auto', background:'#FFFFFF', borderRadius:8, border:`1px solid ${C_LINE}` }}>
            <table style={{ borderCollapse:'collapse', width:'100%', minWidth:560 }}>
              <thead><tr>
                <th style={th('left')}>2026</th>
                <th style={th()}>Budget</th>
                <th style={th()}>Invoiced</th>
                {/* #WIND_V1 — tail/head wind chip hovering over the Variance header (completed months only) */}
                <th style={{ ...th(), position:'relative', paddingTop:32 }}>
                  <div style={{ position:'absolute', top:3, left:'50%', transform:'translateX(-50%)', display:'inline-flex', alignItems:'center', gap:6, padding:'2px 9px', borderRadius:7, whiteSpace:'nowrap', background: windPos?'#DCFCE7':'#FEE2E2', border:`1px solid ${windPos?'#86EFAC':'#FCA5A5'}`, boxShadow:'0 1px 3px rgba(0,0,0,0.14)' }}>
                    <span style={{ fontSize:9.5, fontWeight:800, letterSpacing:0.3, textTransform:'uppercase', color: windPos?'#166534':'#B91C1C' }}>{windPos?'Tailwind':'Headwind'}</span>
                    <span style={{ fontSize:12.5, fontWeight:800, color: windPos?'#166534':'#B91C1C' }}>{moneyVar(windSum)}</span>
                    <span style={{ fontSize:8.5, fontWeight:700, color:C.muted }}>thru {MONTHS[Math.max(0,_curMo-1)]}</span>
                  </div>
                  Variance
                </th>
                <th style={{ ...th(), borderLeft:'2px solid #9CA3AF' }}>2025</th>
                <th style={th()}>2024</th>
              </tr></thead>
              <tbody>
                {MONTHS.map((mo,i)=>{
                  const v = invoiced[i] - BUDGET_2026[i];
                  return (
                    <tr key={mo}>
                      <td style={td('left', { fontWeight:700 })}>{mo}</td>
                      <td style={td('right', { background:'#FDE68A', fontWeight:600 })}>{acct(BUDGET_2026[i])}</td>
                      <td style={td('right', { fontWeight:800 })}>{acct(invoiced[i])}</td>
                      <td style={td('right', { color: v<0 ? '#B91C1C' : '#111' })}>{acctVar(v)}</td>
                      <td style={td('right', { fontWeight:700, color:C.muted, borderLeft:'2px solid #9CA3AF' })}>{acct(ACTUAL_2025[i])}</td>
                      <td style={td('right', { fontWeight:700, color:C.muted })}>{acct(ACTUAL_2024[i])}</td>
                    </tr>
                  );
                })}
                <tr>
                  <td style={td('left', { fontWeight:800, borderTop:`2px solid #111` })}>2026</td>
                  <td style={td('right', { background:'#FCD34D', fontWeight:800, borderTop:`2px solid #111` })}>{acct(budTot)}</td>
                  <td style={td('right', { fontWeight:800, borderTop:`2px solid #111` })}>{acct(invTot)}</td>
                  <td style={td('right', { fontWeight:800, color:(invTot-budTot)<0?'#B91C1C':'#111', borderTop:`2px solid #111` })}>{acctVar(invTot-budTot)}</td>
                  <td style={td('right', { fontWeight:800, color:C.muted, borderTop:`2px solid #111`, borderLeft:'2px solid #9CA3AF' })}>{acct(actTot)}</td>
                  <td style={td('right', { fontWeight:800, color:C.muted, borderTop:`2px solid #111` })}>{acct(act24Tot)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {/* #BUDGET_RPT_V1 — Budget / Invoiced / 2025 by month (mirrors the workbook chart) */}
          <div style={{ marginTop:10 }}>
            <div style={{ display:'flex', gap:14, flexWrap:'wrap', alignItems:'center', marginBottom:6 }}>
              <div style={{ fontWeight:700, fontSize:13, color:C.text }}>Budget vs. Invoice</div>
              {[['Budget','#2563EB'],['Invoiced','#EA580C'],['2025','#9CA3AF']].map(([l,c])=>(
                <span key={l} style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11, color:C.muted }}>
                  <span style={{ width:11, height:11, background:c, borderRadius:2, display:'inline-block' }} />{l}
                </span>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={chartData} margin={{ left:6, right:6, top:4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="name" tick={{ fill:C.muted, fontSize:11 }} />
                <YAxis tick={{ fill:C.muted, fontSize:10 }} tickFormatter={v=>`$${(v/1e6).toFixed(0)}M`} />
                <Tooltip contentStyle={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:6, color:C.text }} formatter={v=>`$${Math.round(v).toLocaleString()}`} />
                <Bar dataKey="Budget"   fill="#2563EB" radius={[3,3,0,0]} />
                <Bar dataKey="Invoiced" fill="#EA580C" radius={[3,3,0,0]} />
                <Bar dataKey="2025"     fill="#9CA3AF" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* #BUDGET_RPT_V1 — cumulative pace: running YTD Plan (budget) vs Actual (invoiced) */}
          <div style={{ marginTop:14 }}>
            <div style={{ display:'flex', gap:14, flexWrap:'wrap', alignItems:'center', marginBottom:6 }}>
              <div style={{ fontWeight:700, fontSize:13, color:C.text }}>Cumulative Pace — Plan vs Actual</div>
              {[['Plan (Budget)','#2563EB'],['Actual (Invoiced)','#EA580C']].map(([l,c])=>(
                <span key={l} style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11, color:C.muted }}>
                  <span style={{ width:11, height:11, background:c, borderRadius:2, display:'inline-block' }} />{l}
                </span>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={paceData} margin={{ left:6, right:6, top:4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="name" tick={{ fill:C.muted, fontSize:11 }} />
                <YAxis tick={{ fill:C.muted, fontSize:10 }} tickFormatter={v=>`$${(v/1e6).toFixed(0)}M`} />
                <Tooltip contentStyle={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:6, color:C.text }} formatter={v=>v==null?'—':`$${Math.round(v).toLocaleString()}`} />
                <Line type="monotone" dataKey="Budget"   name="Plan"   stroke="#2563EB" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="Invoiced" name="Actual" stroke="#EA580C" strokeWidth={2.5} dot={{ r:2 }} connectNulls={false} />
              </LineChart>
            </ResponsiveContainer>
            <div style={{ fontSize:10, color:C.muted, marginTop:4 }}>Running YTD total. The Actual line stops at the latest invoiced month — the gap to Plan is how far behind/ahead you are.</div>
          </div>
        </div>

        {/* RIGHT — quarterly by year: 2026 live, prior years baked in */}
        <div style={{ flex:'1 1 340px', minWidth:300 }}>
          {qtr({ label:'2026', rows:q26, tot:q26Tot, revLF:revPerLF, avgLen, live:true })}
          {PRIOR_QTR.map(y => qtr(y))}
          <div style={{ fontSize:10, color:C.muted, marginTop:2, fontStyle:'italic' }}>2026 is live from the invoice lines; 2025–2023 are from the workbook. Utility (service-type) breakdown lives on the SVC tab.</div>
        </div>

      </div>
      <div style={{ fontSize:10.5, color:C.muted, marginTop:10 }}>Variance = Invoiced − Budget. Budget &amp; 2025 are from the Job Revenue workbook (baked in); Invoiced &amp; the 2026 quarterly figures are live from the invoice lines.</div>
    </div>
  );
}

function WeekReport({ inv, view, setView }) {
  // #WEEK_RPT_V1 — frozen header + frozen-left RIG/TYPE/SUP + frozen-right SUP/YTD; weeks scroll; PM totals bottom.
  const [baseOff, setBaseOff] = useState(-1);
  const [hideRigTot, setHideRigTot] = useState(true);  // #RIG_COLLAPSE_V1 — default collapsed: R# Total subtotal rows hidden (tap "Show R totals" to expand)
  // #REV_TOPBAR_TIGHTEN_V1 — Bill: turning the phone sideways still "flips into the full desktop
  // grid view... just with a crowded top section." Root cause: this header row's view-switch labels
  // key off `isDesktop` (768px), so the moment landscape crosses that width the 10 buttons expand
  // to full text ("📥 INV Input") in a viewport that's still only ~700-900px wide — nowhere near
  // enough room for that plus the title/legend/paging controls. `wide` is a second, higher
  // breakpoint local to this header: short (icon-only) labels persist through landscape-phone and
  // narrow-tablet widths, only expanding to full text on genuinely wide desktop screens.
  const [wide, setWide] = useState(() => window.innerWidth >= 1100);
  useEffect(() => {
    const h = () => setWide(window.innerWidth >= 1100);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  // #WK_LANDSCAPE_GRID_V1 (2026-10-22) — Bill: "WK collapsed list is ok in portrait but we should be able to see the weeks in
  // landscape." A phone turned sideways still counts as a phone app-wide (useIsDesktop), so this view checks orientation itself:
  // wider than tall = show the week grid; portrait keeps the tap-to-expand cards.
  const [isLandscape, setIsLandscape] = useState(() => window.innerWidth > window.innerHeight);
  useEffect(() => {
    const h = () => setIsLandscape(window.innerWidth > window.innerHeight);
    const ho = () => { h(); setTimeout(h, 300); };
    window.addEventListener('resize', h); window.addEventListener('orientationchange', ho);
    return () => { window.removeEventListener('resize', h); window.removeEventListener('orientationchange', ho); };
  }, []);
  const isDesktop = useIsDesktop();  // #WK_BTN_RESPONSIVE_V1 — emojis on desktop; text-only on mobile so the view buttons fit one row
  // #REV_MOBILE_CARDS_V1 (2026-08-24) — Bill: this table is used often by senior leadership and on
  // phone it's the same wide sticky-column grid as desktop, just rendered tiny — pinch-zoom/scroll
  // messy. Desktop keeps the full grid untouched; mobile gets a stacked card list instead, grouped by
  // rig, one card per sup showing the under-review week's $ at a glance — tap to expand the full
  // week-by-week + YTD detail (same underlying data, same incentive-hit highlight, just laid out for
  // a phone instead of a horizontally-scrolling table).
  const [mobOpen, setMobOpen] = useState(() => new Set());
  const toggleMob = (key) => setMobOpen(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const NWEEKS = 4;
  const C_LINE = '#D1D5DB', DARK = '2px solid #111';
  const BLUE_HDR='#93C5FD', BLUE_SUB='#DBEAFE', RIG_TOT='#E2ECF8', GREY='#F3F4F6', RED='#DC2626', NAVY='#1E3A8A';
  // #REV_WKCOLW_V1 (2026-08-24) — Bill: RIG/TYPE/SUP (left) and SUP/YTD (right) were wider than the
  // content needs — tightened all five so the scrolling week columns get more room, on mobile and
  // desktop both. RIG is a 2-char code (R1..R5, AU); TYPE's longest values are 7-char rig models
  // ("100x140", "23x30DR"); SUP's longest is "RICHARD H"; YTD's longest so far is ~7 digits.
  const W_RIG=34, W_TYPE=64, W_SUP=94, L_TYPE=W_RIG, L_SUP=W_RIG+W_TYPE, LEFT=W_RIG+W_TYPE+W_SUP;
  // #REV_WKCOLW_V2 (2026-08-24) — Bill: still not visibly tighter after V1's frozen-column cut (that
  // pass only freed ~130px total, spread across the whole row — not enough to notice against a
  // horizontally-scrolling table, and nowhere near the ~200px a single week block needs). This pass
  // also shrinks the three per-week data columns themselves (×4 weeks on screen = the real width
  // hog): W_INV 104→88, W_LF 58→48, W_BORE 46→38 — still enough room for a 6-digit dollar amount
  // plus enough slack for the hit-highlight background, just no longer padded past what the content needs.
  const W_INV=88, W_LF=48, W_BORE=38;
  const W_RSUP=92, W_YTD=94, R_SUP=W_YTD;
  const HROW1 = 30; // height of the WK-label header row → top offset for the sub-label row
  const num   = v => v ? Math.round(v).toLocaleString() : '';
  const bnum  = v => v ? Math.round(v) : '';
  const accMoney = v => v ? (
    <span style={{ display:'flex', justifyContent:'space-between' }}><span>$</span><span style={{ marginLeft:6 }}>{Math.round(v).toLocaleString()}</span></span>
  ) : '';
  const normName = s => { const t = String(s||'').trim().toUpperCase().replace(/\./g,'').replace(/\s+/g,' '); return REV_SUP_ALIAS[t] || t; };   // #RU_R4_JUANP_V1
  const parseMDY = s => { const m=String(s||'').match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/); if(!m) return null; let y=m[3]; if(y.length===2) y='20'+y; return new Date(Number(y),Number(m[1])-1,Number(m[2]),12,0,0,0); };
  const startOfWeek = d => { const x=new Date(d); const day=x.getDay()||7; x.setDate(x.getDate()-(day-1)); x.setHours(0,0,0,0); return x; };
  const isoStr = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const fmtMDY = d => `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${String(d.getFullYear()).slice(-2)}`;
  const thisMon = startOfWeek(new Date());
  const YR = new Date().getFullYear();

  const weekDesc = (off) => { const mon=new Date(thisMon); mon.setDate(mon.getDate()-7*(off+1)); const sun=new Date(mon); sun.setDate(sun.getDate()+6); sun.setHours(23,59,59,999);
    return { off, mon, sun, wkNo:isoWeekNum(isoStr(mon)), q:Math.floor(mon.getMonth()/3)+1, range:`${fmtMDY(mon)}–${fmtMDY(sun)}`, current:off<0, review:off===0 }; };
  const cols = []; for (let i=NWEEKS-1;i>=0;i--) cols.push(weekDesc(baseOff+i));
  const reviewIdx = cols.findIndex(c=>c.review);

  // #REV_INCENTIVE_V3 (2026-08-24) — Bill: "By THUR following Week as we meet on Fridays to discuss."
  // A week's billing isn't fully entered the moment it's the most-recently-completed (red-boxed
  // "review") week — entry is still catching up through that week. Bill's rule: a week only becomes
  // safe to judge once today reaches the THURSDAY of the week that follows it (they review revenue
  // Fridays, so by Thursday the prior week's entries are settled). weekDesc(0) is always "last week";
  // its Sunday +4 days lands on the Thursday of the following (live/current) week. If off=0 isn't
  // judgeable yet (e.g. it's only Mon/Tue/Wed of the current week), roll back to off=1, etc.
  const judgeOff = (() => {
    const t0 = new Date(); t0.setHours(0,0,0,0);
    for (let off = 0; off <= 26; off++) {
      const wd = weekDesc(off);
      const cutoff = new Date(wd.sun); cutoff.setHours(0,0,0,0); cutoff.setDate(cutoff.getDate()+4);
      if (t0 >= cutoff) return off;
    }
    return 1; // shouldn't happen — 26 weeks back is always well past its own cutoff
  })();
  // judgeIdx (the single "currently judged" column) no longer needed — #REV_INCENTIVE_HIST_V1
  // flags every already-judged week in view via incentiveByKey, not just one column index.

  // #REV_INCENTIVE_V1 (2026-08-24) — Bill: weekly production revenue incentive floors per rig.
  // #REV_INCENTIVE_V2 (2026-08-24, same day) — Bill tested V1 (flat weekly check, all rigs) and it
  // flagged almost every row red — too noisy, and most of that noise was just billing still catching
  // up rather than a real miss.
  // #REV_INCENTIVE_V3 (2026-08-24, same day) — Bill's next correction, from his own KADE math (WK33
  // $465K + last post WK29 $21,500 → $93,060/wk avg over 5 weeks, clears the $85K floor — should NOT
  // be flagged) plus "Only look for the Revenue line to turn Yellow maybe if they hit the target for
  // the floor amount and avg the floor amount since last revenue entry": this flips the feature from
  // flagging MISSES (red, ambiguous — a low number can just mean incomplete entry) to flagging
  // confirmed HITS (yellow) — a sup's revenue averaged since their last posted week, on the judgeable
  // week (see judgeOff above), clears their rig's floor. A hit can't be a false positive from
  // incomplete data (missing entries only pull the average down, never up), so this reads clean even
  // while billing is still catching up. No highlight = "nothing confirmed yet," not "missed."
  const DEFAULT_INC_TARGETS = { R1:35000, R2:38000, R3:50000, R4:85000, R5:100000, AU:50000 };
  const MAN_BG = '#EDE9FE', MAN_INK = '#7C3AED';   // #REV_INCENTIVE_MANUAL_V1 — violet: a judgment-call incentive, apart from the yellow auto hit
  const [incTargets, setIncTargets] = useState(DEFAULT_INC_TARGETS);
  const [incEdit, setIncEdit] = useState(false);
  useEffect(() => { (async () => {
    try { const t = await sg('revenue-incentive-targets'); if (t && typeof t === 'object') setIncTargets(p => ({ ...p, ...t })); }
    catch {}
  })(); }, []);
  const saveIncTargets = async (next) => {
    try { await ss('revenue-incentive-targets', next); } catch (e) { alert('Could not save incentive targets: ' + (e.message||e)); }
  };

  // #REV_INCENTIVE_NOTE_V1 (2026-09-10) — Bill: keep past hit-weeks referenceable and let him drop
  // an Excel-comment-style note on a hit cell (e.g. "progress billing — holding for bore completion",
  // like WK34 Felix/Norberto — hit the floor but not paid out yet because it's progress-billed
  // revenue that could still move once the bore actually finishes). Notes are keyed `${supKey}__${weekIso}`
  // so they stick to that exact sup+week forever, independent of paging. Small yellow corner marker on
  // any hit cell that has a note; click a hit cell to add/edit.
  const [incNotes, setIncNotes] = useState({});
  useEffect(() => { (async () => {
    try { const n = await sg('revenue-incentive-notes'); if (n && typeof n === 'object') setIncNotes(n); }
    catch {}
  })(); }, []);
  // #REV_INCENTIVE_MANUAL_V1 (2026-10-12) — Bill: "a way to highlight and make a note on these rare moments."
  // e.g. Ramiro WK40 billed $436,955 but it took 5 weeks — ~10 days lost to holiday, weather and delays out of
  // his control — so a 4-week production incentive is granted for his crew even though the floor math didn't
  // flag it. A manual flag is a judgment call layered on top of the automatic yellow hits: click ANY supervisor
  // week cell → tick "Manual production incentive", how many weeks it runs, and why. Shown violet (yellow stays
  // the automatic hit) with the same corner-note marker. Stored `${supKey}__${weekIso}` → { weeks, at } in
  // 'revenue-incentive-manual'; the reason lives in the same note store as the automatic hits, so one note per
  // cell either way. Rare by design — nothing else on the page changes.
  const [incManual, setIncManual] = useState({});
  useEffect(() => { (async () => {
    try { const m = await sg('revenue-incentive-manual'); if (m && typeof m === 'object') setIncManual(m); }
    catch {}
  })(); }, []);
  const [noteEditor, setNoteEditor] = useState(null); // { noteKey, label, hit }
  // #REV_WK_DRILL_V1 — click a SUP week cell → the invoice lines behind it (→ 📒 Daily for the job).
  // The note / manual-incentive editor is still one click away from that popup (📝 button).
  const [drill, setDrill] = useState(null);
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const openDrill = (supKey, supLabel, col, noteKey, hitInfo) => setDrill({ supKey, supLabel, mon:col.mon, sun:col.sun, wkNo:col.wkNo, range:col.range, noteKey, hitInfo, note: incNotes[noteKey] || '' });
  const runYearXlsx = async () => {
    if (xlsxBusy) return; setXlsxBusy(true);
    try { await exportRevenueYearXlsx({ inv, roster: RIG_ROSTER, normName, pmCanon, year: new Date().getFullYear() }); }
    catch (e) { alert('Could not build the year file: ' + (e && e.message ? e.message : e)); }
    finally { setXlsxBusy(false); }
  };
  const [noteEditorText, setNoteEditorText] = useState('');
  const [noteEditorMan, setNoteEditorMan] = useState({ on:false, weeks:'' });
  const openNoteEditor = (noteKey, label, hit) => {
    setNoteEditor({ noteKey, label, hit: hit || null });
    setNoteEditorText(incNotes[noteKey] || '');
    const m = incManual[noteKey];
    setNoteEditorMan({ on: !!m, weeks: m && m.weeks ? String(m.weeks) : '' });
  };
  const saveIncNote = async () => {
    const k = noteEditor.noteKey;
    const text = noteEditorText.trim();
    if (noteEditorMan.on && !text) { alert('Add a short note on why this week earns a manual incentive.'); return; }
    const next = { ...incNotes };
    if (text) next[k] = text; else delete next[k];
    const prevMan = incManual[k] || null;
    const wks = Number(String(noteEditorMan.weeks||'').replace(/[^\d]/g,'')) || 0;
    const nextMan = { ...incManual };
    if (noteEditorMan.on) nextMan[k] = { weeks: wks || null, at: (prevMan && prevMan.at) || new Date().toISOString() };
    else delete nextMan[k];
    const manChanged = JSON.stringify(prevMan) !== JSON.stringify(nextMan[k] || null);
    setIncNotes(next);
    setIncManual(nextMan);
    setNoteEditor(null);
    try { await ss('revenue-incentive-notes', next); } catch (e) { alert('Could not save note: ' + (e.message||e)); }
    if (manChanged) { try { await ss('revenue-incentive-manual', nextMan); } catch (e) { alert('Could not save the manual incentive flag: ' + (e.message||e)); } }
  };
  const keyWeekTotal = (keys, off) => {
    const wd = weekDesc(off);
    let amt = 0;
    (inv||[]).forEach(e => {
      const d = parseMDY(e.pullDate); if (!(d && d>=wd.mon && d<=wd.sun)) return;
      if (keys.includes(normName(e.sup))) amt += _revNum(e.invAmnt);
    });
    return amt;
  };
  // Bill's example: WK33 Manuel posts $99,300, nothing WK32, last real post WK31 — average is over
  // the 2 weeks since that last post (WK32 $0 + WK33 $99,300) / 2 = $49,650. Averages the judgeable
  // week (baseOff) back to the sup's last posted week; null = no revenue at all in the 26-week
  // lookback (a brand-new/inactive sup) — nothing meaningful to flag either way.
  const incentiveAvg = (keys, baseOff) => {
    const reviewAmt = keyWeekTotal(keys, baseOff);
    let span = 1, sum = reviewAmt, found = reviewAmt > 0;
    for (let back = baseOff+1; back <= baseOff+26; back++) {
      const amt = keyWeekTotal(keys, back);
      if (amt > 0) { found = true; break; }   // last posted week found — stop; it's not folded into the average
      span++; sum += amt;                      // amt is 0 here — just extends the span
    }
    if (!found) return null;
    return { avg: sum/span, span, reviewAmt };
  };
  // #REV_INCENTIVE_HIST_V1 (2026-09-10) — Bill: "keep prior weeks highlighted so we could
  // reference" instead of the hit disappearing once judging rolls onto the next week. Was keyed
  // sup-only (a single, ever-refreshing "current judgeable week" snapshot); now keyed sup + the
  // week's own Monday ISO date (stable regardless of what day it is "today"), computed for every
  // already-judged week (off >= judgeOff) currently paged into view — so scrolling back still
  // shows exactly which weeks hit, same as always, they just don't vanish anymore.
  const incentiveByKey = useMemo(() => {
    const m = {}; // m[supKey][weekIsoMon] = { avg, span, reviewAmt, target, hit, rig }
    const offs = [...new Set(cols.map(c => c.off).filter(off => off >= judgeOff))];
    RIG_ROSTER.forEach(g => {
      const target = incTargets[g.rig]; if (!target) return;
      g.sups.forEach(s => {
        const key = normName(s.n);
        // #REV_INCENTIVE_V3 — hit-only: average since last posted week, on the judgeable week, vs.
        // the floor. Applies the same averaging math to every rig now, not just R4/R5 — a blank
        // week just extends the average's span rather than reading as an isolated miss.
        offs.forEach(off => {
          const info = incentiveAvg([key], off);
          if (info) {
            const wkKey = isoStr(weekDesc(off).mon);
            m[key] = m[key] || {};
            m[key][wkKey] = { ...info, target, hit: info.avg >= target, rig: g.rig };
          }
        });
      });
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inv, incTargets, judgeOff, baseOff]);

  const colData = cols.map(col => {
    const sup={}, pm={}; const g={amt:0,lf:0,bore:0};
    (inv||[]).forEach(e=>{ const d=parseMDY(e.pullDate); if(!(d && d>=col.mon && d<=col.sun)) return;
      const a=_revNum(e.invAmnt), l=_revNum(e.lf), b=_revNum(e.bore);
      const sk=normName(e.sup); if(sk){ (sup[sk]=sup[sk]||{amt:0,lf:0,bore:0}); sup[sk].amt+=a; sup[sk].lf+=l; sup[sk].bore+=b; }
      const pk=pmCanon(e.pm); if(pk){ (pm[pk]=pm[pk]||{amt:0,lf:0,bore:0}); pm[pk].amt+=a; pm[pk].lf+=l; pm[pk].bore+=b; }
      g.amt+=a; g.lf+=l; g.bore+=b;
    });
    return { col, sup, pm, grand:g };
  });

  const ytdSup={}, ytdPm={};
  (inv||[]).forEach(e=>{ const d=parseMDY(e.pullDate); if(!d || d.getFullYear()!==YR) return; const a=_revNum(e.invAmnt);
    const sk=normName(e.sup); if(sk) ytdSup[sk]=(ytdSup[sk]||0)+a;
    const pk=pmCanon(e.pm); if(pk) ytdPm[pk]=(ytdPm[pk]||0)+a; });

  const Z = { amt:0, lf:0, bore:0 };
  const rosterSet = new Set(); RIG_ROSTER.forEach(r=>r.sups.forEach(s=>rosterSet.add(normName(s.n))));
  const otherKeys = [...new Set(colData.flatMap(c=>Object.keys(c.sup)))].filter(k=>!rosterSet.has(k)).sort();
  const pmTotal = {}; colData.forEach(c=>Object.entries(c.pm).forEach(([k,d])=>{ pmTotal[k]=(pmTotal[k]||0)+d.amt; }));
  const pmNames = Object.keys(pmTotal).sort((a,b)=>a.localeCompare(b));

  const stick = (left, w, extra={}) => ({ position:'sticky', left, minWidth:w, width:w, maxWidth:w, zIndex:2, background:'#FFFFFF', padding:'3px 8px', fontSize:12, borderBottom:`1px solid ${C_LINE}`, whiteSpace:'nowrap', boxSizing:'border-box', ...extra });
  const rstick = (right, w, extra={}) => ({ position:'sticky', right, minWidth:w, width:w, maxWidth:w, zIndex:2, background:'#FFFFFF', padding:'3px 8px', fontSize:12, textAlign:'right', borderBottom:`1px solid ${C_LINE}`, whiteSpace:'nowrap', boxSizing:'border-box', ...extra });
  const cell = (w, extra={}) => ({ padding:'3px 8px', fontSize:12, textAlign:'right', color:'#111', borderBottom:`1px solid ${C_LINE}`, whiteSpace:'nowrap', width:w, minWidth:w, maxWidth:w, boxSizing:'border-box', ...extra });

  const dataCells = (perWeek, opt={}) => {
    const tb = opt.lineTB ? { borderTop:DARK, borderBottom:DARK } : (opt.top ? { borderTop:DARK } : {});
    return perWeek.flatMap((d,ci)=>{
      const rev = ci===reviewIdx;
      const col = cols[ci];
      // #REV_INCENTIVE_HIST_V1 — every already-judged week that hit stays flagged whenever it's in
      // view (opt.incForKey, keyed by this column's own week), not just the single live judgeIdx
      // column. opt.supKey (only set on a real sup row) doubles as the note key's namespace.
      const wkKey = col ? isoStr(col.mon) : null;
      const hitInfo = (opt.incForKey && wkKey) ? opt.incForKey[wkKey] : null;
      const flagged = !!(hitInfo && hitInfo.hit);
      const noteKey = (opt.supKey && wkKey) ? `${opt.supKey}__${wkKey}` : null;
      const note = noteKey ? incNotes[noteKey] : null;
      const man = noteKey ? incManual[noteKey] : null;   // #REV_INCENTIVE_MANUAL_V1
      const base = { ...(opt.bold?{fontWeight:800}:{}), ...(opt.bg?{background:opt.bg}:{}), ...(opt.color?{color:opt.color}:{}), ...(flagged?{background:'#FEF9C3'}:{}), ...(man?{background:MAN_BG}:{}), ...tb };
      const lB = rev ? `2px solid ${RED}` : '2px solid #E5E7EB';
      const rB = rev ? `2px solid ${RED}` : `1px solid ${C_LINE}`;
      const flagTitle = hitInfo
        ? `Hit the $${hitInfo.target.toLocaleString()}/wk ${hitInfo.rig==='AU'?'AUGER':hitInfo.rig} incentive floor — averaged $${Math.round(hitInfo.avg).toLocaleString()}/wk over ${hitInfo.span} week${hitInfo.span===1?'':'s'} since the last posted week`
        : undefined;
      const manTitle = man ? `MANUAL production incentive${man.weeks ? ` — ${man.weeks} week${man.weeks===1?'':'s'}` : ''}` : '';
      const titleText = (flagged || man)
        ? [manTitle, flagged ? flagTitle : ''].filter(Boolean).join('\n') + (note ? `\n\nNote: ${note}` : '') + '\n\nClick for the invoice lines, Daily, and note'
        : (noteKey ? 'Click for the invoice lines behind this week, the Daily, and notes / manual incentive' : undefined);
      return [
        <td key={ci+'a'} style={cell(W_INV,{ borderLeft:lB, position:'relative', cursor: noteKey ? 'pointer' : undefined, ...base })}
          title={titleText}
          onClick={(noteKey && col) ? () => openDrill(opt.supKey, opt.supLabel||'', col, noteKey, hitInfo) : undefined}>
          {accMoney(d.amt)}
          {(flagged || man) && note ? (
            <span style={{ position:'absolute', top:0, right:0, width:0, height:0, borderStyle:'solid', borderWidth:'0 8px 8px 0', borderColor:`transparent ${man ? MAN_INK : '#CA8A04'} transparent transparent` }} />
          ) : null}
        </td>,
        <td key={ci+'l'} style={cell(W_LF,{ ...base })}>{num(d.lf)}</td>,
        <td key={ci+'b'} style={cell(W_BORE,{ color:opt.color||'#64748B', borderRight:rB, ...base })}>{bnum(d.bore)}</td>,
      ];
    });
  };
  const rightCells = (name, ytd, opt={}) => {
    const bg = opt.bg || '#FFFFFF';
    const tb = opt.lineTB ? { borderTop:DARK, borderBottom:DARK } : (opt.top ? { borderTop:DARK } : {});
    const base = { ...(opt.bold?{fontWeight:800}:{}), ...tb };
    return [
      <td key="rs" style={rstick(R_SUP, W_RSUP, { background:bg, textAlign:'left', borderLeft:'2px solid #94A3B8', ...base, ...(opt.nameStyle||{}) })}>{name}</td>,
      <td key="ry" style={rstick(0, W_YTD, { background:bg, ...base, ...(opt.color?{color:opt.color}:{}) })}>{accMoney(ytd)}</td>,
    ];
  };

  const sumWeek = (keys, pick) => colData.map(c => keys.reduce((a,k)=>{ const d=pick(c,k)||Z; return { amt:a.amt+d.amt, lf:a.lf+d.lf, bore:a.bore+d.bore }; }, {amt:0,lf:0,bore:0}));
  const sumYtd = (keys, src) => keys.reduce((a,k)=>a+(src[k]||0),0);

  let rix = 0;
  const supTr = (rig, type, name, key, divTop) => {
    const bg = (rix++ % 2 === 1) ? GREY : '#FFFFFF';
    const dt = divTop ? { borderTop:DARK } : {};  // #RIG_DIV_V1 — group divider when R totals are hidden
    // #REV_INCENTIVE_V1 — 'rig' is the roster code (R1..R5, AU) so it doubles as the target lookup key;
    // the "OTHER" rows pass rig:'—' which has no target, so they're never flagged.
    const incForKey = incentiveByKey[key];
    return (
      <tr key={'s'+rig+name}>
        <td style={stick(0, W_RIG, { background:bg, textAlign:'center', fontWeight:700, color:NAVY, ...dt })}>{rig}</td>
        <td style={stick(L_TYPE, W_TYPE, { background:bg, textAlign:'center', color:'#475569', ...dt })}>{type}</td>
        <td style={stick(L_SUP, W_SUP, { background:bg, borderRight:`1px solid ${C_LINE}`, ...dt })}>{name}</td>
        {dataCells(colData.map(c=>c.sup[key]||Z), { bg, top:divTop, incForKey, supKey:key, supLabel:name })}
        {rightCells(name, ytdSup[key], { bg, top:divTop })}
      </tr>
    );
  };
  const rigBlock = (g) => {
    const sups = [...g.sups].sort((a,b)=>a.n.localeCompare(b.n));
    const keys = sups.map(s=>normName(s.n));
    const perWeek = sumWeek(keys, (c,k)=>c.sup[k]);
    return [
      ...sups.map((s,i) => supTr(g.rig, s.t, s.n, normName(s.n), hideRigTot && i===0)),
      hideRigTot ? null : (  // #RIG_COLLAPSE_V1 — hide just the R# Total subtotal rows
      <tr key={'t'+g.rig}>
        <td style={stick(0, W_RIG, { background:RIG_TOT, borderTop:DARK, borderBottom:DARK })} />
        <td style={stick(L_TYPE, W_TYPE, { background:RIG_TOT, borderTop:DARK, borderBottom:DARK })} />
        <td style={stick(L_SUP, W_SUP, { background:RIG_TOT, fontWeight:800, color:NAVY, borderTop:DARK, borderBottom:DARK, borderRight:`1px solid ${C_LINE}` })}>{g.rig} Total</td>
        {dataCells(perWeek, { bold:true, bg:RIG_TOT, lineTB:true, color:NAVY })}
        {rightCells(g.rig+' Total', sumYtd(keys, ytdSup), { bold:true, bg:RIG_TOT, lineTB:true, color:NAVY, nameStyle:{ color:NAVY } })}
      </tr>
      ),
    ];
  };

  // #REV_MOBILE_CARDS_V1 — headIdx: the column to headline on a card (the review week when it's in
  // view; otherwise fall back to the newest visible week so a card never headlines blank).
  const headIdx = reviewIdx>=0 ? reviewIdx : cols.length-1;
  const mMoney = v => v ? `$${Math.round(v).toLocaleString()}` : '—';
  const mobileSupCard = (rig, type, name, key) => {
    const perWeek = colData.map(c => c.sup[key] || Z);
    // #REV_INCENTIVE_HIST_V1 — incForKey now holds every already-judged week for this sup, not
    // just one; the card itself lights up if ANY visible week hit (so a past hit stays visible on
    // mobile scroll-back too), and each week's own row inside flags individually.
    const incForKey = incentiveByKey[key] || {};
    const anyHit = cols.some(c => { const info = incForKey[isoStr(c.mon)]; return (info && info.hit) || incManual[`${key}__${isoStr(c.mon)}`]; });
    const open = mobOpen.has(key);
    const headCell = perWeek[headIdx] || Z;
    return (
      <div key={'m'+rig+name} onClick={()=>toggleMob(key)}
        style={{ background: anyHit ? '#FEFCE8' : '#FFFFFF', border:`1px solid ${anyHit?'#FDE68A':C_LINE}`, borderRadius:8, padding:'8px 10px', marginBottom:6, cursor:'pointer' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}>
          <div style={{ minWidth:0 }}>
            <div style={{ fontWeight:800, color:NAVY, fontSize:13 }}>{name}</div>
            <div style={{ fontSize:10.5, color:'#64748B' }}>{rig==='AU'?'AUGER':rig}{type&&type!=='—' ? ` · ${type}` : ''}</div>
          </div>
          <div style={{ textAlign:'right', flexShrink:0 }}>
            <div style={{ fontWeight:800, fontSize:14, color:NAVY }}>{mMoney(headCell.amt)}</div>
            <div style={{ fontSize:9.5, color: headIdx===reviewIdx ? RED : '#64748B', fontWeight:700 }}>
              WK {String(cols[headIdx].wkNo).padStart(2,'0')}{headIdx===reviewIdx ? ' · review' : ''}
            </div>
          </div>
          <div style={{ fontSize:11, color:'#94A3B8', flexShrink:0 }}>{open ? '▲' : '▼'}</div>
        </div>
        {open && (
          <div style={{ marginTop:8, borderTop:`1px solid ${C_LINE}`, paddingTop:6 }}>
            {cols.map((col,ci) => {
              const d = perWeek[ci] || Z;
              const wkKey = isoStr(col.mon);
              const info = incForKey[wkKey];
              const rowHit = !!(info && info.hit);
              const noteKey = `${key}__${wkKey}`;
              const note = incNotes[noteKey];
              const rowMan = !!incManual[noteKey];   // #REV_INCENTIVE_MANUAL_V1
              return (
                <div key={ci}
                  onClick={(e)=>{ e.stopPropagation(); openDrill(key, `${name} · ${rig==='AU'?'AUGER':rig}`, col, noteKey, info); }}
                  style={{ display:'flex', justifyContent:'space-between', fontSize:11, padding:'3px 4px', borderRadius:4, background: rowMan ? MAN_BG : rowHit ? '#FEF9C3' : 'transparent', cursor:'pointer' }}>
                  <span style={{ color: ci===reviewIdx?RED:'#475569', fontWeight: ci===reviewIdx?700:400 }}>WK {String(col.wkNo).padStart(2,'0')}{col.current?' ●':''}{note?' 📝':''}</span>
                  <span style={{ color:'#111' }}>{mMoney(d.amt)} <span style={{ color:'#94A3B8' }}>· {d.lf?Math.round(d.lf).toLocaleString():'—'} LF · {d.bore?Math.round(d.bore):'—'} bore</span></span>
                </div>
              );
            })}
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, padding:'5px 4px 0', fontWeight:800, color:NAVY, borderTop:`1px solid ${C_LINE}`, marginTop:4 }}>
              <span>YTD {YR}</span><span>{mMoney(ytdSup[key])}</span>
            </div>
            {anyHit && (
              <div style={{ fontSize:10, color:'#854D0E', marginTop:5 }}>
                Tap a week above for the hit detail, to add/view a note, or to flag a manual incentive.
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  const mobileRigBlock = (g) => {
    const sups = [...g.sups].sort((a,b)=>a.n.localeCompare(b.n));
    const keys = sups.map(s=>normName(s.n));
    const perWeek = sumWeek(keys, (c,k)=>c.sup[k]);
    return (
      <div key={'mg'+g.rig} style={{ marginBottom:14 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', padding:'2px 2px 6px', borderBottom:`2px solid ${NAVY}`, marginBottom:6 }}>
          <span style={{ fontWeight:800, color:NAVY, fontSize:13 }}>{g.rig==='AU'?'AUGER':g.rig}</span>
          {!hideRigTot && <span style={{ fontSize:12, fontWeight:700, color:NAVY }}>{mMoney((perWeek[headIdx]||Z).amt)}</span>}
        </div>
        {sups.map(s => mobileSupCard(g.rig, s.t, s.n, normName(s.n)))}
      </div>
    );
  };

  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:12 }}>
      {/* #REV_TOPBAR_TIGHTEN_V1 — grouped onto its own rows instead of one long flex-wrap line, so
          landscape/narrow widths wrap predictably instead of crowding everything together. */}
      <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:6 }}>
        <div style={{ fontWeight:800, fontSize:15, color:C.text }}>📅 Weekly by Supervisor</div>
        <span style={{ fontSize:11, color:C.muted }}><span style={{ color:'#16A34A' }}>●</span> current · <span style={{ color:RED }}>◆ under review (boxed)</span> · YTD pinned right</span>
        <div style={{ flex:1 }} />
        {isDesktop && <span onClick={runYearXlsx} title="Download the whole year by week (REV · LF · BORE), every bore's start → pull, and every line — Excel, live formulas"
          style={{ fontSize:11, color: xlsxBusy ? C.muted : '#475569', cursor: xlsxBusy ? 'wait' : 'pointer', textDecoration:'underline', whiteSpace:'nowrap' }}>{xlsxBusy ? 'Building…' : '⬇ Year XLSX'}</span>}  {/* #REV_WK_DRILL_V1 */}
        <Btn onClick={()=>setHideRigTot(v=>!v)} variant="secondary" small>{hideRigTot?(wide?'▾ Show R totals':'▾ R tot'):(wide?'▸ Hide R totals':'▸ R tot')}</Btn>
        <Btn onClick={()=>setBaseOff(o=>o+1)} variant="secondary" small>◀ {wide?'Older':''}</Btn>
        <Btn onClick={()=>setBaseOff(o=>Math.max(-1,o-1))} variant="secondary" small>{wide?'Newer ':''}▶</Btn>
      </div>
      {setView && (
        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginBottom:10 }}>
          {[['input','📥 INV Input'],['pm','👤 PM'],['svc','🔧 SVC'],['sup','👷 SUP'],['jobs','📋 JOBS'],['budget','📊 BUDGET'],['week','📅 WK'],['sum','📈 SUM'],['yoy','🔁 YOY'],['aging','⏳ AGING']].map(([v,l])=>(
            <Btn key={v} onClick={()=>setView(v)} variant={view===v?'primary':'secondary'} small>{wide ? l : l.replace(/^\S+\s+/,'')}</Btn>
          ))}
        </div>
      )}

      {(isDesktop || isLandscape) ? (
      <div style={{ overflow:'auto', maxHeight: isDesktop ? 'calc(100vh - 160px)' : 'calc(100vh - 70px)', background:'#FFFFFF', borderRadius:8, border:`1px solid ${C_LINE}` }}>
        <table style={{ borderCollapse:'separate', borderSpacing:0, width:'100%' }}>
          <thead>
            <tr>
              <th rowSpan={2} style={stick(0, W_RIG, { top:0, zIndex:6, background:BLUE_HDR, color:'#000', fontWeight:800, textAlign:'center', verticalAlign:'bottom', borderBottom:`1px solid ${C_LINE}` })}>RIG</th>
              <th rowSpan={2} style={stick(L_TYPE, W_TYPE, { top:0, zIndex:6, background:BLUE_HDR, color:'#000', fontWeight:800, textAlign:'center', verticalAlign:'bottom', borderBottom:`1px solid ${C_LINE}` })}>TYPE</th>
              <th rowSpan={2} style={stick(L_SUP, W_SUP, { top:0, zIndex:6, background:BLUE_HDR, color:'#000', fontWeight:800, textAlign:'left', verticalAlign:'bottom', borderRight:`1px solid ${C_LINE}`, borderBottom:`1px solid ${C_LINE}` })}>SUP</th>
              {cols.map((col,ci)=>{ const rev=ci===reviewIdx; return (
                <th key={ci} colSpan={3} style={{ position:'sticky', top:0, zIndex:4, height:HROW1, padding:'5px 8px', fontSize:12.5, fontWeight:800, textAlign:'center', background:(Q_COLORS[col.q]||{}).hdr||BLUE_HDR, color:'#000', borderBottom:`1px solid ${C_LINE}`, borderLeft:rev?`2px solid ${RED}`:'2px solid #fff', borderRight:rev?`2px solid ${RED}`:undefined, borderTop:rev?`2px solid ${RED}`:undefined, whiteSpace:'nowrap' }}>
                  WK {String(col.wkNo).padStart(2,'0')}{col.current && <span style={{ color:'#15803D' }}> ●</span>}<span style={{ fontWeight:600, fontSize:11, color:NAVY, marginLeft:7 }}>{col.range}</span><span style={{ fontWeight:800, marginLeft:7 }}>Q{col.q}</span>
                </th>
              ); })}
              <th colSpan={2} style={{ ...rstick(0, W_RSUP+W_YTD, { top:0, zIndex:6, background:BLUE_HDR, color:'#000', fontWeight:800, textAlign:'center', borderLeft:'2px solid #94A3B8', borderBottom:`1px solid ${C_LINE}` }) }}>YTD {YR}</th>
            </tr>
            <tr>
              {cols.flatMap((col,ci)=>{ const rev=ci===reviewIdx; return [
                <th key={ci+'a'} style={{ position:'sticky', top:HROW1, zIndex:4, padding:'3px 8px', fontSize:10, fontWeight:700, textAlign:'right', background:(Q_COLORS[col.q]||{}).sub||BLUE_SUB, color:NAVY, borderBottom:`1px solid ${C_LINE}`, borderLeft:rev?`2px solid ${RED}`:'2px solid #fff' }}>INV TOTAL</th>,
                <th key={ci+'l'} style={{ position:'sticky', top:HROW1, zIndex:4, padding:'3px 8px', fontSize:10, fontWeight:700, textAlign:'right', background:(Q_COLORS[col.q]||{}).sub||BLUE_SUB, color:NAVY, borderBottom:`1px solid ${C_LINE}` }}>LF</th>,
                <th key={ci+'b'} style={{ position:'sticky', top:HROW1, zIndex:4, padding:'3px 8px', fontSize:10, fontWeight:700, textAlign:'right', background:(Q_COLORS[col.q]||{}).sub||BLUE_SUB, color:NAVY, borderBottom:`1px solid ${C_LINE}`, borderRight:rev?`2px solid ${RED}`:undefined }}>BORE</th>,
              ]; })}
              <th style={rstick(R_SUP, W_RSUP, { top:HROW1, zIndex:6, background:BLUE_SUB, color:NAVY, fontWeight:700, fontSize:10, textAlign:'left', borderLeft:'2px solid #94A3B8' })}>SUP</th>
              <th style={rstick(0, W_YTD, { top:HROW1, zIndex:6, background:BLUE_SUB, color:NAVY, fontWeight:700, fontSize:10 })}>YTD REV</th>
            </tr>
          </thead>
          <tbody>
            {RIG_ROSTER.flatMap(rigBlock)}
            {/* #REV_DEDUP_TOTALS_V1 — Other Total subtotal removed (it duplicated the single OTHER row) */}
            {otherKeys.length>0 && otherKeys.map((k,i) => supTr('—','—',k,k, hideRigTot && i===0))}
            <tr>
              <td style={stick(0, W_RIG, { background:BLUE_HDR, borderTop:DARK, borderBottom:DARK })} />
              <td style={stick(L_TYPE, W_TYPE, { background:BLUE_HDR, borderTop:DARK, borderBottom:DARK })} />
              <td style={stick(L_SUP, W_SUP, { background:BLUE_HDR, color:'#000', fontWeight:800, borderTop:DARK, borderBottom:DARK, borderRight:`1px solid ${C_LINE}` })}>WEEK TOTAL</td>
              {dataCells(colData.map(c=>c.grand), { bold:true, bg:BLUE_HDR, lineTB:true, color:'#000' })}
              {rightCells('TOTAL', sumYtd(Object.keys(ytdSup), ytdSup), { bold:true, bg:BLUE_HDR, lineTB:true, color:'#000', nameStyle:{ color:'#000' } })}
            </tr>

            {/* #REV_DEDUP_TOTALS_V1 — empty 'PM TOTALS' divider row removed to save a line */}
            {pmNames.length===0 && (
              <tr><td colSpan={3} style={stick(0, LEFT, { color:C.muted, borderRight:`1px solid ${C_LINE}` })}>No pulls invoiced in this window.</td>{dataCells(colData.map(()=>Z))}{rightCells('',0)}</tr>
            )}
            {pmNames.map(pk => { const bg=(rix++%2===1)?GREY:'#FFFFFF'; return (
              <tr key={'pm'+pk}>
                <td colSpan={3} style={stick(0, LEFT, { background:bg, fontWeight:700, borderRight:`1px solid ${C_LINE}` })}>{pk}</td>
                {dataCells(colData.map(c=>c.pm[pk]||Z), { bg })}
                {rightCells(pk, ytdPm[pk], { bg })}
              </tr>
            ); })}
            {pmNames.length>0 && (
              <tr>
                <td colSpan={3} style={stick(0, LEFT, { background:BLUE_HDR, fontWeight:800, color:'#000', borderTop:DARK, borderBottom:DARK, borderRight:`1px solid ${C_LINE}` })}>PM TOTAL</td>
                {dataCells(sumWeek(pmNames, (c,k)=>c.pm[k]), { bold:true, bg:BLUE_HDR, lineTB:true, color:'#000' })}
                {rightCells('TOTAL', sumYtd(pmNames, ytdPm), { bold:true, bg:BLUE_HDR, lineTB:true, color:'#000', nameStyle:{ color:'#000' } })}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      ) : (
      <div>
        {RIG_ROSTER.map(mobileRigBlock)}
        {otherKeys.length>0 && (
          <div style={{ marginBottom:14 }}>
            <div style={{ fontWeight:800, color:NAVY, fontSize:13, padding:'2px 2px 6px', borderBottom:`2px solid ${NAVY}`, marginBottom:6 }}>OTHER</div>
            {otherKeys.map(k => mobileSupCard('—','—',k,k))}
          </div>
        )}
        <div style={{ background:BLUE_HDR, borderRadius:8, padding:'10px 12px', display:'flex', justifyContent:'space-between', alignItems:'center', fontWeight:800, color:'#000' }}>
          <span>WEEK TOTAL · WK {String(cols[headIdx].wkNo).padStart(2,'0')}</span>
          <span>{mMoney((colData[headIdx]||{ grand:Z }).grand.amt)}</span>
        </div>
      </div>
      )}
      <div style={{ fontSize:10, color:C.muted, marginTop:8, fontStyle:'italic' }}>{(isDesktop || isLandscape)
        ? 'Header and the RIG/TYPE/SUP + YTD columns stay frozen — scroll down to PM and across the weeks freely. Buckets invoice lines by the pull date’s Mon–Sun week; YTD ' + YR + ' sums the full year per supervisor.'
        : 'Tap a supervisor to expand the full week-by-week breakdown + YTD. PM totals are on the PM tab above.'}</div>

      {/* #REV_INCENTIVE_V3 (2026-08-24) — editable key so Bill can move the floors without a code
          change. A yellow-highlighted cell means that sup's revenue, averaged since their last
          posted week, has cleared this rig's floor — hits only, no more red "miss" flag (a low
          number just means billing hasn't caught up yet, not a confirmed miss). Judged on the most
          recent week that's actually safe to judge — see judgeOff — which may sit one week behind
          the red-boxed review column until Thursday of the following week. Bill: "just no STARS
          needed.. high light is enough" — dropped the ★ badge, kept the cell background + hover
          tooltip. */}
      <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap', marginTop:10, padding:'8px 12px', background:'#FEFCE8', border:'1px solid #FDE68A', borderRadius:8 }}>
        <span style={{ fontSize:11, fontWeight:800, color:'#CA8A04', whiteSpace:'nowrap' }}>Weekly incentive floor — hit</span>
        <span style={{ fontSize:10, color:'#854D0E' }}>Avg'd since last posted week, checked once that week is safe to judge (by Thursday of the following week) · no highlight just means nothing confirmed yet, not a miss · past hits stay highlighted — click one to add a note</span>
        {/* #REV_INCENTIVE_MANUAL_V1 — key for the violet manual flag */}
        <span title="Click any supervisor week to flag a manual production incentive (rare — e.g. a big week that took longer for reasons outside the crew's control)"
          style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:10.5, fontWeight:800, color:MAN_INK, whiteSpace:'nowrap' }}>
          <span style={{ width:12, height:12, borderRadius:3, background:MAN_BG, border:`1px solid ${MAN_INK}` }} />
          Manual incentive{Object.keys(incManual).length ? ` · ${Object.keys(incManual).length}` : ''}
        </span>
        <div style={{ flex:1 }} />
        {Object.keys(DEFAULT_INC_TARGETS).map(rig => (
          <span key={rig} style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11.5 }}>
            <b style={{ color:NAVY }}>{rig==='AU'?'AUGER':rig}</b>
            {incEdit ? (
              <input type="text" inputMode="numeric" value={incTargets[rig]||0}
                onChange={e=>{ const v=Number(e.target.value.replace(/[^\d]/g,''))||0; setIncTargets(p=>({ ...p, [rig]:v })); }}
                onBlur={()=>saveIncTargets(incTargets)}
                style={{ width:70, fontSize:11, fontWeight:700, textAlign:'right', border:`1px solid ${C_LINE}`, borderRadius:5, padding:'2px 5px' }} />
            ) : (
              <span style={{ fontWeight:700 }}>${(incTargets[rig]||0).toLocaleString()}</span>
            )}
          </span>
        ))}
        <Btn onClick={()=>setIncEdit(v=>!v)} variant="secondary" small>{incEdit ? 'Done' : 'Edit targets'}</Btn>
      </div>

      {/* #REV_WK_DRILL_V1 */}
      {drill && (
        <WeekLinesModal drill={drill} inv={inv} normName={normName} onClose={()=>setDrill(null)}
          onNote={()=>{ const d = drill; setDrill(null); openNoteEditor(d.noteKey, `${d.supLabel} · WK ${String(d.wkNo).padStart(2,'0')} (${d.range})`, d.hitInfo); }} />
      )}
      {/* #REV_INCENTIVE_NOTE_V1 — click a hit cell to add/edit its note. Same modal styling as the
          Weekly Production Input note popover, so it reads as the same feature. */}
      {noteEditor && (
        <div onClick={()=>setNoteEditor(null)} style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div onClick={ev=>ev.stopPropagation()} style={{ background:C.card, borderRadius:12, padding:18, width:'100%', maxWidth:440, boxShadow:'0 20px 60px rgba(0,0,0,0.35)' }}>
            <div style={{ fontWeight:800, fontSize:14, color:C.text, marginBottom:2 }}>📝 Incentive note</div>
            <div style={{ fontSize:12, color:C.muted, marginBottom:10 }}>{noteEditor.label}</div>
            {noteEditor.hit && noteEditor.hit.hit && (
              <div style={{ fontSize:11.5, color:'#854D0E', background:'#FEF9C3', border:'1px solid #FDE68A', borderRadius:6, padding:'6px 9px', marginBottom:10 }}>
                Automatic hit — averaged ${Math.round(noteEditor.hit.avg).toLocaleString()}/wk over {noteEditor.hit.span} week{noteEditor.hit.span===1?'':'s'} vs. the ${noteEditor.hit.target.toLocaleString()} floor.
              </div>
            )}
            {/* #REV_INCENTIVE_MANUAL_V1 */}
            <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', background: noteEditorMan.on ? MAN_BG : '#F8FAFC', border:`1px solid ${noteEditorMan.on ? MAN_INK : C.border}`, borderRadius:7, padding:'7px 10px', marginBottom:10 }}>
              <label style={{ display:'flex', alignItems:'center', gap:7, fontSize:12.5, fontWeight:800, color: noteEditorMan.on ? MAN_INK : C.text, cursor:'pointer' }}>
                <input type="checkbox" checked={noteEditorMan.on} onChange={ev=>setNoteEditorMan(m=>({ ...m, on:ev.target.checked }))} />
                Manual production incentive
              </label>
              {noteEditorMan.on && (
                <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color:C.text }}>
                  for
                  <input type="text" inputMode="numeric" value={noteEditorMan.weeks} onChange={ev=>setNoteEditorMan(m=>({ ...m, weeks:ev.target.value.replace(/[^\d]/g,'') }))}
                    placeholder="4" style={{ width:42, fontSize:12, fontWeight:700, textAlign:'center', border:`1px solid ${C.border}`, borderRadius:5, padding:'3px 4px' }} />
                  weeks
                </label>
              )}
            </div>
            <textarea value={noteEditorText} onChange={ev=>setNoteEditorText(ev.target.value)} autoFocus
              placeholder={noteEditorMan.on ? 'Why — e.g. took 5 weeks, ~10 days lost to holiday, weather and delays out of his control…' : 'Why this hit is on hold, when it was actually paid, who to check with…'}
              style={{ width:'100%', minHeight:100, fontSize:13, padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius:7, boxSizing:'border-box', resize:'vertical', fontFamily:'inherit' }} />
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:12, gap:8 }}>
              <Btn onClick={()=>{ setNoteEditorText(''); setNoteEditorMan({ on:false, weeks:'' }); }} variant="secondary" small>Clear</Btn>
              <div style={{ display:'flex', gap:8 }}>
                <Btn onClick={()=>setNoteEditor(null)} variant="secondary" small>Cancel</Btn>
                <Btn onClick={saveIncNote} small>Save</Btn>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SumReport({ inv, view, setView }) {
  // #SUM_RPT_V1 — two standalone printable tables: QUARTER (quarter-colored, flips on ◀▶) + YTD (grey). Each self-contained, each auto-sorts by its own revenue within rig. Other is collapsible.
  const YR=new Date().getFullYear();
  const cq=Math.floor(new Date().getMonth()/3)+1;
  const [qSel,setQSel]=useState(cq);
  const [otherOpen,setOtherOpen]=useState(false);
  const C_LINE='#D1D5DB', DARK='2px solid #111', GREYC='#EBEDF0';
  const GREY_HDR='#CBD5E1', GREY_SUB='#DDE1E6', RIG_TOT='#E2ECF8', GREY='#F3F4F6', NAVY='#1E3A8A';
  const W_SIZE=40,W_RIG=66,W_SUP=110;
  const num=v=>v?Math.round(v).toLocaleString():'';
  const accMoney=v=>v?(<span style={{display:'flex',justifyContent:'space-between'}}><span>$</span><span style={{marginLeft:6}}>{Math.round(v).toLocaleString()}</span></span>):'';
  const normName=s=>{const t=String(s||'').trim().toUpperCase().replace(/\./g,'').replace(/\s+/g,' ');return REV_SUP_ALIAS[t]||t;};   // #RU_R4_JUANP_V1
  const parseMDY=s=>{const m=String(s||'').match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);if(!m)return null;let y=m[3];if(y.length===2)y='20'+y;return new Date(Number(y),Number(m[1])-1,Number(m[2]),12,0,0,0);};
  const startOfWeek=d=>{const x=new Date(d);const day=x.getDay()||7;x.setDate(x.getDate()-(day-1));x.setHours(0,0,0,0);return x;};
  const isoStr=d=>`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const avg=(n,d)=>d>0?n/d:0;

  const thisMon=startOfWeek(new Date());
  const lastMon=new Date(thisMon);lastMon.setDate(lastMon.getDate()-7);
  const weeksYTD=isoWeekNum(isoStr(lastMon));
  const selFwq=(qSel-1)*13+1;
  let selWeeks,weeksLeft,qState;
  if(qSel<cq){selWeeks=13;weeksLeft=0;qState='closed';}
  else if(qSel===cq){selWeeks=Math.max(1,Math.min(13,weeksYTD-selFwq+1));weeksLeft=Math.max(0,13-selWeeks);qState='open';}
  else{selWeeks=0;weeksLeft=13;qState='upcoming';}
  const qLabel=['Q1','Q2','Q3','Q4'][qSel-1];
  const qc=Q_COLORS[qSel]||Q_COLORS[2];

  const blank=()=>({qRev:0,qLf:0,qBore:0,yRev:0,yLf:0,yBore:0});
  const sup={},pmAgg={};
  // #SUM_INVDATE_V1 — revenue tracks INVOICE date (pull date only for Weekly Production). Falls back to pull date if inv date is blank.
  (inv||[]).forEach(e=>{const d=parseMDY(e.invDate)||parseMDY(e.pullDate);if(!d||d.getFullYear()!==YR)return;
    const a=_revNum(e.invAmnt),l=_revNum(e.lf),b=_revNum(e.bore);const inQ=(Math.floor(d.getMonth()/3)+1)===qSel;
    const sk=normName(e.sup);if(sk){const o=sup[sk]=sup[sk]||blank();o.yRev+=a;o.yLf+=l;o.yBore+=b;if(inQ){o.qRev+=a;o.qLf+=l;o.qBore+=b;}}
    const pk=pmCanon(e.pm);if(pk){const p=pmAgg[pk]=pmAgg[pk]||blank();p.yRev+=a;p.yLf+=l;p.yBore+=b;if(inQ){p.qRev+=a;p.qLf+=l;p.qBore+=b;}}
  });
  const rosterSet=new Set();RIG_ROSTER.forEach(r=>r.sups.forEach(s=>rosterSet.add(normName(s.n))));
  const otherKeys=Object.keys(sup).filter(k=>!rosterSet.has(k));
  const sumGroup=keys=>keys.reduce((a,k)=>{const o=sup[k]||blank();return{qRev:a.qRev+o.qRev,qLf:a.qLf+o.qLf,qBore:a.qBore+o.qBore,yRev:a.yRev+o.yRev,yLf:a.yLf+o.yLf,yBore:a.yBore+o.yBore};},blank());
  const company=sumGroup(Object.keys(sup));
  const pmNames=Object.keys(pmAgg).sort((a,b)=>a.localeCompare(b));

  const QV=[{lbl:'REVENUE',w:96,fn:o=>o.qRev,money:1},{lbl:'LF',w:54,fn:o=>o.qLf},{lbl:'BORE',w:42,fn:o=>o.qBore,muted:1}];
  const YV=[{lbl:'REVENUE',w:96,fn:o=>o.yRev,money:1},{lbl:'LF',w:54,fn:o=>o.yLf},{lbl:'BORE',w:42,fn:o=>o.yBore,muted:1},{lbl:'AVG WK LF',w:58,fn:o=>avg(o.yLf,weeksYTD),grey:1},{lbl:'AVG LEN',w:58,fn:o=>avg(o.yLf,o.yBore),grey:1},{lbl:'REV / WK',w:78,fn:o=>avg(o.yRev,weeksYTD),money:1,grey:1}];

  const hCell=(w,bg,al)=>({padding:'4px 6px',fontSize:9.5,fontWeight:700,textAlign:al,background:bg,color:NAVY,borderBottom:`1px solid ${C_LINE}`,width:w,minWidth:w,maxWidth:w,boxSizing:'border-box',whiteSpace:'nowrap'});
  const idC=(w,bg,al,extra={})=>({padding:'3px 6px',fontSize:12,textAlign:al,background:bg,borderBottom:`1px solid ${C_LINE}`,width:w,minWidth:w,maxWidth:w,boxSizing:'border-box',whiteSpace:'nowrap',...extra});
  const vC=(w,bg,extra={})=>({padding:'3px 6px',fontSize:12,textAlign:'right',color:'#111',background:bg,borderBottom:`1px solid ${C_LINE}`,width:w,minWidth:w,maxWidth:w,boxSizing:'border-box',whiteSpace:'nowrap',...extra});

  const mkTable=(title,hdr,sub,V,sortVal)=>{
    let rix=0;
    const valCells=(o,rowbg,opt={})=>V.map(c=>{
      const bg=c.grey?GREYC:rowbg;
      const v=c.fn(o);
      return <td key={c.lbl} style={vC(c.w,bg,{...(opt.bold?{fontWeight:800}:{}),...(opt.tb?{borderTop:DARK,borderBottom:DARK}:{}),...(c.muted&&!opt.color?{color:'#64748B'}:{}),...(opt.color?{color:opt.color}:{})})}>{c.money?accMoney(v):(c.muted?(v?Math.round(v):''):num(v))}</td>;
    });
    const supTr=(size,type,name,o)=>{const bg=(rix++%2===1)?GREY:'#FFFFFF';return(
      <tr key={'s'+size+name}>
        <td style={idC(W_SIZE,bg,'center',{fontWeight:700,color:NAVY})}>{size}</td>
        <td style={idC(W_RIG,bg,'center',{color:'#475569'})}>{type}</td>
        <td style={idC(W_SUP,bg,'left')}>{name}</td>
        {valCells(o,bg)}
      </tr>
    );};
    const totalTr=(label,o,bg,txt,onClick,caret)=>(
      <tr key={'t'+label} onClick={onClick} style={onClick?{cursor:'pointer'}:undefined}>
        <td style={idC(W_SIZE,bg,'center',{borderTop:DARK,borderBottom:DARK})} />
        <td style={idC(W_RIG,bg,'center',{borderTop:DARK,borderBottom:DARK})} />
        <td style={idC(W_SUP,bg,'left',{fontWeight:800,color:txt,borderTop:DARK,borderBottom:DARK})}>{caret?caret+' ':''}{label}</td>
        {valCells(o,bg,{bold:1,tb:1,color:txt})}
      </tr>
    );
    const rigBlock=g=>{
      const rows=g.sups.map(s=>({s,key:normName(s.n),o:sup[normName(s.n)]||blank()})).sort((a,b)=>sortVal(b.o)-sortVal(a.o));
      return [...rows.map(r=>supTr(g.rig,r.s.t,r.s.n,r.o)),totalTr(`${g.rig} Total`,sumGroup(g.sups.map(s=>normName(s.n))),RIG_TOT,NAVY)];
    };
    const otherRows=otherKeys.map(k=>({k,o:sup[k]})).sort((a,b)=>sortVal(b.o)-sortVal(a.o));
    const totalW=W_SIZE+W_RIG+W_SUP+V.reduce((a,c)=>a+c.w,0);
    return (
      <div style={{border:DARK,borderRadius:6,overflow:'hidden',background:'#fff'}}>
        <table style={{borderCollapse:'separate',borderSpacing:0}}>
          <thead>
            <tr><th colSpan={3+V.length} style={{padding:'5px 8px',fontSize:13,fontWeight:800,textAlign:'center',background:hdr,color:'#000',borderBottom:`1px solid ${C_LINE}`,width:totalW}}>{title}</th></tr>
            <tr>
              <th style={hCell(W_SIZE,sub,'center')}>SIZE</th>
              <th style={hCell(W_RIG,sub,'center')}>RIG</th>
              <th style={hCell(W_SUP,sub,'left')}>SUP</th>
              {V.map(c=><th key={c.lbl} style={hCell(c.w,c.grey?GREY_SUB:sub,'right')}>{c.lbl}</th>)}
            </tr>
          </thead>
          <tbody>
            {RIG_ROSTER.flatMap(rigBlock)}
            {otherKeys.length>0 && [
              ...(otherOpen?otherRows.map(r=>supTr('—','—',r.k,r.o)):[]),
              totalTr('Other Total',sumGroup(otherKeys),RIG_TOT,'#92400E',()=>setOtherOpen(v=>!v),otherOpen?'▾':'▸'),
            ]}
            {totalTr('COMPANY TOTAL',company,hdr,'#000')}
            <tr><td colSpan={3+V.length} style={{padding:'3px 8px',fontSize:11,fontWeight:800,color:'#334155',background:'#F1F5F9',borderBottom:`1px solid ${C_LINE}`,borderTop:DARK}}>PM TOTALS</td></tr>
            {pmNames.map(pk=>{const bg=(rix++%2===1)?GREY:'#FFFFFF';return(
              <tr key={'pm'+pk}>
                <td colSpan={3} style={idC(W_SIZE+W_RIG+W_SUP,bg,'left',{fontWeight:700})}>{pk}</td>
                {valCells(pmAgg[pk],bg)}
              </tr>
            );})}
          </tbody>
        </table>
      </div>
    );
  };

  const QBtn=({d,l,disabled})=>(<button onClick={()=>!disabled&&setQSel(q=>Math.min(4,Math.max(1,q+d)))} disabled={disabled} style={{padding:'4px 9px',fontSize:13,fontWeight:800,borderRadius:6,cursor:disabled?'default':'pointer',border:`1px solid #CBD5E1`,background:disabled?'#F1F5F9':'#fff',color:disabled?'#CBD5E1':'#334155'}}>{l}</button>);

  return (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:12}}>
      <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',marginBottom:10}}>
        <div style={{fontWeight:800,fontSize:15,color:C.text}}>📈 SUM — Quarter &amp; YTD by Supervisor</div>
        {setView && [['input','📥 INV Input'],['pm','👤 PM'],['svc','🔧 SVC'],['sup','👷 SUP'],['jobs','📋 JOBS'],['budget','📊 BUDGET'],['week','📅 WK'],['sum','📈 SUM'],['yoy','🔁 YOY'],['aging','⏳ AGING']].map(([v,l])=>(
          <Btn key={v} onClick={()=>setView(v)} variant={view===v?'primary':'secondary'} small>{l}</Btn>
        ))}
        <div style={{flex:1}} />
        <span style={{fontSize:11,fontWeight:700,color:'#64748B'}}>Quarter:</span>
        <QBtn d={-1} l="◀" disabled={qSel<=1} />
        <span style={{fontWeight:800,fontSize:13,color:NAVY,minWidth:140,textAlign:'center',padding:'2px 8px',borderRadius:6,background:qc.sub}}>{qLabel} {YR} · {qState==='open'?`WK ${selWeeks}/13 · ${weeksLeft} left`:qState==='closed'?'closed':'upcoming'}</span>
        <QBtn d={1} l="▶" disabled={qSel>=4} />
      </div>
      <div style={{display:'flex',gap:16,flexWrap:'wrap',alignItems:'flex-start',overflowX:'auto'}}>
        {mkTable(`${qLabel} ${YR} · ${selWeeks} wks`, qc.hdr, qc.sub, QV, o=>o.qRev)}
        {mkTable(`YTD ${YR} · through WK ${weeksYTD}`, GREY_HDR, GREY_SUB, YV, o=>o.yRev)}
      </div>
      <div style={{fontSize:10,color:C.muted,marginTop:8,fontStyle:'italic'}}>Two standalone tables (each prints on its own). Each auto-sorts by its own revenue, highest first, within each rig, and re-sorts whenever invoices update. Click <b>Other Total</b> to collapse one-off / former crews. Quarter color follows the Week tab. YTD averages (grey): AVG WK LF = LF ÷ {weeksYTD} · AVG LEN = LF ÷ bores · REV/WK = revenue ÷ {weeksYTD}.</div>
    </div>
  );
}

// #JOBS_AWARDED_NAV_V1 — dashboard "Awarded" tile opens the Job List with the Awarded dropdown expanded

// #OWNER_YOY_V2 — Year-over-Year revenue, now a Revenue-tab view. Self-contained: loads revenue-input
// plus every prior-year key so it sees all years without disturbing Budget/WK/SUM (which stay current-year).
function YoyReport() {
  const [inv, setInv] = useState([]);
  useEffect(()=>{ (async()=>{
    const cy=new Date().getFullYear(); let ri=await sg('revenue-input'); ri=Array.isArray(ri)?ri.slice():[];
    for (let y=cy-1; y>=cy-12; y--){ const h=await sg(`revenue-${y}`); if(Array.isArray(h)) ri=ri.concat(h); }
    setInv(ri);
  })(); },[]);
  const C = { text:'#111827', muted:'#6B7280', green:'#15803D', red:'#DC2626', blue:'#1D4ED8', border:'#E2E8F0', card:'#fff', hi:'#F3F7FE' };
  const WK_GOAL = 769231;
  const _nd = x => String(x||'').trim().toUpperCase().replace(/^CP-/,'').replace(/-CP$/,'');
  const _iy = e => { const m=String(e.invDate||e.pullDate||'').match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/); return m?(m[3].length===2?2000+Number(m[3]):Number(m[3])):null; };
  const cy=new Date().getFullYear(); const map={};
  inv.forEach(e=>{ const y=_iy(e); if(!y) return; if(!map[y]) map[y]={rev:0,rows:0,lf:0,bore:0,ducs:new Set()}; const o=map[y]; o.rev+=_revNum(e.invAmnt); o.rows+=1; o.lf+=_revNum(e.lf); o.bore+=_revNum(e.bore); const d=_nd(e.duc); if(d) o.ducs.add(d); });
  const _wk=(()=>{ const d=new Date(); const st=new Date(d.getFullYear(),0,1); return Math.max(1,Math.ceil(((d-st)/86400000+1)/7)); })();
  const rows = Object.keys(map).map(Number).sort((a,b)=>b-a).map(y=>{ const o=map[y]; const cur=(y===cy); const wks=cur?_wk:52; const ducs=o.ducs.size; return { y, rev:o.rev, rows:o.rows, ducs, lf:o.lf, bore:o.bore, revDuc:ducs?o.rev/ducs:0, revJob:o.rows?o.rev/o.rows:0, invWk:o.rows/wks, lfWk:o.lf/wks, revWk:o.rev/wks, proj:cur?o.rev/wks*52:o.rev, cur }; });
  const $ = v => '$'+Math.round(Number(v)||0).toLocaleString();
  const $M = v => v>=1e6?`$${(v/1e6).toFixed(1)}M`:v>=1e3?`$${Math.round(v/1e3)}K`:`$${Math.round(v)}`;
  const th = { padding:'5px 7px', fontSize:9.5, fontWeight:800, letterSpacing:0.3, textTransform:'uppercase', color:C.muted, borderBottom:`2px solid ${C.border}` };
  const cur = rows.find(r=>r.cur);
  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:12, overflowX:'auto' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:8, flexWrap:'wrap', gap:6 }}>
        <span style={{ fontSize:13, fontWeight:800, color:C.text }}>Revenue · Year over Year</span>
        <span style={{ fontSize:11, color:C.muted }}>all years from invoice lines · current year live pace · weekly goal {$M(WK_GOAL)}</span>
      </div>
      {rows.length ? (
        <table style={{ width:'100%', borderCollapse:'collapse', minWidth:820 }}>
          <thead><tr>{['Year','Revenue','DUCs','Inv','$/DUC','$/Job','Inv/Wk','LF','Bores','LF/Wk','Rev/Wk'].map((h,i)=>(<th key={h} style={{ ...th, textAlign:i?'right':'left' }}>{h}</th>))}</tr></thead>
          <tbody>
            {rows.map(r=>{ const below=r.revWk<WK_GOAL; const td=(v,c2)=>(<td style={{ padding:'5px 7px', fontSize:11.5, textAlign:'right', color:c2||C.text }}>{v}</td>);
              return (
                <tr key={r.y} style={{ background:r.cur?C.hi:'transparent', borderBottom:`1px solid ${C.border}` }}>
                  <td style={{ padding:'5px 7px', fontSize:11.5, fontWeight:800, color:r.cur?C.blue:C.text }}>{r.y}{r.cur?' *':''}</td>
                  {td($M(r.rev), C.green)}{td(r.ducs.toLocaleString())}{td(r.rows.toLocaleString())}{td($(r.revDuc), C.muted)}{td($(r.revJob), C.muted)}{td(Math.round(r.invWk), C.muted)}{td(Math.round(r.lf).toLocaleString())}{td(Math.round(r.bore).toLocaleString())}{td(Math.round(r.lfWk).toLocaleString(), C.muted)}{td($M(r.revWk), below?C.red:C.green)}
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : <div style={{ color:C.muted, fontSize:12, padding:'8px 0' }}>Loading…</div>}
      {cur && <div style={{ marginTop:8, fontSize:11.5, color:C.muted }}>{cur.y} pace &rarr; <b style={{ color:C.blue }}>{$M(cur.proj)}</b> projected full-year · {cur.rows.toLocaleString()} invoices · {cur.ducs.toLocaleString()} jobs so far</div>}
    </div>
  );
}

// #OWNER_AGING_V1 — AR aging: paste QB aging sections (customer bucket list / retainage / past-terms).
function AgingReport() {
  const [ag, setAg] = useState({});
  const [txt, setTxt] = useState('');
  const [msg, setMsg] = useState('');
  useEffect(()=>{ (async()=>{ const a=await sg('ar-aging'); if (a && typeof a==='object') setAg(a); })(); },[]);
  const C = { text:'#111827', muted:'#6B7280', green:'#15803D', red:'#DC2626', blue:'#1D4ED8', amber:'#B45309', border:'#E2E8F0', card:'#fff' };
  const _n = v => { const x=parseFloat(String(v==null?'':v).replace(/[^0-9.\-]/g,'')); return isNaN(x)?0:x; };
  const $ = v => '$'+Math.round(Number(v)||0).toLocaleString();
  const parse = async () => {
    const lines = txt.split(/\r?\n/).filter(l=>l.trim()!==''); if (!lines.length){ setMsg('Paste a section first.'); return; }
    const rows = lines.map(l=>l.split('\t'));
    const next = { ...ag };
    const hIx = rows.findIndex(r => r.some(x=>/current/i.test(String(x||''))) && r.some(x=>/total/i.test(String(x||''))) && r.some(x=>/>\s*90/.test(String(x||''))));
    // #AGING_DUC_ROWS_V1 (2026-08-28) — Bill: "Retainage, past due already piped in within program by
    // job, customer... this is what I update each week" — his YTD Cost file's Past Due (A1:K22) /
    // Retainage (M1:X117) tabs, DUC-level line detail (DUC/CLIENT/JOB/STATUS/Date/Num/Terms/Due Date/
    // Aging/Balance/CUSTOMER). Detected and parsed FIRST (most specific) so per-job Retainage/Past Due
    // can finally be real instead of a company-wide total.
    const dIx = rows.findIndex(r => r.some(x=>/^duc$/i.test(String(x||'').trim())) && r.some(x=>/status/i.test(String(x||''))) && r.some(x=>/balance/i.test(String(x||''))));
    if (dIx>=0) {
      const H = rows[dIx].map(x=>String(x||'').trim().toLowerCase());
      const ci = re => H.findIndex(h=>re.test(h));
      const dI=ci(/^duc$/), cI=ci(/client/), jI=ci(/^job$/), sI=ci(/status/), dtI=ci(/^date$/),
            nI=ci(/^num/), tmI=ci(/^ter/), ddI=ci(/due.?date/), agI=ci(/aging/), balI=ci(/balance/), cuI=ci(/customer/);
      const _looksDuc = s => /^[A-Za-z]\d[0-9A-Za-z]{3,7}$/.test(String(s||'').trim());
      const ducFromJob = (job) => { const pre=String(job||'').split('-')[0].trim(); const m=pre.match(/([A-Za-z]\d[0-9A-Za-z]{3,7})$/); return m?m[1].toUpperCase():''; };
      const parsed = { AGING:[], RETAINAGE:[] };
      rows.forEach((r,ri) => {
        if (ri<=dIx) return;
        const rawDuc = String(r[dI]||'').trim();
        const duc = _looksDuc(rawDuc) ? rawDuc.toUpperCase() : (ducFromJob(r[jI]) || rawDuc.toUpperCase());
        const status = String(r[sI]||'').trim().toUpperCase();
        const balance = _n(r[balI]);
        if (!duc || !balance || !parsed[status]) return;
        parsed[status].push({ duc, client:String(r[cI]||'').trim(), job:String(r[jI]||'').trim(),
          date:String(r[dtI]||'').trim(), num:String(r[nI]||'').trim(), terms:String(r[tmI]||'').trim(),
          dueDate:String(r[ddI]||'').trim(), agingDays:_n(r[agI]), balance, customer:String(r[cuI]||'').trim() });
      });
      if (parsed.AGING.length) { next.pastDueRows = parsed.AGING; next.pastTerms = Math.round(parsed.AGING.reduce((s,x)=>s+x.balance,0)); }
      if (parsed.RETAINAGE.length) { next.retainageRows = parsed.RETAINAGE; next.retainage = Math.round(parsed.RETAINAGE.reduce((s,x)=>s+x.balance,0)); }
      if (!parsed.AGING.length && !parsed.RETAINAGE.length) { setMsg('Could not read any DUC rows — check the Status column says AGING or RETAINAGE.'); return; }
      setMsg(`${parsed.AGING.length?'Past Due '+parsed.AGING.length+' jobs '+$(next.pastTerms||0)+'   ':''}${parsed.RETAINAGE.length?'Retainage '+parsed.RETAINAGE.length+' jobs '+$(next.retainage||0):''}`.trim());
    } else if (hIx>=0) {
      const H = rows[hIx].map(x=>String(x||'').trim().toLowerCase());
      const ci = re => H.findIndex(h=>re.test(h));
      const cCur=ci(/current/), c30=ci(/^1\s*-\s*30/), c60=ci(/31\s*-\s*60/), c90=ci(/61\s*-\s*90/), cO=ci(/>\s*90/), cT=ci(/total/);
      let tot=0,cur=0,b30=0,b60=0,b90=0,bo=0; const cust=[];
      rows.forEach((r,ri)=>{ if (ri<=hIx) return; const name=String(r[0]||'').trim(); if (!name || /^total/i.test(name)) return;
        const v={ cur:_n(r[cCur]), b30:_n(r[c30]), b60:_n(r[c60]), b90:_n(r[c90]), o:_n(r[cO]), t:_n(r[cT]) };
        if (!(v.cur||v.b30||v.b60||v.b90||v.o||v.t)) return;
        tot+=v.t; cur+=v.cur; b30+=v.b30; b60+=v.b60; b90+=v.b90; bo+=v.o; cust.push({ name, ...v });
      });
      next.total=Math.round(tot); next.current=Math.round(cur); next.over90=Math.round(bo);
      next.over61=Math.round(b90+bo); next.over61Pct= tot ? (b90+bo)/tot : 0; next.pastDue=Math.round(tot-cur);
      next.byCustomer = cust.sort((a,b)=>b.t-a.t);
      setMsg(`Aging list · ${cust.length} customers · total ${$(tot)} · 61+ ${((next.over61Pct)*100).toFixed(1)}%`);
    } else {
      const hi = rows.findIndex(r => r.some(x=>/balance/i.test(String(x||''))));
      const H = hi>=0 ? rows[hi].map(x=>String(x||'').trim().toLowerCase()) : [];
      const balI = H.findIndex(h=>/balance/.test(h)); const stI = H.findIndex(h=>/status/.test(h));
      let ret=0, pt=0;
      rows.forEach((r,ri)=>{ if (ri<=hi) return; const st=String(r[stI]||'').toUpperCase(); const bal=_n(r[balI]); if (/RETAIN/.test(st)) ret+=bal; else if (/AGING/.test(st)) pt+=bal; });
      if (ret) next.retainage=Math.round(ret);
      if (pt) next.pastTerms=Math.round(pt);
      if (!ret && !pt){ setMsg('Could not read that — expected Status + Balance columns, or the aging-bucket list.'); return; }
      setMsg(`${ret?'Retainage '+$(ret)+'   ':''}${pt?'Past terms '+$(pt):''}`.trim());
    }
    next.at=new Date().toISOString();
    await ss('ar-aging', next); setAg(next); setTxt('');
  };
  const pct = (ag.over61Pct||0)*100;
  const tiles = [
    { l:'Total AR', v: ag.total?$(ag.total):'—', c:C.blue },
    { l:'Past Terms', v: ag.pastTerms?$(ag.pastTerms):(ag.pastDue?$(ag.pastDue):'—'), c:C.amber },
    { l:'Retainage', v: ag.retainage?$(ag.retainage):'—', c:C.text },
    { l:'61+ Days %', v: ag.total?pct.toFixed(1)+'%':'—', c: pct>10?C.red:C.green, hint: ag.total?(pct>10?'over 10% target':'under 10% target'):'' },
  ];
  return (
    <div>
      <div style={{ background:'#FFF7ED', border:'1px solid #FED7AA', borderRadius:10, padding:12, marginBottom:12 }}>
        <div style={{ fontSize:12, fontWeight:800, color:C.text, marginBottom:4 }}>Update AR Aging</div>
        <div style={{ fontSize:11, color:C.muted, marginBottom:6 }}>Paste one section at a time — the DUC-level Past Due / Retainage tabs from your YTD Cost file (DUC · Client · Job · Status · Date · Num · Terms · Due Date · Aging · Balance · Customer — this is what feeds real per-job Retainage/Past Due on Job P&L), the QuickBooks customer bucket list (Current · 1-30 · 31-60 · 61-90 · &gt;90 · TOTAL), or the plain Status + Balance rows. It auto-detects which. Header included is fine.</div>
        <textarea value={txt} onChange={e=>setTxt(e.target.value)} placeholder="Paste an aging section here…" spellCheck={false} style={{ width:'100%', height:78, fontFamily:'monospace', fontSize:11, padding:8, border:`1px solid ${C.border}`, borderRadius:6, boxSizing:'border-box', resize:'vertical' }} />
        <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:6 }}>
          <button onClick={parse} disabled={!txt.trim()} style={{ background:C.blue, color:'#fff', border:'none', borderRadius:6, padding:'6px 14px', fontSize:12, fontWeight:700, cursor:txt.trim()?'pointer':'default', opacity:txt.trim()?1:0.4 }}>Load section</button>
          {msg && <span style={{ fontSize:11.5, color:C.green, fontWeight:600 }}>{msg}</span>}
          {ag.at && <span style={{ fontSize:10.5, color:C.muted, marginLeft:'auto' }}>updated {String(ag.at).slice(0,10)}</span>}
        </div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px,1fr))', gap:10, marginBottom:14 }}>
        {tiles.map((t,i)=>(
          <div key={i} style={{ background:C.card, border:`1px solid ${C.border}`, borderLeft:`3px solid ${t.c}`, borderRadius:10, padding:'10px 12px' }}>
            <div style={{ fontSize:10, fontWeight:800, letterSpacing:0.4, textTransform:'uppercase', color:C.muted }}>{t.l}</div>
            <div style={{ fontSize:20, fontWeight:900, color:t.c, marginTop:3 }}>{t.v}</div>
            {t.hint && <div style={{ fontSize:10, color:t.c, marginTop:2 }}>{t.hint}</div>}
          </div>
        ))}
      </div>
      {Array.isArray(ag.byCustomer) && ag.byCustomer.length>0 && (
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1.6fr repeat(6, 1fr)', padding:'8px 10px', background:'#0F172A', color:'#fff', fontSize:10, fontWeight:800, letterSpacing:0.4 }}>
            <div>CUSTOMER</div><div style={{textAlign:'right'}}>CURRENT</div><div style={{textAlign:'right'}}>1-30</div><div style={{textAlign:'right'}}>31-60</div><div style={{textAlign:'right'}}>61-90</div><div style={{textAlign:'right'}}>&gt;90</div><div style={{textAlign:'right'}}>TOTAL</div>
          </div>
          {ag.byCustomer.map((r,i)=>(
            <div key={i} style={{ display:'grid', gridTemplateColumns:'1.6fr repeat(6, 1fr)', padding:'6px 10px', fontSize:11, borderTop:`1px solid ${C.border}`, background:((r.b90||0)+(r.o||0))>0?'#FEF2F2':'#fff' }}>
              <div style={{ fontWeight:600, color:C.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.name}</div>
              <div style={{textAlign:'right', color:C.muted}}>{r.cur?$(r.cur):'—'}</div>
              <div style={{textAlign:'right', color:C.muted}}>{r.b30?$(r.b30):'—'}</div>
              <div style={{textAlign:'right', color:C.muted}}>{r.b60?$(r.b60):'—'}</div>
              <div style={{textAlign:'right', color:r.b90?C.red:C.muted, fontWeight:r.b90?700:400}}>{r.b90?$(r.b90):'—'}</div>
              <div style={{textAlign:'right', color:r.o?C.red:C.muted, fontWeight:r.o?700:400}}>{r.o?$(r.o):'—'}</div>
              <div style={{textAlign:'right', fontWeight:800, color:C.text}}>{$(r.t)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function RevenueTab({ jobs, onSyncActuals, canEdit, userRole, upn }) {
  const [view, setView] = useState('input');
  const [inv, setInv] = useState([]);
  const [supOpen, setSupOpen] = useState(null);
  const reloadInv = useCallback(async () => { const ri = await sg('revenue-input'); if (Array.isArray(ri)) setInv(ri); }, []);
  useEffect(()=>{ reloadInv(); }, [reloadInv]);
  useLiveKeys(['revenue-input'], reloadInv);   // #LIVE_KEYS_V1 — display view auto-refreshes on others' revenue edits
  useEffect(()=>{ if(PENDING_REV.view){ setView(PENDING_REV.view); PENDING_REV.view=null; } }, []); // #REV_NAV_V1

  const BACKLOG_STATUSES = ['Approved','Called In','In Progress'];
  const completed = jobs.filter(j=>j.stat==='Complete');
  const backlog = jobs.filter(j=>BACKLOG_STATUSES.includes(j.stat));

  const ytdBilled = completed.reduce((s,j)=>s+(j.arev||j.prev||0),0);
  const invoicedTotal = (inv||[]).reduce((s,e)=>s+_revNum(e.invAmnt),0); // #REV_INVOICED_V1 — period invoiced (matches ribbon)
  // Backlog = Plan Rev minus whatever is already billed (Actual Rev), active jobs only
  const backlogRev = backlog.reduce((s,j)=>s+Math.max(0,(j.prev||0)-(j.arev||0)),0); // #BACKLOG_FLOOR_V1

  const byPM = PMS.map(pm => {
    const pmActive = backlog.filter(j=>j.pm===pm);
    const pmBacklog = pmActive.reduce((s,j)=>s+Math.max(0,(j.prev||0)-(j.arev||0)),0);
    const pmBilled = completed.filter(j=>j.pm===pm).reduce((s,j)=>s+(j.arev||j.prev||0),0);
    return { name:pm, backlog:Math.round(pmBacklog/1000), billed:Math.round(pmBilled/1000), jobs:pmActive.length };
  });

  const bySvc = Object.entries(SVC).map(([k,label]) => {
    const sActive = backlog.filter(j=>j.svc===k);
    const sBacklog = sActive.reduce((s,j)=>s+Math.max(0,(j.prev||0)-(j.arev||0)),0);
    const sBilled = completed.filter(j=>j.svc===k).reduce((s,j)=>s+(j.arev||j.prev||0),0);
    return { name:label, backlog:Math.round(sBacklog/1000), billed:Math.round(sBilled/1000), jobs:sActive.length };
  });
  const _svcBilledTot = bySvc.reduce((a,x)=>a+(x.billed||0),0);  // #SVC_PCT_V1 — each service's YTD-billed share of total

  const topJobs = [...jobs].filter(j=>j.prev).sort((a,b)=>(b.prev||0)-(a.prev||0)).slice(0,15);

  // By Supervisor — from invoice lines (supervisors aren't on the Job List)
  const MO = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const normDuc = d => String(d||'').trim().toUpperCase().replace(/^CP-/,'').replace(/-CP$/,'');
  // Customer "truth" comes from the Job List (keyed by DUC), so invoice-line typos don't split a customer
  const jobCust = {};
  jobs.forEach(j => { const c = j.cust||''; if(!c) return; [j.duc, j.displayDuc].forEach(d => { const n=normDuc(d); if(n) jobCust[n]=c; }); });
  const monthOf = e => parseInt(e.month,10) || (()=>{ const x=String(e.invDate||'').match(/(\d{1,2})[\/\-]/); return x?parseInt(x[1],10):0; })();

  const bySup = (() => {
    const m = {};
    inv.forEach(e => {
      const name = String(e.sup||'').trim() || '—';
      if (!m[name]) m[name] = { name, amt:0, lf:0, bores:0, lines:0, custs:{}, months:new Set() };
      const amt = _revNum(e.invAmnt);
      m[name].amt   += amt;
      m[name].lf    += _revNum(e.lf);
      m[name].bores += _revNum(e.bore);
      m[name].lines += 1;
      const matched = !!jobCust[normDuc(e.duc)];
      const cust = jobCust[normDuc(e.duc)] || String(e.customer||'').trim() || '—';
      const mo = monthOf(e);
      if (!m[name].custs[cust]) m[name].custs[cust] = { name:cust, total:0, months:{}, unverified:false };
      m[name].custs[cust].total += amt;
      if (!matched) m[name].custs[cust].unverified = true;
      if (mo>=1 && mo<=12) { m[name].custs[cust].months[mo] = (m[name].custs[cust].months[mo]||0) + amt; m[name].months.add(mo); }
    });
    return Object.values(m).map(s => ({
      ...s,
      custList: Object.values(s.custs).sort((a,b)=>b.total-a.total),
      monthList: [...s.months].sort((a,b)=>a-b),
    })).sort((a,b)=>b.amt-a.amt);
  })();
  // #SUP_RIG_COLOR_V1 — color each supervisor bar by their rig (RIG_ROSTER); flare = rig legend + value labels
  const RIG_BAR_COLORS = { R1:'#5A82B0', R2:'#6B9C7E', R3:'#BBA253', R4:'#D98A45', R5:'#D98A45', AU:'#5E9AA0' };
  const RIG_LEGEND = [['R1',RIG_BAR_COLORS.R1],['R2',RIG_BAR_COLORS.R2],['R3',RIG_BAR_COLORS.R3],['R4/R5',RIG_BAR_COLORS.R4],['AU',RIG_BAR_COLORS.AU]]; // R4/R5 share one orange — merged in legend
  const _normSup = x => { const t = String(x||'').trim().toUpperCase().replace(/\./g,'').replace(/\s+/g,' '); return REV_SUP_ALIAS[t] || t; };   // #RU_R4_JUANP_V1
  const _supToRig = {}; RIG_ROSTER.forEach(r => r.sups.forEach(sp => { _supToRig[_normSup(sp.n)] = r.rig; }));
  const supChart = bySup.map(s => { const rg = _supToRig[_normSup(s.name)] || null; return { name:s.name, invoiced: Math.round(s.amt/1000), rig:rg, fill: rg ? RIG_BAR_COLORS[rg] : '#94A3B8' }; });
  const fK = v => v ? fAbbr(v*1000) : '$0';   // chart values are in $K → show $1.5M / $450K

  return (
    <div>
      {/* #AR_MOBILE_V1 (2026-10-16) — Bill: Budget / WK / SUM / Aging are the popular views but sat near the bottom on a phone. Pinned on top. */}
      {_isPhoneNow() && view === 'input' && (
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
          {[['budget','📊 BUD'],['week','📅 WK'],['sum','📈 SUM'],['aging','⏳ AGING'],['pm','👤 PM'],['sup','👷 SUP']].map(([v,l])=>(
            <Btn key={v} onClick={()=>setView(v)} variant="secondary" small>{l}</Btn>
          ))}
        </div>
      )}
      <style>{`@media (max-width: 899px){ [data-rev-scroll]::-webkit-scrollbar{ height:10px; width:8px; } [data-rev-scroll]::-webkit-scrollbar-thumb{ background:#EA580C; border-radius:6px; } [data-rev-scroll]::-webkit-scrollbar-track{ background:#FDE7D6; } }`}</style>
      {/* #REV_SUBBANNER_REMOVED_V1 — 2-tile Backlog/Invoiced banner removed on sub-views for screen space (rich tiles remain on Invoice Input) */}
      {/* View toggle — standalone on chart views; folded into the strip on Invoice Input & Week */}
      {view !== 'input' && view !== 'week' && view !== 'sum' && (
      <div style={{ display:'flex', gap:6, marginBottom:14, justifyContent:'space-between', flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          {[['input','📥 INV Input'],['pm','👤 PM'],['svc','🔧 SVC'],['sup','👷 SUP'],['jobs','📋 JOBS']].map(([v,l])=>(
            <Btn key={v} onClick={()=>setView(v)} variant={view===v?'primary':'secondary'} small>{l}</Btn>
          ))}
        </div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          {[['budget','📊 BUDGET'],['week','📅 WK'],['sum','📈 SUM'],['yoy','🔁 YOY'],['aging','⏳ AGING']].map(([v,l])=>(
            <Btn key={v} onClick={()=>setView(v)} variant={view===v?'primary':'secondary'} small>{l}</Btn>
          ))}
        </div>
      </div>
      )}

      {view === 'input' && <RevenueInput jobs={jobs} view={view} setView={setView} onSyncActuals={onSyncActuals} canEdit={canEdit} userRole={userRole} upn={upn} />}

      {view === 'budget' && <BudgetReport inv={inv} />}

      {view === 'week' && <WeekReport inv={inv} view={view} setView={setView} />}

      {view === 'sum' && <SumReport inv={inv} view={view} setView={setView} />}

      {view === 'yoy' && <YoyReport />}

      {view === 'aging' && <AgingReport />}

      {view === 'pm' && (
        <div>
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10,
            padding:16, marginBottom:12 }}>
            <div style={{ color:C.muted, fontSize:11, fontWeight:600, letterSpacing:0.5,
              textTransform:'uppercase', marginBottom:12 }}>Revenue by PM ($K)</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={byPM} margin={{left:2,right:5}}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="name" tick={{fill:C.muted,fontSize:11}} />
                <YAxis tick={{fill:C.muted,fontSize:10}} tickFormatter={fK} />
                <Tooltip contentStyle={{background:C.card,border:`1px solid ${C.border}`,borderRadius:6,color:C.text}} formatter={fK} />
                <Bar dataKey="backlog" name="Backlog Remaining" fill={C.accent} opacity={0.8} radius={[3,3,0,0]}/>
                <Bar dataKey="billed" name="YTD Billed" fill={C.green} radius={[3,3,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {byPM.map(p => (
            <div key={p.name} style={{ background:C.card, border:`1px solid ${C.border}`,
              borderRadius:8, padding:'10px 14px', marginBottom:6,
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontFamily:"Arial Narrow, Arial, sans-serif", fontWeight:700,
                  fontSize:18, color:C.white }}>{p.name}</div>
                <div style={{ color:C.muted, fontSize:11 }}>{p.jobs} active jobs · YTD: {f$(p.billed*1000)}</div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ color:C.accent, fontWeight:600 }}>{f$(p.backlog*1000)}</div>
                <div style={{ color:C.muted, fontSize:11 }}>remaining backlog</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {view === 'svc' && (
        <div>
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10,
            padding:16, marginBottom:12 }}>
            <div style={{ color:C.muted, fontSize:11, fontWeight:600, letterSpacing:0.5,
              textTransform:'uppercase', marginBottom:12 }}>Revenue by Service ($K)</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={bySvc} margin={{left:2,right:5}}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="name" tick={{fill:C.text,fontSize:14,fontWeight:700}} />
                <YAxis tick={{fill:C.muted,fontSize:10}} tickFormatter={fK} />
                <Tooltip contentStyle={{background:C.card,border:`1px solid ${C.border}`,borderRadius:6,color:C.text}} formatter={fK} />
                <Bar dataKey="backlog" name="Backlog Remaining" fill={C.accent} opacity={0.8} radius={[3,3,0,0]} />
                <Bar dataKey="billed" name="YTD Billed" fill={C.green} radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {bySvc.sort((a,b)=>b.backlog-a.backlog).map(s => (
            <div key={s.name} style={{ background:C.card, border:`1px solid ${C.border}`,
              borderRadius:8, padding:'10px 14px', marginBottom:6 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                <span style={{ fontWeight:700, fontSize:17, color:C.text }}>{s.name}</span>
                <span style={{ color:C.accent, fontWeight:700, fontSize:16 }}>{f$(s.backlog*1000)}</span>
              </div>
              <ProgressBar value={s.billed} max={s.backlog+s.billed} color={C.green} />
              <div style={{ color:C.muted, fontSize:11, marginTop:3 }}>
                {f$(s.billed*1000)} billed (<span style={{color:C.accent,fontWeight:700}}>{_svcBilledTot? Math.round(s.billed/_svcBilledTot*100):0}% of total</span>) · {s.jobs} active jobs
              </div>
            </div>
          ))}
        </div>
      )}

      {view === 'sup' && (
        <div>
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:16, marginBottom:12 }}>
            <div style={{ color:C.muted, fontSize:11, fontWeight:600, letterSpacing:0.5, textTransform:'uppercase', marginBottom:3 }}>Invoiced by Supervisor ($K)</div>
            <div style={{ color:C.muted, fontSize:10, marginBottom:12 }}>From invoice lines · not the Job List</div>
            {supChart.length>0 && (
            <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:10, fontSize:11 }}>{RIG_LEGEND.map(([rg,c])=>(<span key={rg} style={{ display:'inline-flex', alignItems:'center', gap:5, color:C.text, fontWeight:700 }}><span style={{ width:11, height:11, borderRadius:3, background:c, display:'inline-block' }} /> {rg}</span>))}<span style={{ display:'inline-flex', alignItems:'center', gap:5, color:C.muted, fontWeight:600 }}><span style={{ width:11, height:11, borderRadius:3, background:'#94A3B8', display:'inline-block' }} /> Other</span></div>
            )}
            {supChart.length ? (
              <ResponsiveContainer width="100%" height={Math.max(200, supChart.length*26)}>
                <BarChart data={supChart} layout="vertical" margin={{left:10,right:58}}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                  <XAxis type="number" tick={{fill:C.muted,fontSize:10}} tickFormatter={fK} />
                  <YAxis type="category" dataKey="name" width={100} tick={{fill:C.text,fontSize:12,fontWeight:600}} />
                  <Tooltip contentStyle={{background:C.card,border:`1px solid ${C.border}`,borderRadius:6,color:C.text}} formatter={fK} />
                  <Bar dataKey="invoiced" name="Invoiced" radius={[0,4,4,0]}>
                    {supChart.map((e,i)=><Cell key={'sc'+i} fill={e.fill} />)}
                    <LabelList dataKey="invoiced" position="right" formatter={fK} style={{ fontSize:10, fontWeight:700, fill:C.text }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <div style={{ color:C.muted, fontSize:12, padding:'12px 0' }}>No invoice lines yet.</div>}
          </div>
          {bySup.map(s => {
            const open = supOpen===s.name;
            const anyUnverified = s.custList.some(c=>c.unverified);
            return (
            <div key={s.name} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:8, marginBottom:6, overflow:'hidden' }}>
              <div onClick={()=>setSupOpen(o=>o===s.name?null:s.name)} style={{ padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ color:C.muted, fontSize:12, width:10 }}>{open?'▾':'▸'}</span>
                  <div>
                    <div style={{ fontFamily:"Arial Narrow, Arial, sans-serif", fontWeight:700, fontSize:18, color:C.white }}>{s.name}{anyUnverified && <span title="Has customers not matched to the Job List" style={{ color:C.accent, fontSize:12, marginLeft:6 }}>⚠</span>}</div>
                    <div style={{ color:C.muted, fontSize:11 }}>{s.lines} lines · {s.lf.toLocaleString()} LF · {s.bores} bores · {s.custList.length} customers</div>
                  </div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ color:C.green, fontWeight:700 }}>{f$(s.amt)}</div>
                  <div style={{ color:C.muted, fontSize:11 }}>invoiced</div>
                </div>
              </div>
              {open && (
                <div style={{ borderTop:`1px solid ${C.border}`, padding:'10px 14px', overflowX:'auto' }}>
                  <table style={{ borderCollapse:'collapse', width:'100%', fontSize:14, minWidth:Math.max(360, 120 + s.monthList.length*70) }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign:'left', padding:'4px 8px', color:C.muted, fontSize:10, textTransform:'uppercase', borderBottom:`2px solid ${C.border}`, whiteSpace:'nowrap' }}>Customer</th>
                        {s.monthList.map(mo => <th key={mo} style={{ textAlign:'right', padding:'4px 8px', color:C.text, fontSize:14, fontWeight:800, borderBottom:`2px solid ${C.border}`, whiteSpace:'nowrap' }}>{MO[mo]}</th>)}
                        <th style={{ textAlign:'right', padding:'4px 8px', color:C.muted, fontSize:10, textTransform:'uppercase', borderBottom:`2px solid ${C.border}`, whiteSpace:'nowrap' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.custList.map(c => (
                        <tr key={c.name}>
                          <td style={{ textAlign:'left', padding:'4px 8px', color:C.text, whiteSpace:'nowrap', borderBottom:`1px solid ${C.border}` }}>
                            {c.unverified && <span title="DUC not found on the Job List — name is as-typed, verify spelling" style={{ color:C.accent, marginRight:4 }}>⚠</span>}{c.name}
                          </td>
                          {s.monthList.map(mo => <td key={mo} style={{ textAlign:'right', padding:'4px 8px', color: c.months[mo]?C.text:C.muted, whiteSpace:'nowrap', borderBottom:`1px solid ${C.border}` }}>{c.months[mo]?f$(c.months[mo]):'—'}</td>)}
                          <td style={{ textAlign:'right', padding:'4px 8px', color:C.green, fontWeight:700, whiteSpace:'nowrap', borderBottom:`1px solid ${C.border}` }}>{f$(c.total)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td style={{ textAlign:'left', padding:'5px 8px', fontWeight:800, color:C.text }}>Total</td>
                        {s.monthList.map(mo => { const mt=s.custList.reduce((a,c)=>a+(c.months[mo]||0),0); return <td key={mo} style={{ textAlign:'right', padding:'5px 8px', fontWeight:800, color:C.text, whiteSpace:'nowrap' }}>{mt?f$(mt):'—'}</td>; })}
                        <td style={{ textAlign:'right', padding:'5px 8px', fontWeight:800, color:C.green, whiteSpace:'nowrap' }}>{f$(s.amt)}</td>
                      </tr>
                    </tbody>
                  </table>
                  {anyUnverified && <div style={{ fontSize:10, color:C.muted, marginTop:6 }}>⚠ = DUC not matched to the Job List; customer name is as-typed on the invoice. Verify spelling against the job.</div>}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {view === 'jobs' && (
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {topJobs.map(j => {
            const pctRev = j.arev && j.prev ? Math.round(j.arev/j.prev*100) : null;
            return (
              <div key={j.duc} style={{ background:C.card, border:`1px solid ${C.border}`,
                borderRadius:8, padding:'10px 14px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
                  <div>
                    <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:3 }}>
                      <span style={{ color:C.accent, fontFamily:"Arial Narrow, Arial, sans-serif",
                        fontWeight:700, fontSize:13 }}>{jobNum(j)}</span>{/* #JOBNUM_V1 */}
                      <StatusBadge status={j.stat} />
                    </div>
                    <div style={{ color:C.text, fontSize:12, fontWeight:500 }}>{j.cust} · {j.city}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ color:C.green, fontWeight:700, fontSize:14 }}>{f$(j.prev)}</div>
                    {j.arev && <div style={{ color:C.muted, fontSize:11 }}>Act: {f$(j.arev)}</div>}
                  </div>
                </div>
                {pctRev !== null && (
                  <>
                    <ProgressBar value={j.arev} max={j.prev} color={pctRev>=100?C.green:C.accent} />
                    <div style={{ color:C.muted, fontSize:10, marginTop:2 }}>{pctRev}% billed</div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
